/**
 * PROMPTS MÉDICOS - SISTEMA DE ANÁLISE IA HEPATOLÓGICA
 * Versão: 4.1.0 (Corrigido - Compatível com tipos do projeto)
 * Data: Dezembro 2024
 *
 * GUIDELINES INCORPORADOS (20 documentos):
 * - HCC/Hepatocarcinoma (AASLD 2023)
 * - Portal Hypertension/Varizes (AASLD 2024 - Baveno VII)
 * - Ascites/SBP/HRS (AASLD 2021)
 * - ACLF - Acute-on-Chronic Liver Failure (AASLD 2024)
 * - Doença Hepática Alcoólica (AASLD 2020)
 * - Encefalopatia Hepática (AASLD/EASL 2014)
 * - Nutrição/Fragilidade/Sarcopenia (AASLD 2021)
 * - Hepatite Autoimune (AASLD 2020)
 * - Colangite Esclerosante Primária (AASLD 2023)
 * - Colangite Biliar Primária (AASLD 2021 update)
 * - MASLD/MASH - Nova Nomenclatura (AASLD 2023)
 * - Resmetirom para MASH (AASLD 2024)
 * - Semaglutide para MASH (AASLD 2025)
 * - Hepatite B Crônica (AASLD 2025)
 * - Hepatite C (AASLD-IDSA 2023)
 * - DILI - Drug-Induced Liver Injury (AASLD 2024)
 * - Hemocromatose (AASLD 2011)
 * - TIPS (AASLD 2024)
 * - Trombose Portal - PVT (Múltiplos guidelines + transplante)
 * - Doenças Vasculares Hepáticas (ACG 2020)
 */

// =============================================================================
// IMPORTAR TIPOS DO PROJETO EXISTENTE
// =============================================================================

import type { DadosAnonimizados, AchadosImagemEstruturados } from "../types";
import { calcularAbstinencia } from "../../../shared/utils/abstinenciaAlcoolica";
import { meldNaDoExameMaisRecente } from "../../../shared/utils/meldNa";
// B351 passo 5b (AI_153) — modalidade doador (fonte única).
import { ehDoadorFigado } from "../../../shared/utils/modalidadeAtendimento";
import {
  REGRA_MELD_30,
  REGRA_IMUNOSSUPRESSAO_CONVERSAO,
  REGRA_TERMINOLOGIA,
  REGRA_DISCORDANCIA_EXAMES,
  REGRA_LIRADS_M,
  REGRA_VESICULA_CIRROTICO,
} from "./regrasClinicas";
import { valoresLabParaIA } from "./valoresLabParaIA";
import { ehRespostaPositiva } from "../../../shared/utils/respostaNegativa";

// =============================================
// TEMPLATE COMPARTILHADO — PÓS-TX E FORMATO
// Usado por gerarPromptAnaliseCompleta e gerarPromptFuncaoHepatica
// =============================================
const TEMPLATE_INVESTIGACAO_E_FORMATO = `
ORDEM LÓGICA DE INVESTIGAÇÃO EM DISFUNÇÃO DO ENXERTO:

⚠️ SEGUIR ESTA ORDEM NAS RECOMENDAÇÕES:

1. PRIMEIRO - EXCLUIR EMERGÊNCIA CIRÚRGICA:
   - Doppler de vasos hepáticos URGENTE (mesmo dia)
   - Trombose de artéria hepática = emergência, pode perder enxerto em horas
   - Se Doppler alterado: acionar equipe cirúrgica imediatamente

2. SEGUNDO - COLETA DE EXAMES:
   - PCR quantitativo CMV/EBV
   - Hemoculturas, urocultura
   - Função hepática e renal completas
   - Nível de tacrolimus (pré-dose)

3. TERCEIRO - AJUSTE IMEDIATO DE IMUNOSSUPRESSÃO:
   - Se tacrolimus >12: reduzir dose em 25-30%
   - Se tacrolimus <5: investigar absorção/aderência
   - NÃO suspender, NÃO trocar imunossupressor

4. QUARTO - DIAGNÓSTICO HISTOLÓGICO:
   - Biópsia hepática em até 48h
   - Graduação Banff para rejeição
   - Avaliação de outras causas (CMV, colangite, etc.)

5. QUINTO - TRATAMENTO DIRECIONADO:
   - APENAS após resultado da biópsia OU se deterioração rápida
   - Rejeição confirmada: Metilprednisolona 500mg IV 1x/dia por 3 dias
   - Rejeição cortico-resistente: Timoglobulina 1,5mg/kg/dia

⚠️ PULSOTERAPIA EMPÍRICA (sem biópsia): considerar APENAS se:
- Biópsia indisponível ou contraindicada (coagulopatia grave, ascite volumosa)
- Deterioração clínica rápida aguardando resultado (>48h)
- Probabilidade clínica muito alta de rejeição

⚠️ NAS RECOMENDAÇÕES, SEMPRE LISTAR NESTA ORDEM:
1. Doppler urgente
2. Exames complementares
3. Ajuste de imunossupressão
4. Biópsia hepática
5. Tratamento (após diagnóstico)

SOLICITAÇÃO:
Forneça uma análise em TEXTO ESTRUTURADO seguindo EXATAMENTE este formato:

ANÁLISE CLÍNICA COMPLETA - HEPATOLOGIA/TRANSPLANTE

1. RESUMO CLÍNICO
Escreva um parágrafo com síntese do caso, seguido de:
- Problemas ativos: liste todos com detalhes e valores
- Gravidade: LEVE, MODERADA ou GRAVE (use os critérios acima para pós-Tx)

2. ANÁLISE DETALHADA

Função Hepática
- Enzimas hepáticas: cite valores e interprete
- Colestase: GGT, FA com valores e interpretação
- Função sintética: INR, albumina com valores e interpretação
- Bilirrubinas: BT, BD, BI com valores e interpretação

Complicações Identificadas
- Numere cada complicação com detalhes clínicos

Marcadores Tumorais (se disponíveis e relevantes ao contexto)
- AFP, CA 19-9 com valores e interpretação
- Em pós-transplante, só mencione se houver suspeita de recidiva

3. AVALIAÇÃO DE RISCO

Prognóstico
- Avaliação detalhada baseada no conjunto de dados

Fatores de Risco Modificáveis
- Liste cada fator com explicação

Sinais de Alarme
- Use ✅ para presentes e ⚠️ para pontos de atenção

4. RECOMENDAÇÕES
Condutas Imediatas Prioritárias
- Liste com numeração, incluindo grau de recomendação e referência
Ajustes Terapêuticos Sugeridos
- Doses, vias e frequências específicas
Exames Complementares Necessários
- Imediatos vs complementares
Frequência de Monitoramento
- Detalhada por fase
⚠️ HIERARQUIA LÓGICA DAS RECOMENDAÇÕES — REGRA OBRIGATÓRIA:
- Recomendações que dependem do resultado de outra NÃO são paralelas — são SEQUENCIAIS e CONDICIONAIS
- Se uma investigação pode MUDAR COMPLETAMENTE a conduta subsequente, ela vem PRIMEIRO e as demais ficam condicionadas ao resultado
- Exemplo: se há dúvida diagnóstica sobre se uma lesão é realmente CHC, NÃO recomendar simultaneamente (a) "questionar o diagnóstico" e (b) "reapresentar Situação Especial por CHC" — isso é contraditório. A sequência correta é: (1) resolver a dúvida diagnóstica, (2) SE confirmado CHC → reapresentar SE; SE não confirmado → redirecionar conduta
- Exemplo: se hipertensão portopulmonar não foi excluída, NÃO recomendar terapia ponte (TACE/ablação) como item paralelo — ela fica CONDICIONADA à exclusão de HPoP
- Usar formato condicional quando aplicável: "Se [resultado X] → fazer Y; Se [resultado Z] → fazer W"
- Nunca listar como itens independentes recomendações que são mutuamente excludentes ou que dependem de um resultado pendente

⚠️ CONDUTAS ESPECÍFICAS POR DIAGNÓSTICO (incluir na análise):

SE SUSPEITA DE REJEIÇÃO AGUDA:
- Pulsoterapia: Metilprednisolona 500mg IV 1x/dia por 3 dias (iniciar ANTES da biópsia se alta suspeita)
- Manter tacrolimus (NÃO suspender) - ajustar alvo para 8-10 ng/mL
- Biópsia hepática em até 48h para confirmação e graduação Banff
- Se rejeição cortico-resistente: Timoglobulina 1,5mg/kg/dia por 7-14 dias

SE SUSPEITA DE COMPLICAÇÃO VASCULAR:
- Doppler URGENTE (mesmo dia)
- Se trombose arterial: avaliação para revascularização ou retransplante urgente
- Anticoagulação apenas se trombose portal sem contraindicação

SE SUSPEITA DE COMPLICAÇÃO BILIAR:
- ColangioRM ou CPRE diagnóstica/terapêutica
- Se estenose: dilatação endoscópica + prótese biliar
- Antibioticoterapia se colangite (Piperacilina-Tazobactam 4,5g 8/8h)

RREGRAS DE IMUNOSSUPRESSÃO (OBRIGATÓRIO SEGUIR):
${REGRA_IMUNOSSUPRESSAO_CONVERSAO}

5. CONSIDERAÇÕES ESPECIAIS

Aspectos Relacionados ao Transplante
- Janela de risco, perfil imunológico, preservação do enxerto

Diagnósticos Diferenciais (OBRIGATÓRIO em disfunção pós-Tx)
- Rejeição aguda
- Complicações vasculares (trombose arterial, estenose portal)
- Complicações biliares
- Infecções (CMV, EBV, bacteriana)
- Toxicidade medicamentosa

Outras Considerações
- Aspectos adicionais relevantes

Urgência: [BAIXA/MÉDIA/ALTA] - justificativa conforme guidelines [AASLD/ILTS/EASL]

REGRAS OBRIGATÓRIAS:
1. Use o formato de TEXTO acima, NÃO retorne JSON
2. Seja DETALHADO — extensão por GRAVIDADE: LEVE/MODERADA = 400-600 palavras; GRAVE = 800-1200. Em caso complexo, NÃO sacrifique segurança/conduta para encurtar (B221)
3. SEMPRE inclua valores numéricos dos exames citados
4. SEMPRE inclua valores de referência para contextualização
5. ANCORAGEM DE CITAÇÃO (B219/B224): só nomeie a sociedade e/ou o ano de uma diretriz (AASLD, EASL, ILTS, Baveno VII etc.) se a sigla/ano constar LITERALMENTE de uma "Fonte N" recuperada no CONTEXTO DE GUIDELINES (rótulo, título ou texto do trecho) OU dos dados do paciente — copie EXATAMENTE, nunca de memória; trate "Other"/"undefined"/"N/A"/vazio como ausência de fonte. Sem Fonte, dê o racional clínico sem inventar sigla/ano. NÃO suprima citação legítima nem enfraqueça a conduta (B219/B221)
6. Inclua doses, vias e frequências nas condutas
7. Avalie TENDÊNCIAS quando houver exames seriados
8. Classificação de gravidade pós-Tx: use LEVE/MODERADA/GRAVE conforme critérios acima
9. Em suspeita de rejeição, SEMPRE incluir protocolo de pulsoterapia (Metilprednisolona 500mg IV 1x/dia por 3 dias)
10. AVALIAR CRITICAMENTE as condutas registradas nas consultas
11. CORRELACIONAR EXPLICITAMENTE COM BIÓPSIA quando disponível
12. AVALIAR OPÇÕES CIRÚRGICAS EM TUMORES HEPÁTICOS
13. TRATAMENTO SISTÊMICO ATUALIZADO PARA CHC
14. EM CASOS COMPLEXOS, EXPLORAR ALTERNATIVAS
`;

// =============================================================================
// VERSÃO DO PROMPT - EXPORTAÇÃO OBRIGATÓRIA
// =============================================================================

export const VERSAO_PROMPT = "4.1.0";

// =============================================================================
// INTERFACE LOCAL PARA CONTEXTO CLÍNICO
// =============================================================================

export interface ContextoClinico {
  tipoAnalise?: string;
  urgencia?: string;
  objetivoEspecifico?: string;
}

// =============================================================================
// CONSTANTES CLÍNICAS - GUIDELINES AASLD/EASL
// =============================================================================

export const CRITERIOS_MILAO = {
  descricao: "Critérios de Milão para transplante hepático em CHC",
  criterios: {
    lesaoUnica: "Lesão única ≤ 5cm",
    lesaoMultipla: "Até 3 lesões, cada uma ≤ 3cm",
    ausenciaInvasao: "Sem invasão macrovascular",
    ausenciaMetastase: "Sem metástases extra-hepáticas",
  },
  fonte: "Mazzaferro et al., N Engl J Med 1996; AASLD 2023 Guidelines",
};

export const CRITERIOS_UCSF = {
  descricao: "Critérios UCSF expandidos",
  criterios: {
    lesaoUnica: "Lesão única ≤ 6.5cm",
    lesaoMultipla: "Até 3 lesões, cada uma ≤ 4.5cm, soma ≤ 8cm",
  },
  fonte: "Yao et al., Hepatology 2001",
};

export const CRITERIO_UP_TO_7 = {
  descricao: "Critério Up-to-7 (Mazzaferro)",
  formula: "Número de tumores + tamanho do maior (cm) ≤ 7",
  aplicacao: "Sem invasão microvascular",
  fonte: "Mazzaferro et al., Lancet Oncol 2009",
};

export const CLASSIFICACAO_LI_RADS = {
  descricao: "LI-RADS v2018 para diagnóstico de CHC",
  categorias: {
    "LR-1": "Definitivamente benigno",
    "LR-2": "Provavelmente benigno",
    "LR-3": "Probabilidade intermediária de CHC",
    "LR-4": "Provavelmente CHC",
    "LR-5": "Definitivamente CHC",
    "LR-M": "Provavelmente malignidade, não necessariamente CHC",
    "LR-TIV": "Tumor em veia (trombose tumoral)",
  },
  criteriosMaiores: {
    washoutNaoPeriferico: "Washout não periférico em fase portal/tardia",
    realceArterial: "Hiperrealce arterial (APHE)",
    capsula: "Cápsula de realce",
    crescimento: "Crescimento > 50% em < 6 meses",
  },
  fonte: "ACR LI-RADS v2018; AASLD 2023",
};

export const CLASSIFICACAO_BCLC = {
  descricao: "Barcelona Clinic Liver Cancer Staging",
  estadios: {
    "0": {
      nome: "Muito precoce",
      criterios: "PS 0, Child A, tumor único < 2cm",
      tratamento: "Ablação/Ressecção",
    },
    A: {
      nome: "Precoce",
      criterios: "PS 0, Child A-B, até 3 nódulos ≤ 3cm",
      tratamento: "Ressecção/Ablação/Transplante",
    },
    B: {
      nome: "Intermediário",
      criterios: "PS 0, Child A-B, multinodular, sem invasão vascular",
      tratamento: "TACE/TARE",
    },
    C: {
      nome: "Avançado",
      criterios: "PS 1-2 ou invasão vascular/metástases, Child A-B",
      tratamento: "Terapia sistêmica",
    },
    D: {
      nome: "Terminal",
      criterios: "PS 3-4 ou Child C",
      tratamento: "BSC (Best Supportive Care)",
    },
  },
  fonte: "EASL 2018; AASLD 2023",
};

export const TRATAMENTO_SISTEMICO_HCC = {
  primeiraLinha: {
    preferencial: "Atezolizumab + Bevacizumab",
    alternativas: [
      "Durvalumab + Tremelimumab (STRIDE)",
      "Sorafenib",
      "Lenvatinib",
    ],
    criterios: "Child-Pugh A, ECOG 0-1",
  },
  segundaLinha: {
    opcoes: ["Regorafenib", "Cabozantinib", "Ramucirumab (AFP ≥ 400)"],
  },
  fonte: "AASLD 2023/2025; ASCO Guidelines 2024",
};

export const CLASSIFICACAO_AFP = {
  descricao: "Interpretação de AFP no contexto de CHC",
  faixas: {
    normal: { min: 0, max: 10, interpretacao: "Normal" },
    levementeElevado: {
      min: 10,
      max: 200,
      interpretacao: "Elevação inespecífica - pode ser hepatite, cirrose",
    },
    moderadamenteElevado: {
      min: 200,
      max: 400,
      interpretacao: "Suspeito para CHC",
    },
    altamenteElevado: {
      min: 400,
      max: 1000,
      interpretacao: "Altamente sugestivo de CHC",
    },
    muitoElevado: {
      min: 1000,
      max: Infinity,
      interpretacao:
        "Fortemente sugestivo de CHC - considerar drop-out de lista",
    },
  },
  afpL3: {
    descricao: "Fração L3 da AFP",
    corte: "≥ 15%",
    interpretacao: "Maior especificidade para CHC",
  },
  fonte: "AASLD 2023",
};

// =============================================================================
// TROMBOSE PORTAL - CLASSIFICAÇÃO E MANEJO
// =============================================================================

export const CLASSIFICACAO_YERDEL = {
  descricao: "Classificação de Yerdel para trombose portal em transplante",
  graus: {
    1: {
      descricao: "Trombose parcial (< 50%) da veia porta principal",
      reconstrucao:
        "Trombectomia simples ou tromboendovenectomia com anastomose porto-portal padrão",
      impactoTx: "Baixo risco - excelente prognóstico",
    },
    2: {
      descricao: "Trombose > 50% ou oclusão total da veia porta principal",
      reconstrucao:
        "Tromboendovenectomia por eversão + anastomose porto-portal. Se veia calcificada, ressecção do segmento trombosado",
      impactoTx: "Risco moderado - bom prognóstico",
    },
    3: {
      descricao:
        "Trombose completa de porta + extensão para confluência espleno-mesentérica",
      reconstrucao:
        "Jump graft da VMS para veia porta do enxerto (enxerto de veia ilíaca ou criopreservado)",
      impactoTx: "Alto risco - requer planejamento vascular detalhado",
    },
    4: {
      descricao:
        "Trombose difusa de todo sistema portal (VP + VMS + V. Esplênica)",
      reconstrucaoComShunts: {
        prioridade1: "Anastomose reno-portal (se shunt esplenorrenal presente)",
        prioridade2: "Anastomose cavo-portal (se shunt mesocaval presente)",
        prioridade3:
          "Anastomose variz-portal (varizes pericoledocianas ou gástrica esquerda)",
        prioridade4: "Arterialização portal (último recurso)",
        prioridade5: "Transplante multivisceral (casos extremos)",
      },
      reconstrucaoSemShunts:
        "Arterialização portal ou transplante multivisceral",
      impactoTx: "Muito alto risco - mortalidade 27% vs 7% em PVT não-complexa",
    },
  },
  conceitos: {
    fisiologica:
      "Reconstrução que direciona sangue esplâncnico para o enxerto (preferível)",
    naoFisiologica:
      "Reconstrução que usa fluxo não-esplâncnico (arterialização, cavo-portal sem shunt)",
  },
  fonte: "Bhangui et al., J Hepatol 2019; Fernandes et al., Int J Surg 2020",
};

export const TROMBOSE_PORTAL_TIPOS = {
  bland: {
    descricao: "Trombose não tumoral (bland thrombus)",
    caracteristicas: [
      "Não expansivo",
      "Sem realce arterial",
      "Fluxo hepatopetal prejudicado mas sem invasão",
    ],
    conduta: "Anticoagulação se apropriado",
  },
  tumoral: {
    descricao: "Trombose tumoral (tumor thrombus)",
    caracteristicas: [
      "Expansão da veia",
      "Realce arterial do trombo",
      "Continuidade com lesão hepática",
      "AFP muito elevado",
    ],
    conduta: "CONTRAINDICAÇÃO a transplante",
    liRads: "LR-TIV",
  },
  diferenciacao: {
    criterios: [
      "Expansão do vaso (> 23mm sugere tumoral)",
      "Realce arterial do trombo",
      "Continuidade com massa hepática",
      "Presença de neovasos",
    ],
    exame: "RM com contraste hepatoespecífico ou TC multifásica",
  },
  fonte: "AASLD 2023; LI-RADS v2018",
};

export const ANTICOAGULACAO_PVT = {
  descricao: "Anticoagulação em trombose portal no cirrótico",
  objetivo:
    "Prevenir progressão da trombose e manter patência para transplante",
  indicacoes: {
    forte: [
      "PVT aguda (< 6 meses)",
      "Candidato a transplante (manter patência)",
      "Progressão documentada da trombose",
      "Isquemia mesentérica",
    ],
    relativa: ["PVT crônica estável", "Shunts espontâneos desenvolvidos"],
  },
  contraindicacoes: {
    absolutas: [
      "Sangramento ativo",
      "Trombose tumoral (LR-TIV)",
      "Varizes de alto risco não tratadas",
    ],
    relativas: [
      "Plaquetas < 50.000/mm³",
      "INR > 2.5 espontâneo",
      "História de sangramento varicoso recente",
      "Encefalopatia grave",
    ],
  },
  opcoes: {
    heparinaBPM: "Enoxaparina 1mg/kg 12/12h (ajustar para função renal)",
    varfarina: "INR alvo 2-3 (difícil controle em cirróticos)",
    doacs:
      "Rivaroxaban 10-15mg/dia ou Apixaban 2.5-5mg 12/12h - Child A-B apenas",
  },
  duracao: {
    candidatoTx: "Manter até o transplante",
    naoCandidato: "6 meses mínimo; indefinido se não recanalizar",
  },
  alternativas: {
    tips: "Se progressão sob anticoagulação ou anticoagulação contraindicada",
  },
  casoEspecifico: {
    plaquetopeniaSevera:
      "Plaquetas < 30.000 = anticoagulação geralmente contraindicada pelo alto risco hemorrágico",
    inrElevado:
      "INR > 1.8 espontâneo = risco hemorrágico elevado, individualizar",
  },
  fonte: "EASL Vascular Liver Disorders 2016; Baveno VII 2022; AASLD 2020",
};

// =============================================================================
// SCORES DE GRAVIDADE
// =============================================================================

export const MELD_3_0 = {
  descricao: "MELD 3.0 (2022) - Score atual para alocação de fígado",
  formula: `MELD 3.0 = 
    1.33 (se feminino) +
    4.56 × ln(bilirrubina) +
    0.82 × (137 - Na) - 0.24 × (137 - Na) × ln(creatinina) +
    9.09 × ln(INR) +
    11.14 × ln(creatinina) +
    1.85 × (3.5 - albumina) - 1.83 × (3.5 - albumina) × ln(creatinina) +
    6`,
  limites: {
    creatinina: { min: 1.0, max: 3.0 },
    bilirrubina: { min: 1.0, max: 40.0 },
    inr: { min: 1.0, max: 8.0 },
    sodio: { min: 125, max: 137 },
    albumina: { min: 1.5, max: 3.5 },
  },
  interpretacao: {
    "6-9": "Baixa gravidade",
    "10-19": "Gravidade moderada",
    "20-29": "Alta gravidade",
    "30-39": "Muito alta gravidade",
    "≥40": "Gravidade extrema",
  },
  fonte: "OPTN/UNOS 2022; Kim WR et al., Hepatology 2021",
};

export const CHILD_PUGH = {
  descricao: "Classificação de Child-Pugh",
  parametros: {
    bilirrubina: {
      "1 ponto": "< 2 mg/dL",
      "2 pontos": "2-3 mg/dL",
      "3 pontos": "> 3 mg/dL",
    },
    albumina: {
      "1 ponto": "> 3.5 g/dL",
      "2 pontos": "2.8-3.5 g/dL",
      "3 pontos": "< 2.8 g/dL",
    },
    inr: {
      "1 ponto": "< 1.7",
      "2 pontos": "1.7-2.3",
      "3 pontos": "> 2.3",
    },
    ascite: {
      "1 ponto": "Ausente",
      "2 pontos": "Leve/controlada",
      "3 pontos": "Moderada-grave",
    },
    encefalopatia: {
      "1 ponto": "Ausente",
      "2 pontos": "Grau I-II",
      "3 pontos": "Grau III-IV",
    },
  },
  classificacao: {
    A: { pontos: "5-6", sobrevida1ano: "100%", sobrevida2anos: "85%" },
    B: { pontos: "7-9", sobrevida1ano: "80%", sobrevida2anos: "60%" },
    C: { pontos: "10-15", sobrevida1ano: "45%", sobrevida2anos: "35%" },
  },
};

// =============================================================================
// ACLF - ACUTE-ON-CHRONIC LIVER FAILURE (AASLD 2024)
// =============================================================================

export const ACLF_CRITERIA = {
  descricao: "ACLF conforme EASL-CLIF Consortium",
  definicao:
    "Síndrome em pacientes com cirrose caracterizada por descompensação aguda, falência de órgãos e alta mortalidade a curto prazo",
  graus: {
    "Sem ACLF": {
      criterio: "Sem falência de órgãos ou falência única não-renal",
      mortalidade28d: "< 5%",
    },
    "ACLF-1": {
      criterio:
        "Falência renal isolada OU falência única + disfunção renal/cerebral",
      mortalidade28d: "22-25%",
    },
    "ACLF-2": {
      criterio: "2 falências de órgãos",
      mortalidade28d: "32-40%",
    },
    "ACLF-3": {
      criterio: "≥ 3 falências de órgãos",
      mortalidade28d: "75-80%",
    },
  },
  gatilhos: [
    "Infecção bacteriana (mais comum)",
    "Hepatite alcoólica",
    "Reativação HBV",
    "DILI",
    "Sangramento GI",
    "Cirurgia",
    "Sem gatilho identificável (20-40%)",
  ],
  clifSofaOrgaos: {
    figado: "Bilirrubina ≥ 12 mg/dL",
    rim: "Creatinina ≥ 2.0 mg/dL ou TRS",
    cerebral: "Encefalopatia grau III-IV",
    coagulacao: "INR ≥ 2.5",
    circulatorio: "PAM < 70 ou necessidade de vasopressores",
    respiratorio: "SpO2/FiO2 ≤ 214 ou PaO2/FiO2 ≤ 200",
  },
  fonte: "EASL-CLIF 2013; AASLD Practice Guidance ACLF 2024",
};

export const NACSELD_ACLF = {
  descricao: "NACSELD - North American Consortium for Study of ESLD",
  definicao: "≥ 2 falências de órgãos extra-hepáticos",
  orgaos: {
    choque: "PAM < 60 ou necessidade de vasopressores",
    encefalopatia: "Grau III-IV",
    renal: "Necessidade de diálise",
    respiratorio: "Necessidade de ventilação mecânica",
  },
  mortalidade30d: {
    "0 falencias": "6%",
    "1 falencia": "23%",
    "2 falencias": "49%",
    "3 falencias": "64%",
    "4 falencias": "77%",
  },
  fonte: "NACSELD; AASLD 2024",
};

// =============================================================================
// ASCITE E COMPLICAÇÕES (AASLD 2021)
// =============================================================================

export const ASCITE_GUIDELINES = {
  classificacao: {
    grau1: "Detectável apenas por US",
    grau2: "Distensão abdominal moderada e simétrica",
    grau3: "Distensão abdominal tensa",
  },
  tratamento: {
    grau1: "Restrição sódio (< 2g/dia)",
    grau2: "Espironolactona 100mg ± Furosemida 40mg",
    grau3: "Paracentese + albumina (se > 5L)",
    refrataria: "TIPS se elegível; transplante",
  },
  doseMaxima: {
    espironolactona: "400mg/dia",
    furosemida: "160mg/dia",
    proporcao: "100:40 (espiro:furo)",
  },
  metas: {
    perdaPeso: "0.5 kg/dia sem edema; 1 kg/dia com edema",
    sodioUrinario: "> 78 mEq/dia indica resposta",
  },
  refrataria: {
    definicao:
      "Sem resposta a dose máxima OU complicações impedem dose adequada",
    opcoes: ["TIPS", "Paracenteses seriadas", "Transplante"],
  },
  fonte: "AASLD Practice Guidance Ascites 2021",
};

export const PBE_GUIDELINES = {
  descricao: "Peritonite Bacteriana Espontânea",
  diagnostico: {
    criterio: "PMN ≥ 250/mm³ no líquido ascítico",
    cultura: "Positiva em ~40% dos casos",
  },
  tratamento: {
    empirico: "Cefotaxima 2g 8/8h ou Ceftriaxona 1-2g/dia",
    duracao: "5 dias (ou até PMN < 250)",
    albumina: "1.5g/kg D1 + 1g/kg D3 (se Cr > 1 ou BT > 4 ou Ur > 30)",
  },
  profilaxiaPrimaria: {
    indicacao: "Proteína ascite < 1.5g/dL + Child ≥ 9 ou Cr ≥ 1.2 ou Na ≤ 130",
    regime: "Norfloxacino 400mg/dia ou Cipro 500mg/dia",
  },
  profilaxiaSecundaria: {
    indicacao: "Após primeiro episódio de PBE",
    regime: "Norfloxacino 400mg/dia indefinido ou até Tx",
  },
  fonte: "AASLD 2021; EASL 2018",
};

export const SHR_GUIDELINES = {
  descricao: "Síndrome Hepatorrenal",
  tipos: {
    "SHR-AKI": {
      descricao: "Anteriormente SHR tipo 1",
      criterio: "Aumento Cr ≥ 0.3 em 48h ou ≥ 50% em 7 dias",
      mortalidade: "Muito alta sem tratamento",
    },
    "SHR-NAKI": {
      descricao: "Anteriormente SHR tipo 2",
      criterio: "eGFR < 60 por > 3 meses",
      associacao: "Frequentemente com ascite refratária",
    },
  },
  diagnostico: [
    "Cirrose com ascite",
    "AKI conforme critérios ICA",
    "Sem resposta a albumina 1g/kg por 48h",
    "Ausência de choque",
    "Sem nefrotóxicos recentes",
    "Sem doença renal parenquimatosa",
  ],
  tratamento: {
    primeiraLinha: "Terlipressina + Albumina",
    alternativa: "Norepinefrina + Albumina (em UTI)",
    albumina: "20-40g/dia",
    duracao: "Até reversão ou máximo 14 dias",
  },
  fonte: "AASLD 2021; ICA Consensus 2019",
};

// =============================================================================
// ENCEFALOPATIA HEPÁTICA (AASLD/EASL 2014)
// =============================================================================

export const ENCEFALOPATIA_GUIDELINES = {
  classificacao: {
    minima: "Alterações apenas em testes psicométricos",
    grau1: "Asterixis leve, déficit atenção, alteração sono",
    grau2: "Letargia, desorientação tempo, asterixis evidente",
    grau3: "Sonolência, confusão, desorientação lugar",
    grau4: "Coma",
  },
  gatilhos: [
    "Infecção (mais comum)",
    "Sangramento GI",
    "Constipação",
    "Distúrbio eletrolítico",
    "Desidratação",
    "Uso de sedativos/opioides",
    "TIPS recente",
    "Excesso proteico (raro)",
  ],
  tratamento: {
    lactulose: {
      dose: "25-30mL 2-4x/dia",
      meta: "2-3 evacuações pastosas/dia",
    },
    rifaximina: {
      dose: "550mg 2x/dia",
      indicacao: "Profilaxia secundária após ≥ 1 episódio",
    },
    nutricao: {
      proteina: "1.2-1.5g/kg/dia (NÃO restringir)",
      bcaa: "Considerar BCAA se intolerância proteica",
    },
  },
  fonte: "AASLD/EASL Practice Guidelines HE 2014",
};

// =============================================================================
// HIPERTENSÃO PORTAL E VARIZES (Baveno VII 2022)
// =============================================================================

export const HIPERTENSAO_PORTAL_GUIDELINES = {
  definicao: "HVPG ≥ 6 mmHg",
  clinicamenteSignificativa: "HVPG ≥ 10 mmHg",
  varizes: {
    rastreamento: {
      cirrose: "EDA no diagnóstico",
      semVarizes: "Repetir EDA em 2-3 anos se compensado",
      varizesPequenas: "Repetir EDA em 1-2 anos",
    },
    classificacao: {
      pequenas: "< 5mm, achatam com insuflação",
      medias: "5-10mm ou < 1/3 do lúmen",
      grandes: "> 10mm ou > 1/3 do lúmen",
    },
    profilaxiaPrimaria: {
      indicacao: "Varizes médias-grandes ou pequenas com sinais de risco",
      opcoes: ["NSBB (Carvedilol preferido)", "Ligadura elástica"],
      carvedilol: "6.25mg 2x/dia (máx 12.5mg 2x/dia)",
    },
    sangramentoAgudo: {
      ressuscitacao: "Hb alvo 7-8g/dL",
      vasoativo: "Terlipressina 2mg + 1mg 4/4h ou Octreotide",
      eda: "< 12h após estabilização",
      atb: "Ceftriaxona 1g/dia por 7 dias",
      tips: "Se falha endoscópica ou resangramento precoce",
    },
    profilaxiaSecundaria: {
      regime: "NSBB + Ligadura elástica",
      seguimento: "EDA a cada 2-4 semanas até erradicação",
    },
  },
  baveno7CSPH:
    "Elastografia ≥ 25 kPa + plaquetas > 150.000 = baixo risco de CSPH",
  fonte: "Baveno VII 2022",
};

export const TIPS_GUIDELINES = {
  descricao: "Transjugular Intrahepatic Portosystemic Shunt",
  indicacoes: {
    varizes: "Sangramento refratário ou profilaxia secundária selecionada",
    ascite: "Ascite refratária",
    hidrotorax: "Hidrotórax hepático refratário",
    buddChiari: "Síndrome de Budd-Chiari",
    pvt: "Trombose portal selecionada",
  },
  contraindicacoes: {
    absolutas: [
      "ICC direita grave",
      "Doença hepática policística",
      "Sepse não controlada",
      "Hipertensão pulmonar grave",
    ],
    relativas: [
      "Encefalopatia recorrente",
      "MELD > 18-20",
      "Bilirrubina > 5 mg/dL",
      "Idade > 70 anos",
      "CHC central",
    ],
  },
  complicacoes: {
    encefalopatia: "20-50% - tratar com rifaximina ± lactulose",
    estenose: "Raro com stents revestidos",
    insuficienciaHepatica: "Risco se Child C ou MELD alto",
  },
  fonte: "AASLD Practice Guidance TIPS 2024",
};

export const VARIZES_GASTRICAS = {
  classificacao: {
    GOV1: "Extensão de varizes esofágicas para pequena curvatura",
    GOV2: "Extensão para fundo gástrico",
    IGV1: "Fundo gástrico isoladas",
    IGV2: "Ectópicas (corpo, antro, duodeno)",
  },
  tratamento: {
    GOV1: "Tratar como varizes esofágicas",
    GOV2_IGV1: {
      agudo: "Cianoacrilato (preferido) ou TIPS",
      profilaxia: "TIPS ou cianoacrilato seriado",
    },
  },
  fonte: "Baveno VII 2022; AASLD",
};

// =============================================================================
// NUTRIÇÃO EM CIRROSE (AASLD 2021)
// =============================================================================

export const NUTRICAO_CIRROSE = {
  recomendacoes: {
    calorias: "35 kcal/kg/dia (peso seco)",
    proteinas: "1.2-1.5 g/kg/dia",
    refeicoes: "4-6 refeições/dia incluindo lanche noturno",
    jejum: "Evitar jejum > 6 horas",
  },
  sarcopenia: {
    definicao: "Perda de massa e função muscular",
    diagnostico: "Área muscular em L3 por TC ou DEXA",
    impacto: "Aumenta mortalidade pré e pós-transplante",
    manejo: "Exercício resistido + suplementação proteica",
  },
  fragilidade: {
    avaliacao: "Liver Frailty Index (LFI)",
    componentes: ["Hand grip", "Chair stands", "Balance"],
    impacto: "Preditor independente de mortalidade em lista",
  },
  bcaa: {
    indicacao: "Intolerância proteica ou EH recorrente",
    dose: "Suplementar com BCAA oral",
  },
  fonte: "AASLD Practice Guidance Nutrition 2021",
};

// =============================================================================
// DOENÇA HEPÁTICA ALCOÓLICA (AASLD 2020)
// =============================================================================

export const DOENCA_HEPATICA_ALCOOLICA = {
  espectro: [
    "Esteatose alcoólica",
    "Esteatohepatite alcoólica",
    "Hepatite alcoólica aguda",
    "Cirrose alcoólica",
  ],
  hepatiteAlcoolica: {
    diagnostico: {
      clinico: "Icterícia de início recente + uso pesado de álcool",
      laboratorial: "AST/ALT > 1.5, AST < 400, BT > 3",
      exclusao: "Excluir outras causas de hepatite",
    },
    gravidade: {
      maddrey: {
        formula: "4.6 × (TP paciente - TP controle) + BT",
        grave: "≥ 32",
      },
      meld: {
        grave: "≥ 21",
      },
      lille: {
        dia7: "> 0.45 = não respondedor",
      },
    },
    tratamento: {
      prednisolona: {
        indicacao: "MDF ≥ 32 ou MELD ≥ 21",
        dose: "40mg/dia por 28 dias",
        contraindicacoes: [
          "Infecção ativa",
          "Sangramento GI ativo",
          "IRA grave",
          "HBV/pancreatite",
        ],
      },
      nutricao: "1.2-1.5g proteína/kg + 35-40 kcal/kg/dia",
      suplementos: "Tiamina 250mg/dia, Folato, Multivitamínico",
    },
    transplante: {
      historico: "6 meses abstinência - não mais obrigatório",
      atual: "Avaliação psicossocial rigorosa em casos selecionados",
    },
  },
  fonte: "AASLD Practice Guidance ALD 2020",
};

// =============================================================================
// HEPATITE AUTOIMUNE (AASLD 2020)
// =============================================================================

export const HEPATITE_AUTOIMUNE = {
  diagnostico: {
    scoreSimplificado: {
      ANA_SMA: "≥ 1:40 = 1 ponto; ≥ 1:80 = 2 pontos",
      antiLKM1: "≥ 1:40 = 2 pontos",
      antiSLA: "Positivo = 2 pontos",
      IgG: "> LSN = 1 ponto; > 1.1× LSN = 2 pontos",
      histologia: "Compatível = 1; Típica = 2",
      ausenciaHepatiteViral: "Obrigatório",
    },
    interpretacao: {
      "6 pontos": "Provável HAI",
      "≥ 7 pontos": "HAI definida",
    },
  },
  tratamento: {
    inducao: {
      opcao1: "Prednisona 60mg + Azatioprina 50mg",
      opcao2: "Prednisona 40-60mg monoterapia",
      reducao: "Reduzir prednisona gradualmente",
    },
    manutencao: {
      regime: "Azatioprina 1-2mg/kg ± prednisona baixa dose",
      meta: "AST/ALT normais + IgG normal",
    },
    remissao: {
      definicao: "AST/ALT normais + IgG normal + histologia sem inflamação",
      suspensao: "Considerar após 2 anos de remissão completa",
      recaida: "80% recaem - maioria precisa tratamento longo prazo",
    },
  },
  fonte: "AASLD Practice Guidelines AIH 2020",
};

// =============================================================================
// COLANGITE BILIAR PRIMÁRIA (AASLD 2021)
// =============================================================================

export const PBC_GUIDELINES = {
  diagnostico: {
    criterios: "2 de 3: FA elevada, AMA positivo, histologia compatível",
    AMA: "AMA-M2 em > 90% dos casos",
    AMANegativo: "5-10% são AMA negativos - considerar ANA específicos",
  },
  tratamento: {
    primeiraLinha: "UDCA 13-15mg/kg/dia",
    avaliacaoResposta: {
      criterios: [
        "Paris I: ALP < 3× LSN, AST < 2× LSN, BT ≤ 1",
        "Paris II: ALP < 1.5× LSN, AST < 1.5× LSN, BT normal",
        "UK-PBC risk score",
      ],
      quando: "Após 12 meses de UDCA",
    },
    segundaLinha: {
      obeticholicAcid: "5-10mg/dia se resposta inadequada a UDCA",
      fibrates: "Bezafibrato ou Fenofibrato off-label",
    },
  },
  prurido: {
    opcoes: [
      "Colestiramina 4-16g/dia",
      "Rifampicina 150-600mg/dia",
      "Naltrexona",
      "Sertralina",
    ],
  },
  fonte: "AASLD PBC Update 2021",
};

// =============================================================================
// COLANGITE ESCLEROSANTE PRIMÁRIA (AASLD 2023)
// =============================================================================

export const PSC_GUIDELINES = {
  diagnostico: {
    colangiografia: "Estenoses multifocais + dilatações (padrão em contas)",
    exclusao: "Excluir CEI secundária",
    biopsiaHepatica: "Não obrigatória se ColangioRM típica",
  },
  associacoes: {
    dii: "70-80% têm DII (principalmente RCU)",
    colangiocarcinoma: "Risco de 5-10% ao longo da vida",
  },
  vigilancia: {
    colangiocarcinoma: "CA 19-9 + ColangioRM anual (controvérsia)",
    colonoscopia: "Anual se DII associada",
    caCooretal: "Risco aumentado se PSC + DII",
  },
  tratamento: {
    udca: "Não recomendado rotineiramente (sem benefício comprovado)",
    estenosesDominantes: "CPRE com dilatação ± escovado",
    transplante: "Única terapia curativa",
  },
  fonte: "AASLD Practice Guidance PSC 2023",
};

// =============================================================================
// MASLD / MASH - NOVA NOMENCLATURA (AASLD 2023)
// =============================================================================

export const MASLD_GUIDELINES = {
  nomenclatura: {
    MASLD:
      "Metabolic dysfunction-Associated Steatotic Liver Disease (antigo NAFLD)",
    MASH: "Metabolic dysfunction-Associated Steatohepatitis (antigo NASH)",
    MetALD: "MASLD + álcool moderado",
  },
  criteriosCardiometabolicos: {
    obrigatorio: "Esteatose hepática + ≥ 1 fator de risco cardiometabólico",
    fatores: [
      "IMC ≥ 25 ou circunferência abdominal elevada",
      "Glicemia jejum ≥ 100 ou DM2 ou em tratamento",
      "PA ≥ 130/85 ou em tratamento",
      "TG ≥ 150 ou em tratamento",
      "HDL < 40 (H) ou < 50 (M) ou em tratamento",
    ],
  },
  avaliacaoFibrose: {
    naoInvasivo: {
      FIB4: "< 1.3 baixo risco; > 2.67 alto risco",
      elastografia: "< 8 kPa descarta fibrose significativa; ≥ 12-15 kPa F3-4",
    },
    biopsia: "Indicada se dúvida diagnóstica ou estadiamento pré-tratamento",
  },
  tratamento: {
    MEV: "Perda 7-10% peso reduz esteatose e inflamação",
    farmacologico: {
      resmetirom: "Aprovado FDA 2024 para MASH com F2-F3",
      pioglitazona: "30-45mg/dia - benefício em MASH comprovada",
      vitaminaE: "800UI/dia para MASH não diabético",
      semaglutide: "Benefício em estudos; aguardando aprovação específica",
    },
  },
  fonte: "AASLD Nomenclature 2023; AASLD Practice Guidance 2023",
};

export const RESMETIROM_GUIDELINES = {
  descricao: "Primeiro fármaco aprovado especificamente para MASH",
  indicacao: "MASH com fibrose F2-F3 (moderada a avançada)",
  mecanismo:
    "Agonista seletivo do receptor de hormônio tireoideano beta (THR-β)",
  posologia: {
    dose: "80mg ou 100mg 1x/dia",
    titulacao: "Iniciar 80mg; considerar aumento para 100mg",
  },
  eficacia: {
    MAESTRO_NASH: "Resolução de MASH sem piora de fibrose em 25-30% vs placebo",
    reducaoFibrose: "Redução ≥ 1 estágio em ~25% dos pacientes",
  },
  monitoramento: {
    TFTs: "TSH, T4L basal e periódico",
    lipidios: "Reduz LDL significativamente",
    hepatico: "ALT/AST podem elevar transitoriamente",
  },
  contraindicacoes: [
    "Gravidez/lactação",
    "Doença tireoidiana descompensada",
    "Cirrose descompensada",
  ],
  fonte: "FDA Approval 2024; AASLD",
};

export const SEMAGLUTIDE_GUIDELINES = {
  descricao: "GLP-1 RA com benefício em MASH",
  indicacaoAtual: "Aprovado para DM2 e obesidade; em estudo para MASH/MASLD",
  estudos: {
    "STEP-NASH": "Melhora histológica significativa em MASH",
  },
  dose: "2.4mg SC semanal (dose obesidade)",
  beneficios: [
    "Perda de peso significativa",
    "Melhora de esteatose",
    "Potencial redução de fibrose",
  ],
  consideracoes: [
    "Não aprovado especificamente para MASH ainda",
    "Usar se comorbidade (DM2 ou obesidade)",
  ],
  fonte: "AASLD; Estudos STEP",
};

// =============================================================================
// HEPATITES VIRAIS (AASLD-IDSA)
// =============================================================================

export const HBV_GUIDELINES = {
  descricao: "Hepatite B Crônica",
  fases: {
    imunotolerante:
      "HBeAg+, DNA muito alto, ALT normal, sem fibrose significativa",
    imunoativo: "HBeAg+ ou -, DNA elevado, ALT elevada, inflamação ativa",
    portadorInativo: "HBeAg-, anti-HBe+, DNA < 2000, ALT normal",
    reativacao: "Exacerbação após fase inativa",
  },
  tratamento: {
    indicacoes: [
      "Cirrose (qualquer DNA/ALT)",
      "ALT > 2× LSN + DNA > 2000 (HBeAg-) ou > 20000 (HBeAg+)",
      "Fibrose significativa (≥ F2)",
      "História familiar de CHC",
      "Imunossupressão planejada",
      "Coinfecção HIV/HCV/HDV",
      "Gestantes com DNA alto no 3º trimestre",
    ],
    primeiraLinha: {
      entecavir: "0.5mg/dia (1mg se resistência a lamivudina)",
      TDF: "300mg/dia",
      TAF: "25mg/dia (preferido se doença renal/óssea)",
    },
    duracao: {
      HBeAgPositivo: "Até soroconversão + 12 meses de consolidação",
      HBeAgNegativo: "Indefinido (até perda HBsAg)",
      cirrose: "Indefinido",
    },
  },
  vigilanciaCHC: {
    quem: "Cirróticos + asiáticos > 40a + africanos > 20a + história familiar",
    como: "US + AFP cada 6 meses",
  },
  fonte: "AASLD HBV Guidance 2025 Update",
};

export const HCV_GUIDELINES = {
  descricao: "Hepatite C - Era DAA",
  tratamentoPanGenótipo: {
    Sofosbuvir_Velpatasvir: {
      regime: "SOF/VEL 400/100mg 1x/dia",
      duracao: "12 semanas (sem cirrose ou Child A)",
      cirrose: "± Ribavirina se Child B; individualizar Child C",
    },
    Glecaprevir_Pibrentasvir: {
      regime: "GLE/PIB 300/120mg 1x/dia",
      duracao: "8 semanas (naive sem cirrose); 12 semanas (cirrose compensada)",
      contraindicacao: "Child B/C (contém inibidor de protease)",
    },
  },
  SVR: {
    definicao: "HCV-RNA indetectável 12 semanas pós-tratamento",
    taxas: "> 95% com DAAs atuais",
  },
  seguimentoPosSVR: {
    semCirrose: "Pode ter alta se SVR confirmada",
    comCirrose: "Vigilância CHC indefinida (US + AFP cada 6 meses)",
  },
  fonte: "AASLD-IDSA HCV Guidance 2023",
};

// =============================================================================
// DILI - DRUG-INDUCED LIVER INJURY (AASLD 2024)
// =============================================================================

export const DILI_GUIDELINES = {
  descricao: "Lesão Hepática Induzida por Drogas",
  padrao: {
    hepatocelular: "R ≥ 5 (ALT/LSN ÷ FA/LSN)",
    colestatico: "R ≤ 2",
    misto: "R entre 2 e 5",
  },
  RUCAM: {
    descricao: "Roussel Uclaf Causality Assessment Method",
    interpretacao: {
      "≤ 0": "Excluído",
      "1-2": "Improvável",
      "3-5": "Possível",
      "6-8": "Provável",
      "≥ 9": "Altamente provável",
    },
  },
  hyLaw: {
    descricao: "Lei de Hy - prediz risco de falência hepática",
    criterios: "ALT ≥ 3× LSN + BT ≥ 2× LSN + sem outra causa + FA < 2× LSN",
    mortalidade: "10-50% de mortalidade/transplante",
  },
  conduta: {
    suspensao: "Suspender droga suspeita imediatamente",
    NAC: "Considerar N-acetilcisteína em DILI grave",
    corticoides: "Controverso; considerar se componente autoimune",
    transplante: "Se falência hepática aguda",
  },
  drogas: {
    comuns: [
      "Paracetamol (dose-dependente)",
      "AINEs",
      "Antibióticos (amox-clav, isoniazida, nitrofurantoína)",
      "Anticonvulsivantes",
      "Estatinas (raro)",
    ],
    herbaceos: [
      "Chá verde (alta concentração)",
      "Kava",
      "Suplementos para emagrecimento",
    ],
  },
  fonte: "AASLD Practice Guidance DILI 2024",
};

// =============================================================================
// HEMOCROMATOSE (AASLD 2011)
// =============================================================================

export const HEMOCROMATOSE_GUIDELINES = {
  diagnostico: {
    screening: "Saturação transferrina > 45% + ferritina elevada",
    genetico: "HFE C282Y homozigoto (maioria dos casos)",
    outros: "C282Y/H63D heterozigoto composto; HFE negativo raro",
  },
  indicacaoTratamento: {
    flebotomia: "Ferritina > 300 (homens) ou > 200 (mulheres)",
    meta: "Ferritina 50-100 ng/mL",
  },
  tratamento: {
    flebotomiaInducao: "500mL semanal até ferritina < 50",
    flebotomiaManutencao: "A cada 2-4 meses para manter ferritina 50-100",
    quelantes: "Apenas se flebotomia contraindicada",
  },
  vigilanciaCHC: {
    indicacao: "Se cirrose estabelecida",
    regime: "US + AFP cada 6 meses",
  },
  familyScreening: {
    quem: "Parentes de primeiro grau",
    como: "Saturação transferrina + ferritina + teste genético",
  },
  fonte: "AASLD Practice Guideline Hemochromatosis 2011",
};

// =============================================================================
// DOENÇA DE WILSON (AASLD 2022)
// =============================================================================

export const WILSON_DISEASE_GUIDELINES = {
  descricao: "Doença de Wilson - Diagnóstico e Tratamento",
  definicao:
    "Doença autossômica recessiva causada por mutações no gene ATP7B, resultando em acúmulo tóxico de cobre no fígado, cérebro e outros órgãos",

  diagnostico: {
    scorLeipzig: {
      descricao: "Score de Leipzig para diagnóstico de Doença de Wilson",
      criterios: {
        anelKayserFleischer: { presente: 2, ausente: 0 },
        sintomasNeurologicos: { presentes: 2, ausentes: 0 },
        ceruloplasmina: {
          "<10mg/dL": 2,
          "10-20mg/dL": 1,
          "normal (>20mg/dL)": 0,
        },
        cobreUrinario24h: {
          ">100mcg (>2x LSN)": 2,
          "40-100mcg (1-2x LSN)": 1,
          normal: 0,
        },
        cobreHepatico: {
          ">250mcg/g peso seco": 2,
          "50-250mcg/g": 1,
          "<50mcg/g (exclui DW)": -1,
        },
        rodanina: { presente: 1, ausente: 0 },
        mutacaoATP7B: {
          "2 mutações patogênicas": 4,
          "1 mutação patogênica": 1,
          "nenhuma detectada": 0,
        },
      },
      interpretacao: {
        "≥4 pontos": "Diagnóstico estabelecido",
        "3 pontos": "Diagnóstico provável - investigação adicional",
        "≤2 pontos": "Diagnóstico improvável",
      },
    },
    examesLaboratoriais: {
      ceruloplasmina: {
        normal: ">20 mg/dL",
        suspeito: "<20 mg/dL",
        altamenteSugestivo: "<10 mg/dL",
        nota: "Pode estar normal em 5-15% dos casos; eleva em inflamação",
      },
      cobreUrinario24h: {
        normal: "<40 mcg/24h",
        suspeito: ">40 mcg/24h",
        altamenteSugestivo: ">100 mcg/24h",
        emTratamento:
          "Alvo: 200-500 mcg/24h (quelante) ou <100 mcg/24h (zinco)",
      },
      cobreNaoCeruloplasmina: {
        formula: "Cobre sérico total - (Ceruloplasmina × 3)",
        normal: "<15 mcg/dL",
        alvo: "5-15 mcg/dL em tratamento",
        elevado: ">25 mcg/dL sugere DW não tratada ou má aderência",
      },
      cobreHepatico: {
        normal: "<50 mcg/g peso seco",
        diagnostico: ">250 mcg/g peso seco",
        nota: "Padrão-ouro; pode haver sobreposição com colestase crônica",
      },
    },
    exameFisico: {
      anelKayserFleischer: {
        descricao: "Depósito de cobre na membrana de Descemet",
        exame: "Lâmpada de fenda por oftalmologista",
        prevalencia: "95% na forma neurológica; 50% na forma hepática isolada",
      },
      catarataSunflower: "Catarata em girassol - menos comum",
    },
    imagemCerebral: {
      RM: {
        achados: [
          "Sinal do 'face de panda gigante' no mesencéfalo",
          "Hiperintensidade em T2 nos núcleos da base",
          "Atrofia cerebelar",
          "Lesões talâmicas",
        ],
        indicacao: "Todos pacientes com sintomas neurológicos",
      },
    },
  },

  manifestacoesClinias: {
    hepaticas: {
      espectro: [
        "Elevação assintomática de transaminases",
        "Hepatite crônica ativa",
        "Cirrose",
        "Insuficiência hepática aguda (Wilsoniana)",
      ],
      ihAguada: {
        caracteristicas: [
          "Anemia hemolítica Coombs-negativa",
          "FA/BT ratio < 4 (muito sugestivo)",
          "AST/ALT ratio > 2",
          "INR muito elevado com transaminases moderadas",
          "Insuficiência renal aguda concomitante",
        ],
        prognostico: "Alta mortalidade sem transplante emergencial",
      },
    },
    neurologicas: {
      tipos: [
        "Disartria",
        "Distonia",
        "Tremor (tipo 'bater de asas')",
        "Parkinsonismo",
        "Coreoatetose",
        "Ataxia",
      ],
      pioraInicial: "10-20% podem piorar no início do tratamento com quelantes",
    },
    psiquiatricas: [
      "Depressão",
      "Alterações de personalidade",
      "Psicose",
      "Déficit cognitivo",
      "Ansiedade",
    ],
    outras: [
      "Anemia hemolítica",
      "Disfunção renal (síndrome de Fanconi)",
      "Cardiomiopatia",
      "Artropatia",
      "Hipoparatireoidismo",
    ],
  },

  tratamento: {
    fases: {
      inicial: "Redução agressiva do cobre corporal (quelação)",
      manutencao: "Prevenção de reacúmulo",
    },
    medicamentos: {
      dPenicilamina: {
        classe: "Quelante",
        mecanismo: "Promove excreção urinária de cobre",
        dose: {
          inicial: "250-500 mg/dia, aumentar gradualmente",
          manutencao: "750-1500 mg/dia dividido em 2-4 doses",
          pediatrico: "20 mg/kg/dia",
        },
        administracao: "1 hora antes ou 2 horas após refeições",
        monitoramento: [
          "Hemograma semanal por 1 mês, depois mensal",
          "EAS para proteinúria",
          "Cobre urinário 24h",
        ],
        efeitosAdversos: [
          "Reações de hipersensibilidade (febre, rash)",
          "Leucopenia, trombocitopenia",
          "Proteinúria, síndrome nefrótica",
          "Lúpus-like",
          "Elastose serpiginosa perforante",
          "Piora neurológica inicial (10-20%)",
        ],
        gravidez: "Categoria D - reduzir dose para 25-50% no 3º trimestre",
      },
      trientina: {
        classe: "Quelante alternativo",
        mecanismo: "Quelação de cobre com excreção urinária",
        dose: {
          inicial: "750-1500 mg/dia dividido em 2-3 doses",
          manutencao: "750-1500 mg/dia",
          pediatrico: "20 mg/kg/dia",
        },
        vantagens: [
          "Melhor tolerado que D-penicilamina",
          "Menor risco de piora neurológica",
          "Preferido para pacientes intolerantes à D-penicilamina",
        ],
        efeitosAdversos: [
          "Gastrite (menos comum)",
          "Anemia sideroblástica (raro)",
          "Colite (raro)",
        ],
        gravidez: "Categoria C - manter com ajuste de dose",
      },
      zinco: {
        classe: "Bloqueador de absorção",
        mecanismo: "Induz metalotioneína intestinal que sequestra cobre",
        dose: {
          adulto: "150 mg de zinco elementar/dia dividido em 3 doses",
          pediatrico: "75 mg/dia em < 50 kg",
        },
        administracao: "Longe das refeições (1h antes ou 2h após)",
        indicacoes: [
          "Manutenção após quelação inicial",
          "Pacientes assintomáticos",
          "Gestantes (preferido)",
          "Intolerância a quelantes",
        ],
        efeitosAdversos: [
          "Gastrite, náusea",
          "Pancreatite bioquímica (raro)",
          "Deficiência de cobre se excesso",
        ],
        monitoramento: "Cobre urinário 24h alvo < 75 mcg (ideal < 50)",
        nota: "NÃO usar simultaneamente com quelantes (separar por 4-5h)",
      },
    },
    escolhaTerapeutica: {
      sintomaticoHepatico: "Trientina ou D-penicilamina (quelante)",
      sintomaticoNeurologico: "Trientina preferida (menor risco de piora)",
      assintomatico: "Quelante dose baixa OU Zinco",
      manutencao: "Zinco OU quelante em dose reduzida",
      gravidez: "Zinco preferido; se quelante necessário, reduzir dose",
    },
    alvosMonitoramento: {
      cobreUrinario24h: {
        quelante: "200-500 mcg/24h (fase inicial); reduz com o tempo",
        zinco: "< 75-100 mcg/24h",
      },
      cobreNaoCeruloplasmina: "5-15 mcg/dL",
      transaminases: "Normalização progressiva",
      funcaoHepatica: "Estabilização/melhora de albumina, INR, bilirrubina",
    },
    dietaCobre: {
      evitar: [
        "Fígado e vísceras",
        "Frutos do mar (especialmente ostras, lagosta)",
        "Nozes e castanhas",
        "Chocolate",
        "Cogumelos",
      ],
      limiteRecomendado: "< 1 mg de cobre/dia na fase inicial",
      nota: "Dieta sozinha é insuficiente; sempre associar tratamento farmacológico",
    },
  },

  transplante: {
    indicacoes: {
      absolutas: [
        "Insuficiência hepática aguda Wilsoniana (Nazer/NWI elevado)",
        "Cirrose descompensada refratária ao tratamento médico",
      ],
      relativas: [
        "Cirrose com complicações recorrentes",
        "Não aderência persistente ao tratamento",
      ],
    },
    scoreNWI: {
      descricao: "New Wilson Index - preditor de necessidade de transplante",
      parametros: ["Bilirrubina", "INR", "AST", "Leucócitos", "Albumina"],
      interpretacao: {
        "<11": "Sobrevida com tratamento médico provável",
        "≥11": "Alta mortalidade sem transplante - considerar listagem urgente",
      },
    },
    resultados: "Excelente sobrevida pós-transplante (>85% em 5 anos)",
    nota: "Tx cura o defeito hepático; manifestações neurológicas podem persistir ou melhorar lentamente",
  },

  situacoesEspeciais: {
    gravidez: {
      tratamento: "Manter tratamento - risco de descompensação se suspender",
      preferido: "Zinco (menor risco teratogênico)",
      quelantes: "Reduzir dose 25-50% no 3º trimestre",
      amamentacao: "Dados limitados; monitorar o lactente",
    },
    pediatrico: {
      triagem: "Todos irmãos de pacientes diagnosticados",
      idadeDiagnostico: "Raro antes dos 3 anos; pico 5-15 anos",
      tratamento: "Mesmo que adultos, com ajuste de dose",
    },
    screeningFamiliar: {
      obrigatorio: "Todos parentes de primeiro grau",
      exames: [
        "Ceruloplasmina",
        "Cobre urinário 24h",
        "Função hepática",
        "Teste genético",
      ],
      idade: "A partir dos 3-4 anos (ou antes se sintomático)",
    },
  },

  fonte: "AASLD Multidisciplinary Guidance Wilson Disease 2022; EASL CPG 2012",
};

export const SCORE_NWI_WILSON = {
  descricao: "New Wilson Index para insuficiência hepática aguda",
  parametros: {
    bilirrubina: {
      "<5.8 mg/dL": 0,
      "5.8-8.8": 1,
      "8.9-11.7": 2,
      "11.8-17.5": 3,
      ">17.5": 4,
    },
    INR: {
      "<1.3": 0,
      "1.3-1.6": 1,
      "1.7-1.9": 2,
      "2.0-2.4": 3,
      "≥2.5": 4,
    },
    AST: {
      "<100": 0,
      "100-150": 1,
      "151-200": 2,
      "201-300": 3,
      ">300": 4,
    },
    leucocitos: {
      "<6.7": 0,
      "6.7-8.3": 1,
      "8.4-10.3": 2,
      "10.4-15.3": 3,
      ">15.3": 4,
    },
    albumina: {
      ">4.5": 0,
      "3.4-4.4": 1,
      "2.5-3.3": 2,
      "2.1-2.4": 3,
      "≤2.0": 4,
    },
  },
  interpretacao: {
    "<11": "Provável sobrevida com tratamento médico",
    "≥11": "Alta probabilidade de necessidade de transplante urgente",
  },
  fonte: "Dhawan et al., Liver Transpl 2005",
};

// =============================================================================
// COLANGIOCARCINOMA (EASL-ILCA 2023 / EASL 2025)
// =============================================================================

export const COLANGIOCARCINOMA_GUIDELINES = {
  descricao: "Colangiocarcinoma - Classificação, Diagnóstico e Tratamento",

  classificacao: {
    anatomica: {
      iCCA: {
        nome: "Colangiocarcinoma Intra-hepático",
        localizacao:
          "Proximal aos ductos biliares de segunda ordem (dentro do parênquima)",
        frequencia: "10-20% dos CCAs (incidência crescente)",
        subtipos: {
          smallDuct: {
            descricao: "Ducto pequeno - surge de colangiócitos periféricos",
            caracteristicas: "Mass-forming, sem mucina",
            molecular: "Frequente: FGFR2 fusions, IDH1 mutations, BAP1",
          },
          largeDuct: {
            descricao: "Ducto grande - surge de ductos maiores",
            caracteristicas: "Periductal infiltrating, produz mucina",
            molecular: "Similar a pCCA/dCCA: KRAS, TP53, SMAD4",
          },
        },
      },
      pCCA: {
        nome: "Colangiocarcinoma Peri-hilar (Tumor de Klatskin)",
        localizacao: "Entre ductos de segunda ordem e inserção do cístico",
        frequencia: "50-60% dos CCAs",
        classificacaoBismuth: {
          I: "Abaixo da confluência dos hepáticos",
          II: "Atinge a confluência",
          IIIa: "Estende ao ducto hepático direito",
          IIIb: "Estende ao ducto hepático esquerdo",
          IV: "Envolve ambos ductos hepáticos ou multifocal",
        },
      },
      dCCA: {
        nome: "Colangiocarcinoma Distal",
        localizacao: "Abaixo da inserção do ducto cístico até ampola",
        frequencia: "20-30% dos CCAs",
      },
    },
    terminologia: {
      eCCA: "Extrahepatic CCA = pCCA + dCCA",
      BTC: "Biliary Tract Cancer = todos CCAs + vesícula + ampola",
      nota: "A classificação tripartida (iCCA vs pCCA vs dCCA) é recomendada",
    },
  },

  fatoresRisco: {
    estabelecidos: [
      "Colangite esclerosante primária (PSC) - risco 5-10% ao longo da vida",
      "Infecção por liver flukes (Opisthorchis, Clonorchis) - endêmico no Sudeste Asiático",
      "Cistos de colédoco (malformações biliares)",
      "Hepatolitíase",
      "Cirrose (para iCCA)",
      "Hepatites B e C (para iCCA)",
    ],
    emergentes: [
      "Diabetes mellitus",
      "Obesidade",
      "MASLD/MASH",
      "Álcool",
      "Tabagismo",
    ],
    geneticos: [
      "Síndrome de Lynch (MSI-H) - raro",
      "BRCA1/2 - associação em estudo",
    ],
  },

  diagnostico: {
    marcadores: {
      CA199: {
        descricao: "Marcador mais utilizado",
        corte: ">129 U/mL em PSC tem sensibilidade ~80%",
        limitacoes: [
          "Eleva em colestase benigna",
          "Falso-negativo em Lewis-negativos (5-10%)",
          "Inespecífico isoladamente",
        ],
        emPSC: {
          vigilancia: "CA 19-9 + imagem anual",
          elevado: "Investigar com ColangioRM/CPRE + escovado",
        },
      },
      CEA: {
        descricao: "Complementar",
        utilidade: "Menor sensibilidade; usado em conjunto",
      },
    },
    imagem: {
      iCCA: {
        TC: "Lesão hipodensa com realce arterial periférico, washout tardio",
        RM: "Hipointenso T1, hiperintenso T2, retração capsular",
        caracteristicas: [
          "Dilatação biliar periférica",
          "Retração capsular (fibrose)",
          "Trombose portal associada (diferente de CHC)",
        ],
      },
      pCCA_dCCA: {
        ColangioRM: "Exame de escolha - avalia extensão da estenose",
        TC: "Estadiamento, invasão vascular, linfonodos",
        caracteristicas: "Estenose biliar com dilatação a montante",
      },
      PETCT: {
        utilidade: "Estadiamento, detecção de metástases",
        limitacao: "Baixa sensibilidade para lesões pequenas ou mucinas",
      },
    },
    histologia: {
      obrigatorio: "Confirmação histológica antes de tratamento sistêmico",
      metodos: [
        "Biópsia percutânea (para iCCA)",
        "Escovado biliar por CPRE (para pCCA/dCCA)",
        "Colangioscopia com biópsia (SpyGlass)",
        "EUS com punção (para lesões periductais)",
      ],
      FISH: "Polissomia aumenta acurácia do escovado",
    },
    imunohistoquimica: {
      iCCA: "CK7+, CK19+, CK20-, Hep Par 1-",
      diferencialCHC: "Arginase-1, Hep Par 1, AFP (negativos em CCA)",
      diferencialMetastase: {
        colon: "CK20+, CDX2+",
        pancreas: "SMAD4 loss",
      },
    },
  },

  perfilMolecular: {
    iCCA: {
      FGFR2_fusions: {
        frequencia: "10-20%",
        terapiaAlvo: "Pemigatinib, Futibatinib, Infigratinib (aprovados)",
      },
      IDH1_mutations: {
        frequencia: "10-20%",
        terapiaAlvo: "Ivosidenib (aprovado)",
      },
      BAP1_mutations: "12-19%",
      ARID1A: "10-20%",
    },
    pCCA_dCCA: {
      KRAS: "30-46%",
      TP53: "35-55%",
      SMAD4: "8-30%",
      CDKN2A: "5-28%",
      HER2_ERBB2: {
        frequencia: "3-9%",
        terapiaAlvo: "Trastuzumab deruxtecan (T-DXd) em estudo",
      },
    },
    todosSubtipos: {
      MSI_H_dMMR: {
        frequencia: "1-4%",
        terapiaAlvo: "Pembrolizumab (aprovado para MSI-H)",
      },
      BRAF_V600E: {
        frequencia: "3-5%",
        terapiaAlvo: "Dabrafenib + Trametinib",
      },
      NTRK_fusions: {
        frequencia: "<1%",
        terapiaAlvo: "Larotrectinib, Entrectinib",
      },
    },
    recomendacao:
      "NGS (sequenciamento de nova geração) recomendado para todos pacientes com CCA avançado",
  },

  estadiamento: {
    iCCA: {
      sistema: "AJCC 8th edition",
      fatoresPrognosticos: [
        "Tamanho tumoral",
        "Número de lesões",
        "Invasão vascular",
        "Linfonodos positivos",
        "Invasão perineural",
      ],
    },
    pCCA: {
      sistema: "AJCC 8th + Classificação de Bismuth-Corlette",
      ressecabilidade: {
        ressecavel: [
          "Ausência de metástases",
          "Ausência de envolvimento bilateral de ramos portais/arteriais",
          "Ausência de envolvimento contralateral de ductos de 2ª ordem",
        ],
        irressecavel: [
          "Metástases",
          "Envolvimento vascular bilateral",
          "Atrofia lobar com envolvimento contralateral",
        ],
      },
    },
  },

  tratamento: {
    resseccaoiCCA: {
      indicacao: "Tratamento potencialmente curativo",
      objetivo: "Ressecção R0 com margens negativas",
      linfadenectomia: "Regional recomendada (≥6 linfonodos)",
      sobrevida5anos: "25-40% se R0",
      adjuvancia: {
        indicacao: "Todos pacientes após ressecção",
        regime:
          "Capecitabina 1250 mg/m² 2x/dia D1-14 a cada 21 dias por 8 ciclos",
        base: "Estudo BILCAP",
      },
    },
    resseccaopCCA: {
      cirurgia: "Hepatectomia + ressecção de via biliar + linfadenectomia",
      reconstrucao: "Hepaticojejunostomia em Y de Roux",
      sobrevida5anos: "20-40%",
    },
    resseccaodCCA: {
      cirurgia: "Duodenopancreatectomia (Whipple)",
      sobrevida5anos: "25-50%",
    },
    transplantepCCA: {
      protocolo: "Protocolo Mayo (neoadjuvância + Tx)",
      indicacao:
        "pCCA irressecável, ≤3 cm, sem metástases, em centros selecionados",
      componentes: [
        "Radioterapia externa + braquiterapia",
        "Quimioterapia com capecitabina",
        "Laparoscopia de estadiamento",
        "Transplante se sem progressão",
      ],
      sobrevida5anos: "65-70% em pacientes selecionados",
      nota: "NÃO indicado para iCCA ou dCCA",
    },
    sistemicoPrimeiraLinha: {
      padrao: {
        regime: "Gemcitabina + Cisplatina + Durvalumab",
        base: "TOPAZ-1 trial",
        sobrevida: "OS mediana ~12 meses",
      },
      alternativa: {
        regime: "Gemcitabina + Cisplatina",
        base: "ABC-02 trial",
        uso: "Se contraindicação a imunoterapia",
      },
      outraOpcao: "FOLFOX (se contraindicação a platina)",
    },
    sistemicoSegundaLinha: {
      geral: "FOLFOX (se não usado em 1ª linha)",
      alvosEspecificos: {
        FGFR2_fusion: "Pemigatinib, Futibatinib",
        IDH1_mutation: "Ivosidenib",
        MSI_H: "Pembrolizumab",
        HER2_amplification: "T-DXd (em estudo)",
        BRAF_V600E: "Dabrafenib + Trametinib",
      },
    },
    locoregional: {
      TACE: "Pode ser considerada para iCCA irressecável",
      radioembolizacao: "Y90 - opção para iCCA, dados limitados",
      ablacao: "Para lesões pequenas (<3 cm) em cirróticos",
    },
    paliativo: {
      drenagem: {
        indicacao: "Icterícia obstrutiva",
        metodo: "CPRE com stent (metal preferido para paliação)",
        alternativa: "Drenagem percutânea se falha endoscópica",
      },
    },
  },

  vigilanciaEmPSC: {
    descricao: "Pacientes com PSC têm risco aumentado de CCA",
    protocolo: {
      frequencia: "Anual",
      exames: ["ColangioRM ou US", "CA 19-9"],
      indicacaoInvestigacao: [
        "Elevação de CA 19-9 > 129 U/mL",
        "Nova estenose dominante",
        "Perda de peso inexplicada",
        "Piora de colestase",
      ],
    },
    estenose: {
      avaliacao: "CPRE + escovado com citologia ± FISH",
      colangioscopia: "SpyGlass para visualização direta e biópsia",
    },
  },

  prognostico: {
    fatoresFavoraveis: [
      "Ressecção R0",
      "Ausência de invasão linfonodal",
      "Tumor único",
      "CA 19-9 < 200 U/mL",
      "Presença de alvos moleculares (FGFR2, IDH1)",
    ],
    fatoresDesfavoraveis: [
      "Metástases",
      "Invasão vascular",
      "Linfonodos positivos",
      "Ressecção R1/R2",
      "CA 19-9 muito elevado",
    ],
  },

  fonte: "EASL-ILCA CPG iCCA 2023; EASL CPG eCCA 2025; AASLD PSC Guidance 2023",
};

// =============================================================================
// TRANSPLANTE HEPÁTICO - ATUALIZAÇÕES (EASL 2024 / AASLD 2025)
// =============================================================================

export const TRANSPLANTE_GUIDELINES_UPDATE = {
  descricao: "Atualizações em Transplante Hepático - EASL 2024 / AASLD 2025",

  avaliacaoIdoso: {
    descricao: "Avaliação de candidatos idosos (>65 anos)",
    principios: {
      idadeCronologica: "Não é contraindicação absoluta",
      idadeBiologica: "Foco na avaliação funcional",
    },
    avaliacaoObrigatoria: [
      "Reserva cardiopulmonar completa",
      "Avaliação de fragilidade (Liver Frailty Index)",
      "Avaliação nutricional e sarcopenia",
      "Screening de neoplasias ocultas",
      "Avaliação cognitiva",
      "Fatores anatômicos e cirúrgicos",
    ],
    consideracoes: {
      "65-70 anos": "Avaliação padrão com atenção a comorbidades",
      ">70 anos": "Avaliação rigorosa; resultados aceitáveis em selecionados",
      ">75 anos": "Risco de comprometimento cognitivo precoce pós-Tx",
    },
    fonte: "EASL CPG LT 2024",
  },

  fragilidadeSarcopenia: {
    descricao: "Avaliação e manejo de fragilidade/sarcopenia pré-transplante",
    liverFrailtyIndex: {
      componentes: [
        "Hand grip strength",
        "Chair stands (tempo)",
        "Balance test",
      ],
      interpretacao: {
        "<3.2": "Robusto",
        "3.2-4.4": "Pré-frágil",
        ">4.4": "Frágil",
      },
      impacto: "Fragilidade associada a maior mortalidade em lista e pós-Tx",
    },
    sarcopenia: {
      avaliacao: {
        TC: "Área muscular em L3 (SMI - Skeletal Muscle Index)",
        cortesHomens: "SMI < 50 cm²/m²",
        cortesMulheres: "SMI < 39 cm²/m²",
      },
      impacto: "Aumenta mortalidade, tempo de internação e infecções pós-Tx",
    },
    intervencoes: {
      nutricao: {
        proteinas: "1.2-1.5 g/kg/dia",
        calorias: "35 kcal/kg/dia",
        refeicoes: "4-6 refeições/dia incluindo lanche noturno",
        BCAA: "Considerar suplementação",
      },
      exercicio: {
        resistido: "2-3x/semana",
        aerobico: "150 min/semana se tolerado",
        supervisionado: "Preferível em programa de reabilitação",
      },
    },
    fonte: "AASLD Nutrition Guidance 2021; EASL CPG LT 2024",
  },

  avaliacaoCardiovascular: {
    descricao: "Avaliação cardiovascular pré-transplante",
    todosCandidatos: [
      "ECG",
      "Ecocardiograma transtorácico",
      "Avaliação de fatores de risco (HAS, DM, tabagismo, dislipidemia)",
    ],
    estratificacaoRisco: {
      baixo: {
        criterios: "Sem fatores de risco, assintomático",
        conduta: "Ecocardiograma suficiente",
      },
      intermediario: {
        criterios: "≥3 fatores de risco ou idade >60 com fatores",
        conduta: "Considerar teste funcional (eco de estresse, cintilografia)",
      },
      alto: {
        criterios: "DAC conhecida, sintomas, DM de longa data",
        conduta: "Avaliação invasiva (cateterismo) ou AngioTC coronariana",
      },
    },
    hipertensaoPortopulmonar: {
      screening: "Ecocardiograma em todos - estimar PSAP",
      suspeita: "PSAP > 50 mmHg no eco",
      confirmacao: "Cateterismo cardíaco direito",
      criteriosDiagnostico: {
        mPAP: "> 25 mmHg (ou >20 em critérios novos)",
        PCP: "< 15 mmHg",
        RVP: "> 3 Wood units",
      },
      manejo: {
        leve: "mPAP 25-35 - Tx possível, monitorar",
        moderada: "mPAP 35-45 - Tratamento com vasodilatadores, reavaliar",
        grave: "mPAP > 45 ou RVP > 5 - Contraindicação relativa/absoluta",
      },
    },
    sindromeHepatopulmonar: {
      triade: [
        "Doença hepática",
        "Hipoxemia (PaO2 < 80 ou AaO2 > 15)",
        "Shunt intrapulmonar",
      ],
      diagnostico:
        "Eco com contraste (bolhas) ou cintilografia com macroagregados",
      classificacao: {
        leve: "PaO2 ≥ 80 mmHg",
        moderada: "PaO2 60-79 mmHg",
        grave: "PaO2 50-59 mmHg",
        muitoGrave: "PaO2 < 50 mmHg",
      },
      tratamento: "Transplante (único tratamento efetivo)",
      excecaoMELD: "Pontos adicionais de exceção disponíveis",
    },
    fonte: "EASL CPG LT 2024; AASLD/AST Guideline 2025",
  },

  machinePerfusion: {
    descricao: "Perfusão ex-vivo de enxertos hepáticos",
    tipos: {
      HOPE: {
        nome: "Hypothermic Oxygenated Perfusion",
        temperatura: "4-12°C",
        vantagens: [
          "Reduz lesão de isquemia-reperfusão",
          "Melhora função do enxerto",
          "Permite avaliação de viabilidade",
        ],
        indicacoes: [
          "Doadores com critérios expandidos",
          "DCD (Donation after Circulatory Death)",
          "Tempos de isquemia prolongados",
        ],
      },
      NMP: {
        nome: "Normothermic Machine Perfusion",
        temperatura: "35-38°C",
        vantagens: [
          "Avaliação funcional do enxerto (produção de bile, lactato)",
          "Permite intervenções terapêuticas",
          "Pode recuperar enxertos marginais",
        ],
        criteriosViabilidade: [
          "Produção de bile",
          "Clearance de lactato",
          "Perfusato estável",
          "Homogeneidade de perfusão",
        ],
      },
    },
    resultados: "Redução de complicações biliares e disfunção primária em DCD",
    disponibilidade: "Cada vez mais utilizada em centros de referência",
    fonte: "EASL CPG LT 2024",
  },

  doadoresDCD: {
    descricao: "Donation after Circulatory Death",
    tipos: {
      controlado:
        "Morte esperada, retirada de suporte planejada (Maastricht III)",
      naoControlado: "Morte inesperada, parada cardíaca (Maastricht I-II)",
    },
    resultados: {
      sobrevida: "Similar a DBD em centros experientes",
      complicacoes: "Maior risco de colangiopatia isquêmica (5-15%)",
    },
    mitigacao: [
      "Seleção cuidadosa de doadores",
      "Tempos de isquemia quente curtos (<30 min)",
      "Uso de machine perfusion (HOPE ou NMP)",
      "NRP (Normothermic Regional Perfusion) em alguns centros",
    ],
    fonte: "EASL CPG LT 2024",
  },

  doadoresHCVpositivo: {
    descricao: "Uso de enxertos de doadores HCV NAT+ para receptores HCV-",
    contexto: "Expandido com advento dos DAAs",
    protocolo: {
      preTransplante: "Receptor informado e consente",
      posTransplante: "Iniciar DAA precocemente (dentro de 1-7 dias)",
      regime: "SOF/VEL ou GLE/PIB por 12 semanas",
    },
    resultados: {
      taxaSVR: ">95%",
      sobrevida: "Similar a receptores de doadores HCV-",
    },
    beneficio: "Aumenta pool de doadores; reduz tempo em lista",
    fonte: "AASLD HCV Guidance 2023; AASLD/AST Guideline 2025",
  },

  LDLT: {
    descricao: "Living Donor Liver Transplantation",
    vantagens: [
      "Reduz tempo em lista",
      "Cirurgia eletiva, otimização pré-operatória",
      "Menor tempo de isquemia fria",
      "Resultados de longo prazo superiores em alguns estudos",
    ],
    avaliacaoDoador: {
      obrigatorio: [
        "Idade 18-55 anos (ideal)",
        "IMC < 30 (ideal < 28)",
        "Sem comorbidades significativas",
        "Compatibilidade ABO",
        "Avaliação hepática completa (anatomia, volume, função)",
        "Avaliação psicossocial",
      ],
      volumetria: {
        remanescente: "> 30-35% do volume hepático total",
        enxerto: "> 0.8% do peso do receptor (GRWR)",
      },
    },
    riscoDoador: {
      mortalidade: "0.1-0.5% (lobo direito > lobo esquerdo)",
      complicacoes: "10-30% (maioria grau I-II Clavien-Dindo)",
    },
    fonte: "EASL CPG LT 2024; AASLD/AST Guideline 2025",
  },

  ACLFeTransplante: {
    descricao: "Transplante em Acute-on-Chronic Liver Failure",
    desafios: [
      "Alta mortalidade em lista",
      "Janela terapêutica curta",
      "Complicações pós-operatórias aumentadas",
    ],
    selecaoCandidatos: {
      fatoresFavoraveis: [
        "Estabilização ou melhora de falências orgânicas",
        "ACLF-1 ou ACLF-2 estável",
        "Lactato em queda",
        "Suporte vasopressor estável ou em desmame",
      ],
      contraindicacoesRelativas: [
        "ACLF-3 com múltiplas falências refratárias",
        "Lactato > 9 mmol/L persistente",
        "Falência respiratória com VM e FiO2 alta",
        "Necessidade crescente de vasopressores",
      ],
    },
    resultados: {
      comACLF: "Sobrevida 1 ano: 80-85% (em selecionados)",
      semACLF: "Sobrevida 1 ano: 90-92%",
    },
    nota: "Decisão deve ser individualizada; equipe multidisciplinar",
    fonte: "AASLD ACLF Guidance 2024; EASL CPG LT 2024",
  },

  rejeicao: {
    celularAguda: {
      descricao: "Acute Cellular Rejection (ACR)",
      diagnostico: "Biópsia hepática - critérios de Banff",
      tratamento: {
        leve: "Otimizar imunossupressão basal",
        moderadaGrave: "Metilprednisolona 500-1000mg IV x 3 dias",
        corticoRefrataria: "Timoglobulina (ATG)",
      },
    },
    mediadaPorAnticorpos: {
      descricao: "Antibody-Mediated Rejection (AMR)",
      mecanismo: "DSA (Donor-Specific Antibodies) contra HLA do doador",
      diagnostico: {
        clinico: "Disfunção do enxerto",
        laboratorial: "DSA positivo",
        histologico: "C4d positivo, injúria microvascular",
      },
      tratamento: {
        primeiraLinha: ["Plasmaférese", "IVIG", "Rituximab"],
        refratario: ["Bortezomib", "Eculizumab", "Retransplante"],
      },
      nota: "Entidade cada vez mais reconhecida; tratamento ainda não padronizado",
    },
  },

  recorrenciaDoencas: {
    HCV: {
      prevencao: "DAA pré ou peri-transplante",
      posTransplante: "Tratar com DAA se recorrência (raro atualmente)",
    },
    HBV: {
      profilaxia: "HBIG + análogo de nucleosídeo (Entecavir ou TAF)",
      duracao:
        "Indefinida para análogo; HBIG pode ser descontinuada em casos selecionados",
    },
    ALD: {
      risco: "Recaída alcoólica em 15-30%",
      prevencao: "Avaliação psicossocial rigorosa, acompanhamento pós-Tx",
      impacto: "Recaída pesada associada a pior sobrevida do enxerto",
    },
    MASLD: {
      recorrencia: "Comum (60-100% desenvolvem esteatose)",
      cirrose: "10-15% desenvolvem cirrose em 5-10 anos",
      manejo:
        "Controle de fatores metabólicos, perda de peso, semaglutide em estudo",
    },
    AIH: {
      recorrencia: "20-40%",
      manejo:
        "Manter imunossupressão adequada; alguns centros usam corticoide baixa dose prolongado",
    },
    PBC: {
      recorrencia: "20-40% em 10 anos",
      manejo: "UDCA pós-Tx pode reduzir recorrência",
    },
    PSC: {
      recorrencia: "20-25%",
      nota: "Diagnóstico difícil (diferencial com complicações biliares)",
    },
  },

  imunossupressao: {
    inducao: {
      basiliximab: {
        indicacao: "Padrão em muitos centros; reduz rejeição precoce",
        dose: "20 mg IV D0 e D4",
      },
      timoglobulina: {
        indicacao: "Alto risco imunológico, retransplante",
        dose: "1.5 mg/kg/dia x 3-5 dias",
      },
    },
    manutencao: {
      tacrolimus: {
        padrao: "CNI de escolha na maioria dos centros",
        niveisAlvo: {
          "0-3 meses": "8-12 ng/mL",
          "3-6 meses": "6-10 ng/mL",
          "6-12 meses": "5-8 ng/mL",
          ">12 meses": "4-7 ng/mL (individualizar)",
        },
      },
      ciclosporina: "Alternativa; menos usado atualmente",
      micofenolato: {
        indicacao: "Associado ao CNI para permitir doses menores",
        dose: "500-1000 mg 2x/dia",
      },
      mTORi: {
        indicacao: "CHC, nefrotoxicidade por CNI, neoplasias de novo",
        opcoes: "Sirolimus, Everolimus",
      },
    },
    minimizacao: {
      objetivo:
        "Reduzir efeitos adversos de longo prazo (nefrotoxicidade, infecções, neoplasias)",
      estrategias: [
        "Redução gradual de CNI após 3-6 meses",
        "Conversão para mTORi em casos selecionados",
        "Monoterapia com tacrolimus baixa dose em estáveis de longo prazo",
      ],
    },
  },

  fonte:
    "EASL CPG Liver Transplantation 2024; AASLD/AST Guideline Adult LT 2025",
};

// =============================================================================
// DOENÇAS COLESTÁTICAS GENÉTICAS (EASL 2024)
// =============================================================================

export const DOENCAS_COLESTATICAS_GENETICAS = {
  descricao: "Doenças Colestáticas Genéticas - Diagnóstico e Tratamento",

  visaoGeral: {
    definicao:
      "Grupo de doenças monogênicas que causam colestase por defeitos na formação ou fluxo de bile",
    apresentacao:
      "Maioria na infância; formas mais leves podem apresentar na idade adulta",
    caracteristicaComum: "Prurido colestático frequentemente debilitante",
  },

  deficienciaAlfa1Antitripsina: {
    sigla: "AATD",
    gene: "SERPINA1",
    descricao:
      "Deficiência de alfa-1 antitripsina - doença hepática e pulmonar",

    genetica: {
      heranca: "Autossômica codominante",
      alelosComuns: {
        M: "Normal (Pi*M)",
        Z: "Mutação mais comum e grave (Pi*Z) - Glu342Lys",
        S: "Mutação com redução moderada (Pi*S)",
      },
      genotipos: {
        "Pi*MM": { nivel: "100%", riscoHepatico: "Basal (1x)" },
        "Pi*MZ": { nivel: "~60%", riscoHepatico: "Leve aumento (1.7x)" },
        "Pi*SZ": { nivel: "~40%", riscoHepatico: "Moderado (3x)" },
        "Pi*ZZ": { nivel: "~15%", riscoHepatico: "Alto (20x para cirrose)" },
      },
      frequencia: "Pi*ZZ: 1:2000-3000 em caucasianos",
    },

    manifestacoesHepaticas: {
      pediatrico: [
        "Colestase neonatal (2-10%)",
        "Hepatite neonatal",
        "Cirrose na infância (2-3%)",
      ],
      adulto: [
        "Elevação assintomática de transaminases",
        "Hepatite crônica",
        "Cirrose (20-35% dos Pi*ZZ)",
        "Hepatocarcinoma (risco 7-45x em Pi*ZZ)",
      ],
      notaImportante: "Maioria dos Pi*ZZ permanece assintomática hepaticamente",
    },

    manifestacoesPulmonares: {
      principal: "Enfisema panacinar de início precoce",
      idade: "Geralmente 30-50 anos",
      fatorAgravante: "Tabagismo acelera drasticamente a progressão",
      nota: "Manifestação pulmonar mais comum que hepática em adultos",
    },

    diagnostico: {
      screening: {
        indicacoes: [
          "Doença hepática inexplicada",
          "DPOC/enfisema de início precoce (<45 anos)",
          "Bronquiectasias inexplicadas",
          "História familiar de AATD",
          "Panniculite necrotizante",
          "Vasculite ANCA-positiva",
        ],
      },
      exames: {
        nivelSerico: {
          metodo: "Nefelometria ou turbidimetria",
          corte: "< 110 mg/dL sugere deficiência",
          limitacao: "AAT é proteína de fase aguda - pode elevar em inflamação",
        },
        fenotipagem: {
          metodo: "Focalização isoelétrica",
          utilidade: "Identifica variantes proteicas",
        },
        genotipagem: {
          metodo: "PCR para mutações específicas ou sequenciamento",
          utilidade: "Confirma diagnóstico; identifica variantes raras",
        },
      },
      algoritmo: "Nível sérico baixo → Genotipagem/Fenotipagem → Confirmação",
    },

    avaliacaoHepatica: {
      exames: [
        "TGO, TGP, GGT, FA, bilirrubinas",
        "Albumina, INR",
        "Hemograma com plaquetas",
      ],
      elastografia: {
        utilidade: "Estimar fibrose em adultos Pi*ZZ",
        cortes: "Aplicar cortes de doença hepática crônica",
      },
      biopsia: "Considerar se avaliação não-invasiva inconclusiva",
      histologia: "Glóbulos PAS-positivos, diastase-resistentes em hepatócitos",
    },

    tratamento: {
      medidas: {
        cessacaoTabagismo: "FUNDAMENTAL - principal intervenção modificável",
        evitarAlcool: "Reduz risco de progressão hepática",
        vacinacao: "Hepatite A, B, pneumococo, influenza",
      },
      terapiaReposicao: {
        indicacao:
          "Doença pulmonar (enfisema) - NÃO indicada para doença hepática",
        mecanismo: "Reposição de AAT por via IV",
        nota: "Não reverte doença hepática",
      },
      novasTerapias: {
        fazirsiran: {
          mecanismo: "siRNA que reduz produção hepática de AAT mutante",
          status: "Fase 2 - resultados promissores",
          efeito: "Reduz AAT-Z hepático e sérico; melhora enzimas",
        },
      },
      transplante: {
        indicacao: "Cirrose descompensada ou CHC",
        resultado: "Excelente; cura o defeito hepático",
        doadoresHeterozigotos:
          "Pi*MZ pode ser usado com cautela se sem alternativa",
        contraindicacao: "Doadores Pi*ZZ NÃO devem ser usados",
      },
    },

    vigilanciaCHC: {
      indicacao: "Pacientes com cirrose estabelecida",
      protocolo: "US + AFP cada 6 meses",
    },

    screeningFamiliar: {
      recomendado: "Todos parentes de primeiro grau de Pi*ZZ",
      exames: "Nível sérico de AAT + genotipagem",
    },

    fonte: "EASL CPG Genetic Cholestatic Diseases 2024",
  },

  sindromeAlagille: {
    sigla: "ALGS",
    genes: "JAG1 (94%), NOTCH2 (2-4%)",
    heranca: "Autossômica dominante com penetrância variável",

    descricao:
      "Síndrome multissistêmica com colestase, cardiopatia e dismorfismos",

    criteriosDiagnosticos: {
      classicos: {
        hepatico:
          "Escassez de ductos biliares interlobulares (bile duct paucity)",
        cardiaco:
          "Cardiopatia congênita (estenose pulmonar periférica mais comum)",
        esqueletico: "Vértebras em borboleta",
        ocular: "Embriotoxon posterior",
        facial: "Face triangular, hipertelorismo, nariz em sela",
      },
      diagnostico:
        "≥3 critérios clássicos OU mutação JAG1/NOTCH2 + ≥1 critério",
    },

    manifestacoesHepaticas: {
      colestase: "Presente em >90% na infância; pode melhorar",
      prurido: "Frequentemente severo e debilitante",
      cirrose: "15-30% desenvolvem cirrose",
      transplante: "Necessário em 20-30%",
      CHC: "Risco aumentado - vigilância se cirrose",
    },

    manifestacoesExtraHepaticas: {
      cardiacas: {
        frequencia: ">90%",
        tipos: [
          "Estenose pulmonar periférica",
          "Tetralogia de Fallot",
          "CIV",
          "CIA",
        ],
      },
      vasculares: {
        frequencia: "~15%",
        tipos: [
          "Aneurismas cerebrais",
          "Doença de Moyamoya",
          "Estenose de artéria renal",
        ],
        nota: "Principal causa de mortalidade não-hepática",
      },
      renais: "Displasia renal (30-40%)",
      oculares: "Embriotoxon posterior (80-90%)",
      esqueleticas: "Vértebras em borboleta (50-90%)",
    },

    tratamento: {
      prurido: {
        primeiraLinha: "Inibidores IBAT (Maralixibat - APROVADO)",
        maralixibat: {
          mecanismo: "Inibe transportador ileal de ácidos biliares",
          dose: "380 mcg/kg 1x/dia (até 28.5 mg)",
          eficacia: "Redução significativa de prurido e ácidos biliares",
          base: "Estudo ICONIC",
        },
        segundaLinha: ["Rifampicina", "Colestiramina", "Naltrexona"],
        refratario: [
          "Derivação biliar parcial",
          "Exclusão ileal",
          "Transplante",
        ],
      },
      suporteNutricional: {
        vitaminasLipossoluveis: "A, D, E, K - suplementar",
        formulasMCT: "Se má absorção de gorduras",
      },
      estatinas: "NÃO recomendadas para hipercolesterolemia da ALGS",
      transplante: {
        indicacoes: [
          "Cirrose descompensada",
          "Prurido intratável",
          "Falha de crescimento refratária",
        ],
        consideracoes: "Avaliar doença vascular e cardíaca antes",
      },
    },

    prognostico: {
      sobrevida20anos: "~80% com fígado nativo",
      fatoresPreditivos: [
        "Bilirrubina total aos 5 anos",
        "Presença de fibrose portal na biópsia",
        "Colesterol total",
      ],
    },

    fonte: "EASL CPG Genetic Cholestatic Diseases 2024",
  },

  PFIC: {
    descricao: "Progressive Familial Intrahepatic Cholestasis",
    caracteristicaComum:
      "Colestase progressiva com GGT normal ou baixa (PFIC 1 e 2) ou elevada (PFIC 3)",

    PFIC1: {
      gene: "ATP8B1 (FIC1)",
      nome: "Deficiência de FIC1 (Doença de Byler)",
      GGT: "Normal ou baixo",

      caracteristicas: {
        hepaticas: "Colestase, cirrose",
        extraHepaticas: [
          "Diarreia crônica",
          "Pancreatite",
          "Baixa estatura",
          "Surdez neurossensorial",
          "Pneumonia recorrente",
        ],
      },

      tratamento: {
        medico: [
          "UDCA (benefício limitado)",
          "Inibidores IBAT (Odevixibat - aprovado)",
        ],
        cirurgico: {
          derivacaoBiliar:
            "Pode melhorar prurido; efeito variável na progressão",
          transplante:
            "Cura doença hepática MAS manifestações extra-hepáticas persistem/pioram",
        },
      },

      posTransplante: {
        diarreia: "Frequentemente piora após Tx",
        esteatoseEnxerto: "Comum",
      },
    },

    PFIC2: {
      gene: "ABCB11 (BSEP)",
      nome: "Deficiência de BSEP",
      GGT: "Normal ou baixo",

      caracteristicas: {
        hepaticas: [
          "Colestase severa de início neonatal",
          "Progressão rápida para cirrose",
          "Alto risco de CHC (até 15% - mesmo em crianças pequenas)",
          "Colelitíase",
        ],
        extraHepaticas: "Raras (diferente de PFIC1)",
      },

      subtipos: {
        BSEP1: "Formas graves com mutações truncantes",
        BSEP2: "Formas com alguma função residual (mutações missense)",
      },

      tratamento: {
        medico: [
          "UDCA (pode ajudar em formas leves)",
          "4-fenilbutirato (chaperona - em estudo)",
          "Inibidores IBAT (Odevixibat)",
        ],
        cirurgico: {
          derivacaoBiliar: "Efetiva especialmente em BSEP2",
          transplante: "Frequentemente necessário; excelente resultado",
        },
      },

      posTransplante: {
        aloimunizacao: {
          descricao: "Anticorpos anti-BSEP contra o enxerto",
          frequencia: "Em pacientes com mutações truncantes",
          apresentacao: "Recorrência de colestase com GGT baixo",
          tratamento: [
            "Intensificar imunossupressão",
            "Rituximab",
            "Plasmaférese",
          ],
        },
      },

      vigilanciaCHC: "A partir do diagnóstico - US cada 3-6 meses",
    },

    PFIC3: {
      gene: "ABCB4 (MDR3)",
      nome: "Deficiência de MDR3",
      GGT: "ELEVADO (diferencial importante)",

      caracteristicas: {
        hepaticas: [
          "Colestase (pode ser mais tardia que PFIC1/2)",
          "Colelitíase de repetição",
          "Cirrose biliar",
          "Colangiocarcinoma (risco aumentado)",
        ],
      },

      espectroClinico: {
        severo: "PFIC3 clássico - cirrose na infância",
        moderado: "Colestase crônica, cirrose em adulto jovem",
        leve: [
          "LPAC (Low Phospholipid-Associated Cholelithiasis)",
          "Colestase intra-hepática da gravidez (ICP)",
          "Colestase induzida por drogas (DILI)",
          "Elevação isolada de GGT",
        ],
      },

      LPAC: {
        descricao: "Colelitíase de repetição em jovens (<40 anos)",
        caracteristicas: [
          "Cálculos intrahepáticos",
          "Recorrência após colecistectomia",
          "Sintomas biliares de repetição",
        ],
        tratamento: "UDCA indefinidamente",
      },

      tratamento: {
        UDCA: {
          indicacao: "Todos pacientes sintomáticos",
          dose: "15-20 mg/kg/dia",
          resposta:
            "Melhor em pacientes com mutações missense (alguma função residual)",
        },
        transplante: "Para cirrose descompensada",
        gravidez: "UDCA seguro a partir do 2º trimestre",
      },

      vigilancia: {
        CHC: "Se cirrose - US cada 6 meses",
        CCA: "Risco aumentado - manter vigilância",
      },
    },

    outrosTipos: {
      PFIC4: { gene: "TJP2", caracteristica: "Tight junction protein 2" },
      PFIC5: { gene: "NR1H4 (FXR)", caracteristica: "Receptor farnesoide X" },
      PFIC6: {
        gene: "MYO5B",
        caracteristica: "Associado a doença de inclusão microvilositária",
      },
      nota: "Tipos raros - manejo similar com UDCA, inibidores IBAT e transplante se refratário",
    },

    fonte: "EASL CPG Genetic Cholestatic Diseases 2024",
  },

  defeitosSinteseBiliar: {
    sigla: "BASD (Bile Acid Synthesis Defects)",

    descricao: "Defeitos enzimáticos na síntese de ácidos biliares primários",

    tipos: {
      "3beta-HSD": "Deficiência de 3β-hidroxi-Δ5-C27-esteroide desidrogenase",
      "Delta4-3-oxoR": "Deficiência de Δ4-3-oxosteroide 5β-redutase",
      CTX: "Xantomatose cerebrotendinosa (CYP27A1)",
      outros: "Múltiplos outros defeitos raros",
    },

    caracteristicas: {
      hepaticas: ["Colestase neonatal", "Hepatite", "Cirrose"],
      extraHepaticas: {
        neurologicas: "Em alguns tipos (ex: CTX)",
        vitaminaD: "Raquitismo em alguns tipos",
      },
    },

    diagnostico: {
      suspeitaClinica:
        "Colestase com GGT normal e ácidos biliares séricos normais/baixos",
      confirmacao:
        "Análise de ácidos biliares na urina por espectrometria de massa",
      genetico: "Sequenciamento dos genes específicos",
    },

    tratamento: {
      acidoColicoOral: {
        indicacao: "Tratamento de escolha para maioria dos BASD",
        dose: "10-15 mg/kg/dia",
        mecanismo:
          "Fornece ácido biliar primário; suprime síntese de intermediários tóxicos",
        eficacia:
          "Pode normalizar função hepática e reverter fibrose se iniciado precocemente",
      },
      CDCA: "Para CTX especificamente",
    },

    prognostico: "Excelente se tratamento iniciado precocemente",

    fonte: "EASL CPG Genetic Cholestatic Diseases 2024",
  },

  manejoGeraldoPrurido: {
    descricao: "Abordagem escalonada para prurido colestático",

    escala: {
      nivel1: {
        opcoes: [
          "UDCA (se não em uso)",
          "Emolientes",
          "Anti-histamínicos (efeito limitado)",
        ],
      },
      nivel2: {
        opcoes: [
          {
            nome: "Colestiramina",
            dose: "4-16 g/dia dividido",
            nota: "Separar 4h de outras medicações",
          },
          {
            nome: "Rifampicina",
            dose: "150-600 mg/dia",
            monitoramento: "Função hepática - risco de hepatotoxicidade",
          },
        ],
      },
      nivel3: {
        opcoes: [
          {
            nome: "Naltrexona",
            dose: "25-50 mg/dia",
            nota: "Iniciar com dose baixa; pode precipitar abstinência opioide-like",
          },
          {
            nome: "Sertralina",
            dose: "75-100 mg/dia",
          },
        ],
      },
      nivel4: {
        opcoes: [
          "Inibidores IBAT (Maralixibat, Odevixibat)",
          "Derivação biliar (parcial interna ou externa)",
          "Exclusão ileal",
          "Plasmaférese/MARS (temporário)",
        ],
      },
      nivel5: "Transplante hepático (prurido intratável)",
    },

    inibidoresIBAT: {
      mecanismo:
        "Bloqueiam reabsorção ileal de ácidos biliares → redução do pool",
      medicamentos: {
        maralixibat: {
          aprovacao: "Alagille syndrome",
          status: "Aprovado FDA/EMA",
        },
        odevixibat: {
          aprovacao: "PFIC",
          status: "Aprovado FDA/EMA",
          base: "Estudo PEDFIC",
        },
      },
      efeitosAdversos: [
        "Diarreia (dose-dependente)",
        "Dor abdominal",
        "Deficiência de vitaminas lipossolúveis",
      ],
      monitoramento: "Vitaminas A, D, E (INR para K) periodicamente",
    },

    fonte: "EASL CPG Genetic Cholestatic Diseases 2024",
  },

  testeGenetico: {
    quando: [
      "Colestase inexplicada (especialmente GGT baixo)",
      "História familiar de doença hepática colestática",
      "Colelitíase de repetição em jovem",
      "Colestase da gravidez recorrente/grave",
      "Suspeita de síndrome específica",
    ],
    metodos: {
      painelDeGenes: "Painel de genes de colestase (mais custo-efetivo)",
      WES: "Whole Exome Sequencing - se painel negativo",
      WGS: "Whole Genome Sequencing - casos complexos",
    },
    interpretacao: {
      VUS: "Variantes de significado incerto - correlacionar com clínica",
      aconselhamentoGenetico: "Recomendado para todos com resultado positivo",
    },
  },

  fonte: "EASL CPG Genetic Cholestatic Liver Diseases 2024",
};

// =============================================================================
// HEPATITE B CRÔNICA - ATUALIZAÇÃO (AASLD 2025)
// =============================================================================

export const HBV_GUIDELINES_2025 = {
  descricao: "Hepatite B Crônica - Guidance Atualizado AASLD 2025",

  fasesInfeccao: {
    descricao: "Fases da infecção crônica pelo HBV (nomenclatura atualizada)",
    fases: {
      infeccaoCronicaHBeAgPositivo: {
        nomeAntigo: "Imunotolerante",
        HBeAg: "Positivo",
        HBVDNA: "Muito alto (>10⁷ UI/mL)",
        ALT: "Normal",
        histologia: "Mínima inflamação/fibrose",
        tratamento: "Geralmente não indicado (controverso em alguns)",
      },
      hepatiteHBeAgPositivo: {
        nomeAntigo: "Imunoativo HBeAg+",
        HBeAg: "Positivo",
        HBVDNA: "Alto (>20.000 UI/mL)",
        ALT: "Elevada",
        histologia: "Inflamação ativa ± fibrose",
        tratamento: "INDICADO",
      },
      infeccaoCronicaHBeAgNegativo: {
        nomeAntigo: "Portador inativo",
        HBeAg: "Negativo",
        antiHBe: "Positivo",
        HBVDNA: "Baixo (<2.000 UI/mL)",
        ALT: "Normal",
        histologia: "Mínima",
        tratamento: "Geralmente não indicado; monitorar",
      },
      hepatiteHBeAgNegativo: {
        nomeAntigo: "Imunoativo HBeAg-",
        HBeAg: "Negativo",
        HBVDNA: "Moderado-alto (>2.000 UI/mL)",
        ALT: "Elevada (intermitente ou persistente)",
        histologia: "Inflamação ativa ± fibrose",
        tratamento: "INDICADO",
      },
    },
  },

  indicacoesTratamento: {
    descricao: "Indicações de tratamento antiviral - SIMPLIFICADAS em 2025",

    indicacoesAbsolutas: [
      "Cirrose (qualquer nível de DNA ou ALT)",
      "Manifestações extra-hepáticas (ex: glomerulonefrite, vasculite)",
      "Coinfecção HIV, HCV ou HDV",
      "Reativação HBV",
      "Insuficiência hepática aguda",
      "Gravidez com alto risco de transmissão (3º trimestre se DNA >200.000)",
    ],

    indicacoesBaseadasEmParametros: {
      semCirrose: {
        HBeAgPositivo: {
          criterio: "ALT > 2x LSN E HBV-DNA > 20.000 UI/mL",
          alternativo: "ALT elevada persistente + fibrose significativa (≥F2)",
        },
        HBeAgNegativo: {
          criterio: "ALT > 2x LSN E HBV-DNA > 2.000 UI/mL",
          alternativo: "ALT elevada persistente + fibrose significativa (≥F2)",
        },
      },
    },

    indicacoesExpandidas2025: {
      descricao: "Novas indicações com evidência crescente",
      situacoes: [
        "HBV-DNA > 2.000 com história familiar de CHC (1º grau)",
        "Idade > 40 anos com HBV-DNA detectável + ALT no limite superior",
        "Pacientes com fibrose ≥F2 independente de ALT",
        "Manifestações extra-hepáticas mesmo sem doença hepática significativa",
      ],
    },

    naoIndicado: [
      "Infecção crônica HBeAg+ com ALT normal e sem fibrose (monitorar)",
      "Portador inativo verdadeiro (HBeAg-, DNA <2.000, ALT normal persistente)",
    ],

    nota: "Tendência a tratamento mais liberal - 'treat more' philosophy",
  },

  tratamento: {
    primeiraLinha: {
      preferidos: {
        TAF: {
          nome: "Tenofovir Alafenamide",
          dose: "25 mg/dia",
          vantagens: [
            "Menor toxicidade renal que TDF",
            "Menor impacto ósseo",
            "Preferido em >60 anos, DRC, osteoporose",
          ],
          eficacia: "Supressão viral >95% em 1 ano",
        },
        TDF: {
          nome: "Tenofovir Disoproxil Fumarate",
          dose: "300 mg/dia",
          vantagens: ["Ampla experiência", "Baixo custo", "Seguro na gravidez"],
          precaucoes: [
            "Monitorar função renal",
            "Avaliar densidade óssea em risco",
          ],
        },
        entecavir: {
          nome: "Entecavir",
          dose: "0.5 mg/dia (1 mg se resistência prévia a lamivudina)",
          vantagens: ["Excelente perfil de segurança", "Baixa resistência"],
          precaucao:
            "Não usar se exposição prévia a lamivudina (resistência cruzada)",
        },
      },
      naoRecomendados: {
        lamivudina:
          "Alta taxa de resistência (70% em 5 anos) - não usar como monoterapia",
        adefovir: "Menos potente, nefrotóxico - obsoleto",
        telbivudina: "Risco de miopatia - não disponível em muitos países",
      },
    },

    escolhaDoAgente: {
      geral: "TAF, TDF ou Entecavir são todos aceitáveis",
      preferirTAF: [
        "Idade > 60 anos",
        "DRC (TFG < 60)",
        "Osteoporose ou alto risco",
        "Uso de outros nefrotóxicos",
      ],
      preferirTDF: [
        "Gestação (dados de segurança mais robustos)",
        "Coinfecção HIV (se já em uso para HIV)",
        "Custo (genérico disponível)",
      ],
      preferirEntecavir: [
        "Sem exposição prévia a lamivudina",
        "Intolerância a tenofovir",
      ],
    },

    duracaoTratamento: {
      cirrose: "Indefinido (não suspender)",
      HBeAgPositivo: {
        meta: "Soroconversão HBeAg → anti-HBe",
        posSoroconversao:
          "Consolidação por 12 meses, depois pode tentar suspender",
        monitoramento: "Risco de reativação - acompanhar de perto",
      },
      HBeAgNegativo: {
        meta: "Perda de HBsAg (rara - <5%)",
        recomendacao: "Tratamento indefinido na maioria",
        suspensao: "Apenas se perda de HBsAg + 6-12 meses de consolidação",
      },
    },
  },

  curafuncional: {
    definicao:
      "Perda sustentada de HBsAg com ou sem soroconversão para anti-HBs",
    taxas: {
      analogos: "1-3% em 5-10 anos",
      pegIFN: "3-7% (maior em genótipo A)",
    },
    estrategiasEmEstudo: [
      "Combinação de agentes (análogo + imunomodulador)",
      "siRNA (ex: Bepirovirsen)",
      "Inibidores de entrada viral",
      "Vacinas terapêuticas",
      "Inibidores de HBsAg",
    ],
    nota: "Cura funcional é meta ideal mas raramente alcançada com terapias atuais",
  },

  vigilanciaCHC: {
    indicados: [
      "Cirrose (qualquer HBV-DNA)",
      "Asiáticos: homens >40, mulheres >50",
      "Africanos/afro-americanos >20 anos",
      "História familiar de CHC em 1º grau",
      "Coinfecção HDV",
      "Coinfecção HCV",
    ],
    protocolo: "US abdominal ± AFP a cada 6 meses",
    emTratamento:
      "Manter vigilância mesmo com supressão viral (risco reduzido mas não zero)",
  },

  situacoesEspeciais: {
    gravidez: {
      screening: "HBsAg em todas gestantes",
      tratamento: {
        indicacao: "HBV-DNA > 200.000 UI/mL no 3º trimestre",
        objetivo: "Reduzir transmissão vertical",
        agente: "TDF preferido (maior experiência em gestação)",
        duracao: "Até 1-3 meses pós-parto; depois reavaliar",
      },
      recem_nascido: {
        obrigatorio: [
          "HBIG nas primeiras 12h",
          "1ª dose vacina HBV nas primeiras 12h",
        ],
        completarSerie: "Vacina com 1-2 e 6 meses",
        testarRN: "HBsAg e anti-HBs aos 9-12 meses",
      },
      amamentacao: "Segura se mãe em tratamento ou não tratada",
    },

    coinfeccaoHDV: {
      screening: "Anti-HDV em todos HBsAg+",
      tratamento: {
        HBV: "Análogo de nucleosídeo (TAF, TDF ou Entecavir)",
        HDV: {
          padrao: "PegIFN alfa por 48 semanas (resposta limitada)",
          novaTerapia: {
            bulevirtide: {
              mecanismo: "Inibidor de entrada viral (bloqueia NTCP)",
              aprovacao: "Aprovado EMA; em análise FDA",
              eficacia: "Melhora significativa em estudos de fase 3",
              uso: "Monoterapia ou combinado com PegIFN",
            },
          },
        },
      },
    },

    imunossupressao: {
      risco:
        "Reativação de HBV em imunossuprimidos HBsAg+ ou anti-HBc+ isolado",
      classificacaoRisco: {
        alto: [
          "Rituximab ou anti-CD20",
          "QT com antraciclinas",
          "Transplante de medula óssea",
          "Altas doses de corticoides (>20mg/dia por >4 semanas)",
        ],
        moderado: [
          "Anti-TNF",
          "Inibidores de checkpoint (raro mas grave)",
          "Corticoides em dose moderada",
        ],
        baixo: ["MTX", "Azatioprina", "Corticoides em baixa dose"],
      },
      profilaxia: {
        HBsAgPositivo:
          "Entecavir ou TDF durante e 6-12 meses após imunossupressão",
        antiHBcIsolado: {
          altoRisco: "Profilaxia durante tratamento",
          baixoRisco: "Monitorar HBV-DNA; tratar se reativar",
        },
      },
    },

    reativacao: {
      definicao: "Aumento de HBV-DNA ≥2 log ou reaparecimento de HBsAg",
      manejo:
        "Iniciar análogo imediatamente; considerar suspender imunossupressor se possível",
      prognostico:
        "Alta mortalidade se insuficiência hepática - prevenção é essencial",
    },
  },

  monitoramento: {
    emTratamento: {
      frequencia: "A cada 3-6 meses",
      exames: [
        "HBV-DNA quantitativo",
        "ALT",
        "HBeAg e anti-HBe (se inicialmente HBeAg+)",
        "HBsAg quantitativo (opcional - pode predizer perda)",
        "Função renal (se em TDF)",
        "Função hepática",
      ],
    },
    semTratamento: {
      infeccaoCronica:
        "ALT + HBV-DNA cada 3-6 meses; considerar elastografia anual",
      portadorInativo: "ALT + HBV-DNA cada 6-12 meses",
    },
  },

  fonte: "AASLD HBV Guidance 2025 Update; EASL CPG HBV 2017",
};

// =============================================================================
// DOENÇAS CÍSTICAS DO FÍGADO (EASL 2022)
// =============================================================================

export const DOENCAS_CISTICAS_HEPATICAS = {
  descricao: "Doenças Císticas do Fígado - EASL CPG 2022",

  classificacao: {
    naoInfecciosas: {
      congenitas: [
        "Cistos hepáticos simples",
        "Doença hepática policística (PLD)",
        "Cistos biliares (Caroli, cistos de colédoco)",
        "Hamartomas biliares (complexos de von Meyenburg)",
      ],
      neoplasicas: ["Cistoadenoma biliar", "Cistoadenocarcinoma"],
    },
    infecciosas: ["Cisto hidático (Echinococcus)", "Abscesso hepático"],
    nota: "Este guideline foca em cistos não-infecciosos",
  },

  cistosSimples: {
    descricao: "Cistos hepáticos simples (solitários)",

    epidemiologia: {
      prevalencia: "2.5-18% da população (aumenta com idade)",
      sexo: "Mais comum em mulheres",
      maioria: "Assintomáticos, achado incidental",
    },

    caracteristicasImagem: {
      US: {
        tipico: [
          "Anecoico (conteúdo líquido homogêneo)",
          "Parede fina imperceptível",
          "Reforço acústico posterior",
          "Sem septos ou componentes sólidos",
        ],
      },
      TC_RM: {
        tipico: [
          "Conteúdo líquido (baixa densidade em TC, alto sinal T2)",
          "Sem realce após contraste",
          "Sem septos, calcificações ou componentes sólidos",
        ],
      },
    },

    diagnosticoDiferencial: {
      caracteristicasAtipicas: [
        "Septos",
        "Parede espessada ou irregular",
        "Componente sólido",
        "Realce de parede ou septos",
        "Calcificações",
      ],
      considerarSeAtipico: [
        "Cistoadenoma/cistoadenocarcinoma",
        "Cisto hidático",
        "Metástase cística",
        "Abscesso",
      ],
      conduta:
        "Cistos atípicos requerem investigação adicional (RM, biópsia ou ressecção)",
    },

    tratamento: {
      assintomatico: {
        conduta: "Observação - sem necessidade de seguimento rotineiro",
        nota: "Cistos simples típicos não necessitam controle por imagem",
      },
      sintomatico: {
        sintomas: [
          "Dor abdominal",
          "Saciedade precoce",
          "Náusea",
          "Dispneia (se muito grande)",
        ],
        opcoes: {
          aspiracaoEsclerose: {
            tecnica:
              "Aspiração percutânea + instilação de agente esclerosante (etanol)",
            eficacia: "50-90% de sucesso; recorrência em 15-25%",
            vantagem: "Menos invasivo",
            desvantagem: "Recorrência; múltiplas sessões podem ser necessárias",
          },
          fenestracaoLaparoscopica: {
            tecnica: "Destelhamento laparoscópico do cisto",
            indicacao: "Cistos grandes sintomáticos",
            eficacia: "70-90% de sucesso",
            complicacao: "Recorrência, ascite biliar",
          },
          resseccaoHepatica: {
            indicacao:
              "Cistos gigantes, falha de outras terapias, suspeita de malignidade",
            nota: "Raramente necessária para cistos simples",
          },
        },
      },
    },

    complicacoes: {
      raras: [
        "Hemorragia intracística",
        "Infecção",
        "Ruptura",
        "Compressão de estruturas adjacentes",
      ],
    },

    fonte: "EASL CPG Cystic Liver Diseases 2022",
  },

  doencaHepaticaPolicistica: {
    sigla: "PLD",
    descricao: "Polycystic Liver Disease - múltiplos cistos hepáticos",

    classificacao: {
      associadaADPKD: {
        nome: "PLD associada a Doença Renal Policística Autossômica Dominante",
        genes: "PKD1, PKD2",
        frequencia: "80-90% dos pacientes com ADPKD têm cistos hepáticos",
        caracteristica: "Doença renal é predominante",
      },
      PLDisolada: {
        nome: "PLD isolada (PCLD/ADPLD)",
        genes: "PRKCSH, SEC63, LRP5, ALG8, SEC61B, outros",
        caracteristica:
          "Cistos predominantemente hepáticos, rins normais ou poucos cistos",
        heranca: "Autossômica dominante",
      },
    },

    epidemiologia: {
      prevalenciaEmADPKD: "70-90%",
      PLDisolada: "Rara (1:100.000)",
      predominanciaFeminina: "Mulheres mais afetadas e com doença mais severa",
      fatoresAgravantes: ["Estrogênio exógeno", "Gestações múltiplas"],
    },

    classificacaoGravidade: {
      sistema: "Classificação de Gigot",
      tipos: {
        I: "Número limitado de cistos grandes (< 10 cistos)",
        II: "Envolvimento difuso por cistos médios com áreas de parênquima preservado",
        III: "Envolvimento maciço com distorção completa do parênquima",
      },
      outro: "Classificação volumétrica por TC/RM",
    },

    manifestacoesClinias: {
      maioria: "Assintomáticos (cistos descobertos incidentalmente)",
      sintomas: {
        mecanicos: [
          "Dor/desconforto abdominal",
          "Distensão abdominal",
          "Saciedade precoce",
          "Dispneia",
          "DRGE",
          "Limitação da mobilidade",
        ],
        complicacoes: [
          "Hemorragia intracística",
          "Infecção de cisto",
          "Ruptura (rara)",
          "Compressão de veia hepática/VCI",
          "Hipertensão portal (rara em PLD isolada)",
        ],
      },
      funcaoHepatica: "Geralmente preservada mesmo em doença avançada",
    },

    diagnostico: {
      imagem: {
        US: "Screening inicial; múltiplos cistos anecoicos",
        TC_RM: "Avaliação de volume hepático, planejamento cirúrgico",
        volumetria: "Volume hepático total para estadiamento",
      },
      criteriosDiagnosticos: {
        PLDassociadaADPKD: "Critérios renais de ADPKD + cistos hepáticos",
        PLDisolada:
          ">20 cistos hepáticos na ausência de doença renal policística",
      },
    },

    tratamento: {
      principiosGerais: [
        "Maioria não requer tratamento",
        "Tratar apenas se sintomático",
        "Evitar estrogênios",
        "Abordagem multidisciplinar",
      ],

      medicamentoso: {
        analogosSomatostatina: {
          medicamentos: ["Lanreotida", "Octreotida LAR"],
          mecanismo: "Reduz secreção de fluido cístico, inibe proliferação",
          eficacia: {
            reducaoVolume: "3-6% por ano (estabilização em maioria)",
            melhora: "Alguns pacientes têm redução significativa",
          },
          indicacao: "PLD sintomática com hepatomegalia significativa",
          dose: {
            lanreotida: "120 mg SC a cada 28 dias",
            octreotida: "20-40 mg IM a cada 28 dias",
          },
          efeitosAdversos: [
            "Diarreia",
            "Colelitíase",
            "Dor abdominal",
            "Flatulência",
          ],
          duracao: "Enquanto benefício presente; efeito reverte com suspensão",
        },
        emEstudo: [
          "Inibidores de mTOR (everolimus, sirolimus)",
          "Agonistas de receptor de somatostatina de nova geração",
        ],
      },

      intervencoes: {
        aspiracaoEsclerose: {
          indicacao: "Cisto dominante sintomático",
          eficacia: "Boa para cistos individuais",
          limitacao: "Não trata doença difusa",
        },
        fenestracaoLaparoscopica: {
          indicacao: "Gigot I com cistos periféricos acessíveis",
          tecnica: "Destelhamento de cistos superficiais",
          resultado:
            "Alívio sintomático em 75-90% inicialmente; recorrência comum",
        },
        hepatectomiaComFenestracao: {
          indicacao:
            "Gigot II/III com área de parênquima relativamente poupada",
          objetivo:
            "Ressecção do parênquima mais afetado + fenestração dos remanescentes",
        },
        transplanteHepatico: {
          indicacao: [
            "PLD severa refratária (Gigot III)",
            "Sintomas incapacitantes refratários",
            "Complicações graves recorrentes",
            "Desnutrição por saciedade precoce",
            "Qualidade de vida severamente comprometida",
          ],
          resultado: "Excelente (sobrevida >90% em 5 anos)",
          MELD: "Pode não refletir gravidade - considerar exceção MELD",
          nota: "Função hepática geralmente preservada - transplante por 'qualidade de vida'",
        },
      },
    },

    monitoramento: {
      assintomaticos: "US ou RM a cada 1-2 anos para avaliar progressão",
      emTratamentoMedico: "Volumetria por RM a cada 6-12 meses",
      funcaoRenal: "Monitorar se ADPKD associada",
    },

    doencaRenalAssociada: {
      rastreamento: "US renal em todos com PLD",
      seADPKD: {
        nefrologia: "Acompanhamento nefrológico",
        tolvaptan: "Indicado para ADPKD com risco de progressão",
        transplanteCombinado: "Considerar rim-fígado em casos selecionados",
      },
    },

    fonte: "EASL CPG Cystic Liver Diseases 2022",
  },

  hamartomaBiliar: {
    nome: "Complexos de von Meyenburg / Hamartomas biliares",

    descricao: "Malformações biliares benignas de ductos biliares pequenos",

    caracteristicas: {
      achado: "Geralmente incidental em imagem ou cirurgia",
      numero: "Múltiplos, pequenos (<15mm)",
      distribuicao: "Difusa pelo parênquima",
    },

    imagem: {
      US: "Múltiplas pequenas lesões hiperecogênicas ou hipoecogênicas",
      TC: "Lesões hipodensas que não realçam",
      RM: {
        T1: "Hipointenso",
        T2: "Hiperintenso",
        contraste: "Sem realce significativo",
      },
      diferencial: "Metástases hepáticas pequenas",
    },

    conduta: {
      isolados: "Benigno - sem necessidade de seguimento",
      atipicos: "RM com contraste hepatoespecífico para excluir malignidade",
      nota: "Associação rara com colangiocarcinoma - controverso",
    },

    fonte: "EASL CPG Cystic Liver Diseases 2022",
  },

  cistadenoma: {
    descricao: "Neoplasia cística biliar - potencial maligno",

    tipos: {
      cistadenomaMucinoso: {
        estromaMesenquimal: "Presente (variante mais comum em mulheres)",
        semEstroma: "Raro; maior risco de malignidade",
      },
    },

    caracteristicasImagem: {
      sugestivas: [
        "Septos internos",
        "Projeções papilares",
        "Realce de parede ou septos",
        "Calcificações",
        "Componente sólido",
      ],
    },

    conduta: {
      suspeita: "Ressecção cirúrgica completa recomendada",
      noBiopsia: "Evitar biópsia percutânea (risco de disseminação se maligno)",
      nota: "Diferenciação definitiva de cistoadenocarcinoma requer histologia",
    },

    fonte: "EASL CPG Cystic Liver Diseases 2022",
  },

  fonte: "EASL CPG Cystic Liver Diseases 2022",
};

// =============================================================================
// FUNÇÕES DE FORMATAÇÃO
// =============================================================================

/**
 * 📈 Formata tendências laboratoriais pré-calculadas para injeção no prompt
 */
export function formatarTendencias(dados: DadosAnonimizados): string {
  const tendencias = dados.tendenciasLab;
  if (!tendencias || Object.keys(tendencias).length === 0) {
    return "";
  }

  const nomes: Record<string, string> = {
    AST: "AST",
    ALT: "ALT",
    GGT: "GGT",
    FA: "FA",
    BT: "Bilirrubina Total",
    Creatinina: "Creatinina",
    INR: "INR",
    Albumina: "Albumina",
    Hemoglobina: "Hemoglobina",
    Plaquetas: "Plaquetas",
    Sodio: "Sódio",
    Tacrolimus: "Tacrolimus",
    Lactato: "Lactato",
    Amilase: "Amilase",
    Lipase: "Lipase",
    AcidoUrico: "Ácido úrico",
    CalcioIonico: "Cálcio iônico",
    T3Total: "T3 total",
    // B315-Fase3 (AI_142)
    Potassio: "Potássio",
    PCR: "PCR",
    Procalcitonina: "Procalcitonina",
    // B342 (AI_150)
    Fosforo: "Fósforo",
    LDH: "LDH",
  };

  const linhas: string[] = [];
  const alertas: string[] = [];

  for (const [campo, t] of Object.entries(tendencias)) {
    const nome = nomes[campo] || campo;
    const vals = t.valores;
    const sequencia = vals.map((v) => v.valor).join("→");
    // B244 (AI_110): datar a tendência para a IA saber a RECÊNCIA (um valor
    // antigo não deve ser lido como estado atual).
    const ultimaData = vals[vals.length - 1]?.data;
    let linha = `  ${nome}: ${sequencia} ${t.direcao}`;
    if (t.variacao !== null) {
      linha += ` (${t.variacao > 0 ? "+" : ""}${t.variacao}%)`;
    }
    if (ultimaData) linha += ` [último em ${ultimaData}]`;
    linhas.push(linha);
    if (t.alerta) {
      alertas.push(
        `  ⚠️ ${nome}: ${t.alerta} (último: ${vals[vals.length - 1].valor}${
          ultimaData ? " em " + ultimaData : ""
        })`
      );
    }
  }

  let resultado = `
TENDÊNCIAS LABORATORIAIS (últimas coletas, ordem cronológica):
${linhas.join("\n")}`;

  if (alertas.length > 0) {
    resultado += `

ALERTAS DE TENDÊNCIA:
${alertas.join("\n")}`;
  }

  return resultado;
}

/**
 * Formata o contexto clínico básico do paciente
 */
export function formatarContextoClinico(dados: DadosAnonimizados): string {
  const contexto: string[] = [];

  if (dados.idade) {
    contexto.push(`Idade: ${dados.idade} anos`);
  }
  if (dados.sexo) {
    contexto.push(`Sexo: ${dados.sexo === "M" ? "Masculino" : "Feminino"}`);
  }
  if (dados.tipoAtendimento) {
    contexto.push(`Tipo de Atendimento: ${dados.tipoAtendimento}`);
  }

  if (dados.diagnosticoBase) {
    contexto.push(`\nDiagnóstico de Base: ${dados.diagnosticoBase}`);
  }

  if (dados.comorbidades) {
    contexto.push(`Comorbidades: ${dados.comorbidades}`);
  }
  if (dados.peso) {
    contexto.push(`Peso: ${dados.peso} kg`);
  }
  if (dados.altura) {
    contexto.push(`Altura: ${dados.altura} cm`);
  }
  if (dados.alergias) {
    contexto.push(`Alergias: ${dados.alergias}`);
  }
  if (dados.medicacoesEmUso) {
    contexto.push(`Medicações em uso: ${dados.medicacoesEmUso}`);
  }

  // Dados do transplante (dentro do objeto transplante)
  if (dados.transplante) {
    contexto.push(`\nStatus: Transplantado`);
    contexto.push(`Data do Transplante: ${dados.transplante.dataTransplante}`);
    contexto.push(
      `Tempo de Transplante: ${dados.transplante.tempoTransplante}`
    );
    if (dados.transplante.tipoTransplante) {
      contexto.push(`Tipo: ${dados.transplante.tipoTransplante}`);
    }
    if (dados.transplante.indicacao) {
      contexto.push(`Indicação: ${dados.transplante.indicacao}`);
    }
  }

  // Nota metodológica sobre exames ausentes
  contexto.push(
    `\nNOTA: Exames laboratoriais ou de imagem ausentes nestes dados podem não ter sido digitados no sistema — NÃO assumir automaticamente que não foram realizados. Ao identificar exames ausentes, recomendar "verificar se já foi coletado/realizado antes de solicitar nova coleta" em vez de "solicitar coleta urgente".`
  );

  return contexto.join("\n");
}
/**
 * Formata os scores de gravidade
 */
export function formatarScores(dados: DadosAnonimizados): string {
  // B244 (AI_110) — MELD/Child-Pugh medem a gravidade da CIRROSE NATIVA. No
  // pós-transplante ela não existe mais → NÃO injetar esses scores no prompt
  // (nem como "não calculados"). Antes, exibi-los aqui contradizia a instrução
  // de "não comentar MELD no pós-Tx" e levava o modelo a citá-los/negá-los
  // (ruído). Fonte única: corrige a análise lab, de imagem e de evolução.
  const isPosTx =
    dados.tipoAtendimento === "pos-transplante" ||
    dados.tipoAtendimento === "pós-transplante" ||
    !!dados.transplante;
  if (isPosTx) return "";

  // 🫀 B351 passo 5b (AI_153) — DOADOR VIVO: MELD e Child são escores de
  // gravidade da CIRROSE; num doador saudável não se aplicam. A trava é aqui, na
  // FONTE, e não como regra de prompt: sem ela o bloco imprimia
  // "SCORES: Não calculados" — uma AFIRMAÇÃO ao modelo sobre um escore que não
  // deveria estar em pauta, que é exatamente o convite a narrar a proibição
  // (lição 1 do passo 5a). O MELD do RECEPTOR entra em bloco próprio e rotulado
  // (formatarConjuntoMinimoReceptor), nunca aqui.
  if (ehDoadorFigado(dados.tipoAtendimento)) return "";

  const scores: string[] = [];

  if (dados.scores) {
    if (dados.scores.meld !== undefined) {
      scores.push(`MELD: ${dados.scores.meld}`);
    }
    if (dados.scores.meldNa !== undefined) {
      scores.push(`MELD-Na: ${dados.scores.meldNa}`);
    }
    if (dados.scores.meld3 !== undefined) {
      scores.push(`MELD 3.0: ${dados.scores.meld3}`);
    }
    if (dados.scores.childPugh !== undefined) {
      const classe =
        dados.scores.classeChild ||
        (dados.scores.childPugh <= 6
          ? "A"
          : dados.scores.childPugh <= 9
          ? "B"
          : "C");
      scores.push(
        `Child-Pugh: ${dados.scores.childPugh} pontos (Classe ${classe})`
      );
    }
  }

  // B358-FU2 (AI_156) — MELD-Na AO VIVO do exame mais recente completo. É o
  // escore de ALOCAÇÃO VIGENTE em SP (a Portaria 8.041, que adota o MELD 3.0,
  // segue em vacatio). As regras da Central (MELD mínimo ≤ 14, validade do
  // exame, MELD > 10 da irressecabilidade do CHC) usam ESTE número.
  // ⟳ REVISAR quando a 8.041 entrar em vigor.
  const meldNaVivo =
    dados.meldNaAlocacaoSP ||
    meldNaDoExameMaisRecente(
      dados.examesLab,
      (dados as any).avaliacaoClinica?.dialise === true
    );
  if (meldNaVivo) {
    scores.push(
      `MELD-Na (alocação VIGENTE em SP, calculado do exame de ${meldNaVivo.data || "data não informada"}): ${meldNaVivo.valor}`
    );
  }

  return scores.length > 0
    ? `SCORES DE GRAVIDADE:\n${scores.join("\n")}`
    : "SCORES: Não calculados";
}

// ═══════════════════════════════════════════════════════════════════════════
// 🫀 B351 passo 5b (AI_153) — DOADOR VIVO DE FÍGADO
// ═══════════════════════════════════════════════════════════════════════════

/** Rótulos legíveis dos campos da aba do doador (a IA lê rótulo, não chave). */
const ROTULO_CAMPO_DOADOR: Record<string, string> = {
  peso: "Peso do doador (kg)",
  altura: "Altura do doador",
  tipoSanguineo: "Tipo sanguíneo do doador",
  comorbidades: "Comorbidades registradas",
  fatorVLeiden: "Fator V Leiden",
  protrombinaG20210A: "Protrombina G20210A",
  proteinaC: "Proteína C",
  proteinaS: "Proteína S",
  antitrombina3: "Antitrombina III",
  anticardiolipina: "Anticardiolipina",
  antiB2Glicoproteina: "Anti-β2-glicoproteína I",
  anticoagulanteLupico: "Anticoagulante lúpico",
  loboDoado: "Enxerto pretendido",
  volumeFigadoTotal: "Volume hepático total registrado (cm³)",
  volumeEnxerto: "Volume do enxerto registrado (cm³)",
  pesoFigadoTotal: "Volume hepático total registrado (cm³, campo legado)",
  pesoEnxerto: "Volume do enxerto registrado (cm³, campo legado)",
  pesoReceptor: "Peso do receptor digitado na aba (kg)",
  variacaoArterial: "Artéria hepática (Michels)",
  variacaoPortal: "Veia porta",
  variacaoBiliar: "Via biliar",
  veiasHepaticas: "Veias hepáticas — padrão",
  tributariaV5: "Tributária de V5 significativa",
  tributariaV8: "Tributária de V8 significativa",
  incluiVeiaHepaticaMedia: "Inclui a veia hepática média",
  anatomiaDetalhe: "Detalhe anatômico (texto livre)",
  esteatosePercentual: "Esteatose registrada (%)",
  esteatoseMetodo: "Método da esteatose",
  doacaoRealizada: "Doação já realizada",
  dataDoacao: "Data da cirurgia de doação",
  psicossocialParecer: "Parecer psicossocial",
  psicossocialData: "Data da avaliação psicossocial",
  psicossocialObs: "Observações da avaliação psicossocial",
  parentesco: "Parentesco com o receptor",
  autorizacaoJudicial: "Autorização judicial",
  autorizacaoJudicialData: "Data da autorização judicial",
  observacoes: "Observações gerais",
};

/**
 * Bloco da AVALIAÇÃO DO DOADOR (aba) + os números que o SISTEMA calculou.
 *
 * Por que os derivados vêm prontos: no passo 5a a análise disse "GRWR não
 * calculável" com o peso do receptor JÁ digitado na aba — o prompt é que não o
 * recebia. Os defeitos mais custosos foram de dados que o sistema já tinha e não
 * entregava. Aqui GRWR, remanescente e IMC chegam calculados pela fonte única
 * `volumetriaDoador`, ROTULADOS como calculados pelo sistema: a IA confere e
 * confronta com o laudo, não recalcula do zero.
 */
export function formatarAvaliacaoDoador(dados: DadosAnonimizados): string {
  const av = dados.avaliacaoDoador;
  if (!av) return "";

  const linhas: string[] = [];
  for (const [chave, valor] of Object.entries(av.campos)) {
    linhas.push(`- ${ROTULO_CAMPO_DOADOR[chave] || chave}: ${valor}`);
  }

  const d = av.derivados;
  const derivadas: string[] = [];
  if (d.imc !== null) derivadas.push(`- IMC do doador: ${d.imc} kg/m²`);
  if (d.remanescente !== null) {
    derivadas.push(
      `- Remanescente do doador: ${d.remanescente}% do volume total (${d.remanescenteClassificacao})`
    );
  }
  if (d.grwr !== null) {
    derivadas.push(`- GRWR: ${d.grwr}% (${d.grwrClassificacao})`);
  }

  return `
## 🫀 AVALIAÇÃO DO DOADOR (registrada na aba do prontuário)
${linhas.join("\n") || "- (nenhum campo preenchido)"}
${
  derivadas.length
    ? `
### Números CALCULADOS PELO SISTEMA a partir dos campos acima
${derivadas.join("\n")}
Estes três não foram digitados: saíram da fonte única de volumetria, com os
limiares do serviço. A altura é aceita em metros OU em centímetros e convertida
pelo sistema — "1.6" é 1,60 m, não erro de digitação. CONFIRA-OS contra o laudo de imagem e, se a volumetria do
laudo divergir da registrada, APONTE a divergência — uma das duas está
desatualizada, e isso importa mais que o número em si.`
    : ""
}
`;
}

/**
 * Bloco do RECEPTOR VINCULADO — conjunto mínimo, não identificado.
 *
 * ⚠️ Este é o único ponto do prompt em que dados de OUTRO prontuário aparecem.
 * O receptor é sempre "o receptor": nome, id, idade, sexo e qualquer outro
 * identificador NÃO chegam até aqui (a saída de `montarConjuntoMinimoReceptor`
 * é fechada). Os rótulos de confiança do MELD viajam com o número, porque
 * número derivado herda a confiança da origem (lição 5).
 */
export function formatarConjuntoMinimoReceptor(
  dados: DadosAnonimizados
): string {
  // Só faz sentido na modalidade doador.
  if (!ehDoadorFigado(dados.tipoAtendimento)) return "";

  const r = dados.receptorMinimo;
  if (!r) {
    return `
## 🔗 RECEPTOR VINCULADO
NÃO HÁ receptor vinculado na aba deste doador. Portanto o GRWR e a
compatibilidade sanguínea ficam INDETERMINADOS — não os estime, não os infira a
partir de outros dados e não os declare adequados. Sinalize que o vínculo com o
receptor precisa ser registrado na aba.
`;
  }

  const l: string[] = [];
  if (r.peso !== null) l.push(`- Peso do receptor: ${r.peso} kg (cadastro)`);
  if (r.pesoDigitadoNaAba !== null) {
    l.push(
      `- ⚠️ DIVERGÊNCIA de peso: a aba do doador registra ${r.pesoDigitadoNaAba} kg` +
        (r.peso !== null
          ? `, o cadastro do receptor registra ${r.peso} kg. Uma das duas fontes está desatualizada — aponte, não escolha em silêncio (o GRWR depende deste número).`
          : `; o cadastro do receptor não tem peso.`)
    );
  }
  if (r.superficieCorporal !== null) {
    l.push(
      `- Superfície corporal do receptor: ${r.superficieCorporal} m² (Mosteller)` +
        (r.superficieCorporalProvisoria
          ? " — PROVISÓRIA: a altura do cadastro foi interpretada como metros e convertida."
          : "")
    );
  }
  if (r.meld !== null) {
    const rotulos: string[] = [`origem: ${r.meldOrigem}`];
    if (r.meldDentroDaValidade === false) rotulos.push("FORA da validade");
    if (r.meldComponentesSuspeitos.length) {
      rotulos.push(
        `componente(s) implausível(is): ${r.meldComponentesSuspeitos.join(", ")}`
      );
    }
    l.push(
      `- MELD do receptor: ${r.meld} (exame de ${r.meldData}; ${rotulos.join("; ")})`
    );
  } else {
    l.push(
      "- MELD do receptor: NÃO DETERMINÁVEL nos exames disponíveis — não o estime."
    );
  }
  if (r.diagnostico) l.push(`- Diagnóstico do receptor: ${r.diagnostico}`);
  l.push(
    `- Descompensação atual do receptor: ${
      r.descompensacaoAtual === null
        ? "AVALIAÇÃO CLÍNICA NÃO REGISTRADA — isto NÃO significa ausência de descompensação"
        : r.descompensacaoAtual
        ? "SIM (ascite, encefalopatia ou hemorragia digestiva registradas)"
        : "não registrada nos campos de ascite, encefalopatia e hemorragia digestiva"
    }`
  );
  l.push(
    `- Compatibilidade sanguínea doador→receptor: ${
      r.aboCompativel === "compativel"
        ? "COMPATÍVEL"
        : r.aboCompativel === "incompativel"
        ? "INCOMPATÍVEL"
        : "NÃO INFORMADA (falta o tipo sanguíneo de um dos dois) — nunca presuma compatibilidade"
    }`
  );

  return `
## 🔗 RECEPTOR VINCULADO — CONJUNTO MÍNIMO (não identificado)
Os dados abaixo são de OUTRO prontuário e chegam deliberadamente reduzidos ao
mínimo necessário para avaliar a doação. Refira-se sempre a "o receptor": não
há, e não deve haver, nome, idade ou sexo. Use-os para o dimensionamento do
enxerto e para o risco — não para discutir o tratamento do receptor, que não é o
objeto desta análise.
${l.join("\n")}
`;
}

/**
 * 🔬 CAMADA A — Extração canônica de campos histopatológicos
 *
 * Recebe o texto livre de analiseCompleta (geralmente vindo da análise IA
 * de exames anatomopatológicos / IHQ) e extrai TODOS os campos
 * histopatológicos relevantes via regex tolerante a variações de formatação
 * (case-insensitive, com ou sem acento, com `:` ou `=`).
 *
 * Cada campo retorna null se não encontrado — a renderização posterior
 * (Camada B em formatarAchadosImagem) só exibe campos preenchidos, evitando
 * poluir o prompt com campos vazios.
 *
 * Marcadores de IHQ são capturados como BLOCO ÚNICO (string livre) para
 * preservar variantes de marcadores não previstas em um dicionário fixo
 * (ex: TTF-1, p40, GATA3 podem aparecer em laudos futuros e devem chegar
 * íntegros à IA do laudo, não filtrados por um parser rígido).
 */
function extrairCamposHistopatologicos(texto: string): {
  diagnostico: string | null;
  conclusao: string | null;
  grauHistologico: string | null;
  estadiamento: string | null;
  margemCirurgica: string | null;
  invasaoVascularMicro: string | null;
  invasaoVascularMacro: string | null;
  invasaoPerineural: string | null;
  satelitose: string | null;
  numeroNodulos: string | null;
  maiorDiametro: string | null;
  necroseTumoral: string | null;
  padraoArquitetural: string | null;
  backgroundHepatico: string | null;
  esteatose: string | null;
  colestase: string | null;
  imunohistoquimica: string | null;
} {
  if (!texto || typeof texto !== "string") {
    return {
      diagnostico: null,
      conclusao: null,
      grauHistologico: null,
      estadiamento: null,
      margemCirurgica: null,
      invasaoVascularMicro: null,
      invasaoVascularMacro: null,
      invasaoPerineural: null,
      satelitose: null,
      numeroNodulos: null,
      maiorDiametro: null,
      necroseTumoral: null,
      padraoArquitetural: null,
      backgroundHepatico: null,
      esteatose: null,
      colestase: null,
      imunohistoquimica: null,
    };
  }

  // Helper: extrai primeira ocorrência (campo único)
  const extrair = (regex: RegExp): string | null => {
    const m = texto.match(regex);
    return m && m[1] ? m[1].trim() : null;
  };

  // Helper: extrai TODAS as ocorrências (para campos numerados como DIAGNOSTICO_1, DIAGNOSTICO_2)
  // Junta valores com " | " preservando todos os diagnósticos do laudo.
  // Usa Array.from() em vez de spread para compatibilidade com tsconfig sem downlevelIteration.
  const extrairTodos = (regex: RegExp): string | null => {
    const matches = Array.from(texto.matchAll(regex));
    if (matches.length === 0) return null;
    const valores = matches
      .map((m) => (m[1] ? m[1].trim() : ""))
      .filter((v) => v.length > 0);
    if (valores.length === 0) return null;
    return valores.join(" | ");
  };

  // Helper: estadiamento ypTNM com fallback inteligente
  // 1) Tenta campo explícito ESTADIAMENTO: ou ESTADIO PATOLÓGICO:
  // 2) Se não encontrar, escaneia o texto inteiro por padrões ypT4N0, ypT2 pN0, etc.
  //    Útil quando o estadiamento está embutido dentro de DIAGNOSTICO_1 (formato Delboni)
  const extrairEstadiamento = (): string | null => {
    const explicit = extrair(
      /(?:ESTADIAMENTO|ESTADIO[\s_]+PATOL[OÓ]GICO)\s*:\s*([^\n]+)/i
    );
    if (explicit) return explicit;
    const regexPTNM =
      /\byp?T\d[a-z]?(?:[\s_]*p?N\d[abc]?)?(?:[\s_]*M[x0-9])?/gi;
    const matches = Array.from(texto.matchAll(regexPTNM));
    if (matches.length === 0) return null;
    const valoresTrimados = matches.map((m) => m[0].trim());
    const unicos = valoresTrimados.filter(
      (valor, indice) => valoresTrimados.indexOf(valor) === indice
    );
    return unicos.join(" | ");
  };

  // Helper IHQ: captura bloco multilinha ANCORADO EM INÍCIO DE LINHA (flag m + ^)
  // com dois pontos obrigatórios. Isso evita casar "IHQ" no título de um
  // documento ou em prosa livre. A regex exige que o rótulo seja o primeiro
  // elemento significativo de uma linha.
  const extrairBlocoIHQ = (): string | null => {
    const regexBloco =
      /^[\s#*•>-]*(?:IMUNO[-\s_]?HISTOQU[IÍ]MIC[AO]|PERFIL[\s_]+IMUNOHISTOQU[IÍ]MICO|IHQ)\s*:\s*([\s\S]*?)(?=\n\s*[A-Z][A-Z_\s]{3,}\s*:|$)/im;
    const m = texto.match(regexBloco);
    if (!m || !m[1]) return null;
    const bloco = m[1].trim();
    if (bloco.length === 0) return null;
    return bloco.length > 600 ? bloco.substring(0, 600) : bloco;
  };

  // IMPORTANTE: todas as regex abaixo exigem `:` OBRIGATÓRIO (não `:?`).
  // Isso é o que diferencia um RÓTULO ESTRUTURADO de prosa livre que
  // coincidentemente contém a palavra. Sem essa restrição, auditorias
  // de exames de imagem que escrevem "necrose coagulativa..." em prosa
  // disparam falso positivo e geram blocos ACHADOS HISTOPATOLÓGICOS
  // ESTRUTURADOS poluídos em exames que não são histopatológicos.
  return {
    diagnostico: extrairTodos(/DIAGN[OÓ]STICO(?:_?\d+)?\s*:\s*([^\n]+)/gi),
    conclusao: extrair(/CONCLUS[AÃ]O\s*:\s*([^\n]+)/i),
    grauHistologico: extrair(/GRAU(?:[\s_]+HISTOL[OÓ]GICO)?\s*:\s*([^\n]+)/i),
    estadiamento: extrairEstadiamento(),
    margemCirurgica: extrair(
      /MARGE(?:M|NS)(?:[\s_]+CIR[UÚ]RGIC[AO]S?)?\s*:\s*([^\n]+)/i
    ),
    invasaoVascularMicro: extrair(
      /(?:INVAS[AÃ]O[\s_]+(?:VASCULAR[\s_]+)?MICRO(?:VASCULAR)?|MICROINVAS[AÃ]O[\s_]+VASCULAR|\bMVI)\s*:\s*([^\n]+)/i
    ),
    invasaoVascularMacro: extrair(
      /(?:INVAS[AÃ]O[\s_]+(?:VASCULAR[\s_]+)?MACRO(?:VASCULAR)?|\bMaVI)\s*:\s*([^\n]+)/i
    ),
    invasaoPerineural: extrair(/INVAS[AÃ]O[\s_]+PERINEURAL\s*:\s*([^\n]+)/i),
    satelitose: extrair(/SATELITOSE\s*:\s*([^\n]+)/i),
    numeroNodulos: extrair(
      /(?:N[UÚ]MERO[\s_]+DE[\s_]+N[OÓ]DULOS|N[OÓ]DULOS[\s_]+IDENTIFICADOS)\s*:\s*([^\n]+)/i
    ),
    maiorDiametro: extrair(
      /(?:MAIOR[\s_]+DI[AÂ]METRO|DIMENS[OÕ]ES?[\s_]+DO[\s_]+TUMOR|TAMANHO[\s_]+TUMORAL)\s*:\s*([^\n]+)/i
    ),
    necroseTumoral: extrair(/NECROSE(?:[\s_]+TUMORAL)?\s*:\s*([^\n]+)/i),
    padraoArquitetural: extrair(/PADR[AÃ]O[\s_]+ARQUITETURAL\s*:\s*([^\n]+)/i),
    backgroundHepatico: extrair(
      /(?:BACKGROUND[\s_]+HEP[AÁ]TICO|F[IÍ]GADO[\s_]+ADJACENTE|PAR[EÊ]NQUIMA[\s_]+(?:HEP[AÁ]TICO[\s_]+)?N[AÃ]O[\s_]+(?:NEOPL[AÁ]SICO|TUMORAL))\s*:\s*([^\n]+)/i
    ),
    esteatose: extrair(/ESTEATOSE\s*:\s*([^\n]+)/i),
    colestase: extrair(/COLESTASE\s*:\s*([^\n]+)/i),
    imunohistoquimica: extrairBlocoIHQ(),
  };
}

/**
 * Formata achados de imagem estruturados
 *
 * 🔬 CAMADA B — Renderização hierárquica:
 *   1. 🚨 CONCLUSÃO/DIAGNÓSTICO no topo (soberania do patologista)
 *   2. 📋 ACHADOS HISTOPATOLÓGICOS ESTRUTURADOS (apenas campos preenchidos)
 *   3. Achados de imagem estruturados (Milão, trombose, ascite, etc.)
 *   4. 📄 analiseCompleta truncada como contexto descritivo bruto
 */
export function formatarAchadosImagem(
  achados: AchadosImagemEstruturados[],
  // B351-FU11 (AI_153) — só o caminho do DOADOR passa `true`. O bloco de campos
  // estruturados abaixo é inteiramente oncológico/histopatológico (Milão,
  // nódulos, invasão vascular, margem, IHQ): não existe campo algum que carregue
  // volumetria ou os quatro eixos anatômicos. E quando há conclusão clara a
  // descrição textual completa é SUPRIMIDA — de modo que a análise geral do
  // doador nunca via a anatomia que o próprio prompt dela exige (achado da 3ª
  // validação ao vivo: a imagem trazia porta 1,3 cm e VHD/VHM/VHE, e a análise
  // geral dizia "portal e venoso não descritos"). A supressão existe para a IA
  // não reinterpretar marcadores TUMORAIS — risco que não existe num doador
  // saudável. Destravar aqui é paliativo; o certo é extrair os campos do doador
  // como campos (B354, mesmo padrão do B301).
  opcoes?: { incluirDescricaoCompleta?: boolean }
): string {
  if (!achados || achados.length === 0) {
    return "ACHADOS DE IMAGEM: Não disponíveis";
  }

  return achados
    .map((achado) => {
      const partes: string[] = [];
      partes.push(`\n📷 EXAME: ${achado.tipoExame || "Imagem"}`);
      if (achado.dataExame) {
        partes.push(`   Data: ${achado.dataExame}`);
      }

      // ════════════════════════════════════════════════════════════════
      // ✅ HIERARQUIA DE FONTES (Bloco 1 AI_34)
      //
      // 1. conclusaoLaudistaLiteral (status: sucesso)
      //    → FONTE PRIMÁRIA. Transcrição VERBATIM feita pelo Haiku vision
      //      diretamente do PDF do laudo. Imune ao self-loop interpretativo
      //      do Sonnet. Quando presente, é a verdade-base do que o laudista
      //      humano escreveu.
      //
      // 2. achado.conclusao (interpretação INTEGRADA da IA Sonnet)
      //    → FONTE SECUNDÁRIA. Renderizada SEMPRE que existir, mas COM
      //      label explícito de subordinação ao literal quando o literal
      //      estiver presente. Em exames antigos (pré-Bloco 1) onde o
      //      literal é ausente, vira a fonte primária por fallback.
      //
      // 3. extrairCamposHistopatologicos (regex sobre analiseCompleta)
      //    → FALLBACK LEGADO. Só roda se 1 e 2 ausentes.
      //
      // CRÍTICO: quando há literal + interpretação IA, AMBOS são exibidos
      // com hierarquia explícita. NUNCA esconder a interpretação — o
      // hepatologista vai querer ver a divergência se houver.
      // ════════════════════════════════════════════════════════════════
      const literal = achado.conclusaoLaudistaLiteral;
      const literalStatus = achado.conclusaoLiteralStatus;
      const temLiteralValido =
        literal && literal.trim().length > 0 && literalStatus === "sucesso";

      const analiseTexto = (achado as any).analiseCompleta || "";
      const campos = extrairCamposHistopatologicos(analiseTexto);
      const diagnosticoExtraido = campos.diagnostico || "";
      const conclusaoExtraida = campos.conclusao || "";

      if (temLiteralValido) {
        // CAMINHO PRIMÁRIO: literal verificado existe
        const labelLaudista = achado.tipoLaudista
          ? achado.tipoLaudista.toUpperCase()
          : "LAUDISTA";
        partes.push(
          `   📋 CONCLUSÃO LITERAL DO ${labelLaudista} (transcrição verbatim do laudo — FONTE PRIMÁRIA, USO OBRIGATÓRIO E SOBERANO):`
        );
        partes.push(`      "${literal.trim()}"`);

        if (achado.conclusao) {
          partes.push(
            `   🤖 INTERPRETAÇÃO INTEGRADA DA IA (referência secundária — quando divergir do literal acima, prevalece o literal): ${achado.conclusao}`
          );
        }
      } else {
        // CAMINHO LEGADO: sem literal verificado (exame pré-Bloco 1 ou erro extração)
        if (literalStatus === "erro_extracao") {
          partes.push(
            `   ⚠️ Transcrição literal indisponível (erro de extração) — usando interpretação IA legada:`
          );
        } else if (!literal && !literalStatus) {
          partes.push(
            `   ⚠️ Análise legada (anterior ao Bloco 1 AI_34) — sem transcrição literal verificada:`
          );
        }

        if (achado.conclusao) {
          partes.push(
            `   🚨 DIAGNÓSTICO/CONCLUSÃO (USO OBRIGATÓRIO E SOBERANO): ${achado.conclusao}`
          );
        } else if (diagnosticoExtraido) {
          partes.push(
            `   🚨 DIAGNÓSTICO DO PATOLOGISTA (USO OBRIGATÓRIO E SOBERANO): ${diagnosticoExtraido}`
          );
          if (conclusaoExtraida && conclusaoExtraida !== diagnosticoExtraido) {
            partes.push(`   🚨 CONCLUSÃO DO PATOLOGISTA: ${conclusaoExtraida}`);
          }
        } else if (conclusaoExtraida) {
          partes.push(
            `   🚨 CONCLUSÃO (USO OBRIGATÓRIO E SOBERANO): ${conclusaoExtraida}`
          );
        }
      }

      // CAMADA B: bloco estruturado de achados histopatológicos
      // Renderiza APENAS campos preenchidos. Se nenhum campo histopatológico
      // foi extraído (ex: exame de imagem RM/TC sem laudo histopatológico),
      // o bloco inteiro é omitido — não polui o prompt para exames de imagem.
      const camposHistopat: Array<[string, string | null]> = [
        ["Grau histológico", campos.grauHistologico],
        ["Estadiamento (pTNM)", campos.estadiamento],
        ["Padrão arquitetural", campos.padraoArquitetural],
        ["Margem cirúrgica", campos.margemCirurgica],
        ["Invasão vascular microscópica (MVI)", campos.invasaoVascularMicro],
        ["Invasão vascular macroscópica", campos.invasaoVascularMacro],
        ["Invasão perineural", campos.invasaoPerineural],
        ["Satelitose", campos.satelitose],
        ["Número de nódulos", campos.numeroNodulos],
        ["Maior diâmetro tumoral", campos.maiorDiametro],
        ["Necrose tumoral", campos.necroseTumoral],
        ["Parênquima não-tumoral (background)", campos.backgroundHepatico],
        ["Esteatose", campos.esteatose],
        ["Colestase", campos.colestase],
        ["Imuno-histoquímica (IHQ)", campos.imunohistoquimica],
      ];
      const camposPreenchidos = camposHistopat.filter(
        ([, valor]) => valor !== null && valor !== ""
      );
      // THRESHOLD DE CONFIANÇA: só renderizar o bloco estruturado se houver
      // pelo menos 2 campos preenchidos. Isso elimina falsos positivos
      // isolados (ex: exames de RFA ou procedimentos onde um único rótulo
      // tipo "Margem cirúrgica:" aparece em prosa descritiva sem ser
      // histopatológico). Um verdadeiro laudo histopatológico virtualmente
      // sempre tem 3+ campos preenchidos.
      if (camposPreenchidos.length >= 2) {
        partes.push(`   📋 ACHADOS HISTOPATOLÓGICOS ESTRUTURADOS:`);
        for (const [rotulo, valor] of camposPreenchidos) {
          partes.push(`      • ${rotulo}: ${valor}`);
        }
      }

      // Status Milão - corrigido para usar os valores reais da interface
      if (achado.statusMilao && achado.statusMilao !== "NÃO APLICÁVEL") {
        const emoji = achado.statusMilao === "DENTRO" ? "✅" : "❌";
        partes.push(`   ${emoji} Critérios de Milão: ${achado.statusMilao}`);
      }

      // Lesões hepáticas (texto livre que pode conter info de LI-RADS)
      if (achado.lesoesHepaticas) {
        partes.push(`   Lesões Hepáticas: ${achado.lesoesHepaticas}`);
      }

      // Trombose portal
      if (achado.trombosePortal && achado.trombosePortal !== "NÃO") {
        partes.push(`   Trombose Portal: ${achado.trombosePortal}`);
      }

      // Tipo de trombose - corrigido para usar os valores reais
      if (achado.tipoTrombose && achado.tipoTrombose !== "NÃO APLICÁVEL") {
        const emoji = achado.tipoTrombose === "TUMORAL" ? "🔴" : "🟡";
        partes.push(`   ${emoji} Trombose: ${achado.tipoTrombose}`);
      }

      // Esplenomegalia
      if (achado.esplenomegalia) {
        partes.push(`   Esplenomegalia: ${achado.esplenomegalia}`);
      }

      // Ascite - corrigido para usar "SIM" em vez de boolean
      if (achado.ascite === "SIM") {
        partes.push(`   Ascite: Presente`);
      }

      // Shunts
      if (achado.shunts) {
        partes.push(`   Shunts: ${achado.shunts}`);
      }

      // Outros achados
      if (achado.outrosAchados) {
        partes.push(`   Outros achados: ${achado.outrosAchados}`);
      }

      // ✅ B315 (AI_142) — Endoscopia estruturada (B301): varizes/GAVE/gastropatia/
      // risco. Antes só o insight de CSPH lia; agora chega à análise/laudo.
      const endoscopia: string[] = [];
      if ((achado as any).varizesEsofagicas)
        endoscopia.push(`varizes esofágicas: ${(achado as any).varizesEsofagicas}`);
      if ((achado as any).varizesGastricas)
        endoscopia.push(`varizes gástricas: ${(achado as any).varizesGastricas}`);
      if ((achado as any).gastropatiaHipertensiva)
        endoscopia.push(
          `gastropatia hipertensiva portal: ${(achado as any).gastropatiaHipertensiva}`
        );
      if ((achado as any).gave) endoscopia.push(`GAVE: ${(achado as any).gave}`);
      if ((achado as any).sinaisRisco)
        endoscopia.push(`sinais de risco: ${(achado as any).sinaisRisco}`);
      if ((achado as any).classificacaoRisco)
        endoscopia.push(`classificação de risco: ${(achado as any).classificacaoRisco}`);
      if (endoscopia.length)
        partes.push(`   🔎 Endoscopia (dados estruturados): ${endoscopia.join("; ")}`);

      // ✅ B315 — Elastografia estruturada (B138): rigidez (kPa)/METAVIR/CAP.
      const elasto: string[] = [];
      if ((achado as any).rigidezKpa)
        elasto.push(`rigidez hepática: ${(achado as any).rigidezKpa} kPa`);
      if ((achado as any).fibroseMetavir)
        elasto.push(`METAVIR: ${(achado as any).fibroseMetavir}`);
      if ((achado as any).capEsteatose)
        elasto.push(`CAP (esteatose): ${(achado as any).capEsteatose}`);
      if (elasto.length)
        partes.push(`   📐 Elastografia (dados estruturados): ${elasto.join("; ")}`);

      // ✅ B315 — Adequação amostral da biópsia: confiabilidade do laudo.
      const aa = (achado as any).adequacaoAmostral;
      if (aa?.classificacao) {
        // 0 mm / 0 espaços-porta é dado válido (biópsia inadequada extrema) —
        // usar checagem de null/undefined, não falsy (senão o 0 some).
        const det = [
          aa.comprimentoMm != null ? `${aa.comprimentoMm} mm` : "",
          aa.espacosPorta != null ? `${aa.espacosPorta} espaços-porta` : "",
        ]
          .filter(Boolean)
          .join(", ");
        const ressalva =
          aa.classificacao === "inadequada" || aa.classificacao === "marginal"
            ? " — amostra limitada NÃO exclui fibrose avançada/cirrose"
            : "";
        partes.push(
          `   🔬 Adequação amostral da biópsia: ${aa.classificacao}${
            det ? ` (${det})` : ""
          }${ressalva}`
        );
      }

      // ✅ B315 — Indicação clínica do pedido: calibra a relevância do achado
      // (vigilância de rotina × suspeita de complicação aguda).
      if ((achado as any).indicacaoClinica) {
        partes.push(`   🎯 Indicação do exame: ${(achado as any).indicacaoClinica}`);
      }

      // Conclusão já foi exibida no topo do bloco — não duplicar aqui

      // Resumo da análise IA (apenas se NÃO houver conclusão estruturada
      // nem diagnóstico/conclusão extraído por regex do analiseCompleta).
      // Quando há conclusão clara, evitar mostrar resumo/análise completa para
      // não dar margem à IA reinterpretar marcadores individuais.
      const temConclusaoClara =
        !opcoes?.incluirDescricaoCompleta &&
        !!(achado.conclusao || diagnosticoExtraido || conclusaoExtraida);
      if (achado.resumoAchados && !temConclusaoClara) {
        partes.push(`   Resumo IA: ${achado.resumoAchados}`);
      }
      // Análise completa textual — contexto descritivo bruto
      // Renderizada apenas quando NÃO há conclusão clara extraída.
      // Truncada em 600 chars (reduzido de 800) porque os campos histopatológicos
      // estruturados acima já capturam os dados clinicamente relevantes — esta
      // seção serve apenas como contexto de fundo para a IA do laudo, não como
      // fonte primária de raciocínio.
      if ((achado as any).analiseCompleta && !temConclusaoClara) {
        // B351-FU11: 600 chars cortariam a descrição do doador no meio dos
        // quatro eixos (só o arterial costuma passar disso) — e um eixo cortado
        // seria lido como eixo NÃO DESCRITO, que é pior do que não mandar nada.
        const limite = opcoes?.incluirDescricaoCompleta ? 6000 : 600;
        const completa = (achado as any).analiseCompleta as string;
        const textoResumido = completa.substring(0, limite);
        const cortou = completa.length > limite;
        partes.push(
          `   📄 Contexto descritivo bruto${cortou ? " (truncado)" : ""}: ${textoResumido}`
        );
      }
      return partes.join("\n");
    })
    .join("\n\n");
}

/**
 * 📷 B315-Fase4 (AI_142) — Exames de imagem AINDA NÃO analisados pela IA.
 * A análise geral só renderiza `achadosImagem` (exames com análise estruturada);
 * um exame com só laudo/descrição digitados ficava INVISÍVEL — a IA raciocinava
 * como se ele não existisse (ex.: "solicitar US" com o US já no prontuário). Este
 * bloco lista os não-analisados com o conteúdo do laudo/descrição (truncado),
 * para a IA considerá-los e pedir REVISÃO em vez de novo exame. Custo baixo (só os
 * não-analisados, teto de 12). O chat já mostrava esses exames — isto alinha a
 * análise geral.
 */
export function formatarImagensNaoAnalisadas(dados: DadosAnonimizados): string {
  const imgs = (dados.imagens || []) as any[];
  const naoAnalisados = imgs.filter((i) => i && !i.analisadoPorIA);
  if (naoAnalisados.length === 0) return "";

  // Achata quebras de linha + TRUNCA todo campo livre (mesmo teto do laudo) —
  // bound de custo E de janela de exposição de texto livre (consistência com o
  // truncamento de analiseCompleta em formatarAchadosImagem).
  const umaLinha = (s: any, max = 600) => {
    const t = String(s).replace(/\s+/g, " ").trim();
    return t.length > max ? t.slice(0, max) + "…" : t;
  };
  const linhas = naoAnalisados.slice(0, 12).map((i) => {
    const partes: string[] = [
      `- ${i.tipo || "Exame de imagem"}${i.data ? ` (${i.data})` : ""}`,
    ];
    if (i.indicacaoClinica)
      partes.push(`indicação: ${umaLinha(i.indicacaoClinica, 200)}`);
    if (i.descricao) partes.push(`descrição: ${umaLinha(i.descricao)}`);
    if (i.laudo) partes.push(`laudo: ${umaLinha(i.laudo)}`);
    return partes.join(" | ");
  });

  const omitidos =
    naoAnalisados.length > 12
      ? `\n(+${
          naoAnalisados.length - 12
        } exame(s) não analisado(s) mais antigo(s) omitido(s))`
      : "";

  return `
EXAMES DE IMAGEM AINDA NÃO ANALISADOS PELA IA (existem no prontuário; considerar o laudo/descrição abaixo. Ao precisar destes dados, REVISAR o exame existente — NÃO solicitar novo):
${linhas.join("\n")}${omitidos}
`;
}

/**
 * Formata marcadores tumorais a partir do array de marcadores
 */
export function formatarMarcadores(dados: DadosAnonimizados): string {
  // B284-FU2 (AI_123): debug console.log removido — despejava os valores dos
  // marcadores (conteúdo clínico) no console (higiene LGPD, molde B196).
  const marcadores: string[] = [];

  if (dados.marcadores && dados.marcadores.length > 0) {
    // Cast para any para acessar campos dinâmicos do Firebase
    const marcadoresRaw = dados.marcadores as any[];

    // B284-FU2 (AI_123): série dos marcadores tumorais (até 3 coletas) p/ a IA
    // avaliar CINÉTICA. Antes só o ÚLTIMO valor ia (só o AFP tinha tendência),
    // então CA 125 523→378 (queda, que reforça origem benigna) virava "valor
    // único" e a IA não conseguia traçar a tendência. A rede de fatos
    // verificados já ancora TODAS as coletas → a série não gera falso positivo.
    const serieMarcadorTumoral = (
      getValor: (m: any) => any,
      label: string,
      unidade: string
    ): string | null => {
      const agora = new Date().getTime();
      const limiteMax = agora + 365 * 24 * 60 * 60 * 1000;
      const pontos = marcadoresRaw
        .filter((m) => {
          const v = getValor(m);
          return v !== undefined && v !== null && v !== "";
        })
        .sort((a, b) => {
          let dA = new Date(a.dataColeta || a.data || "").getTime();
          let dB = new Date(b.dataColeta || b.data || "").getTime();
          if (isNaN(dA) || dA > limiteMax) dA = 0;
          if (isNaN(dB) || dB > limiteMax) dB = 0;
          return dB - dA;
        })
        .slice(0, 3)
        .map((m) => {
          const num = parseFloat(String(getValor(m)).replace(",", ".")) || 0;
          return `${num} ${unidade} (${m.dataColeta || m.data || "data N/A"})`;
        });
      if (pontos.length === 0) return null;
      return pontos.length === 1
        ? `${label}: ${pontos[0]}`
        : `${label} (mais recente → mais antigo): ${pontos.join("; ")}`;
    };

    // B315 (AI_142) — série RAW (preserva o valor original: títulos "1/640",
    // números, qualitativos). Antes os metabólicos/autoanticorpos mostravam só o
    // ÚLTIMO valor → perdia-se a cinética (resposta a quelação no Wilson/
    // hemocromatose, atividade da HAI, resposta a diurético na natriúria). Até 3
    // coletas, mais recente → mais antigo. NÃO usa parseFloat (destruiria títulos).
    const serieMarcadorRaw = (
      campo: string,
      label: string,
      contexto: string,
      unidade = ""
    ): string | null => {
      const agora = new Date().getTime();
      const limiteMax = agora + 365 * 24 * 60 * 60 * 1000;
      const pontos = marcadoresRaw
        .filter(
          (m) => m[campo] !== undefined && m[campo] !== null && m[campo] !== ""
        )
        .sort((a, b) => {
          let dA = new Date(a.dataColeta || a.data || "").getTime();
          let dB = new Date(b.dataColeta || b.data || "").getTime();
          if (isNaN(dA) || dA > limiteMax) dA = 0;
          if (isNaN(dB) || dB > limiteMax) dB = 0;
          return dB - dA;
        })
        .slice(0, 3)
        .map(
          (m) =>
            `${m[campo]}${unidade ? " " + unidade : ""} (${
              m.dataColeta || m.data || "data N/A"
            })`
        );
      if (pontos.length === 0) return null;
      const corpo =
        pontos.length === 1
          ? pontos[0]
          : `${pontos.join("; ")} (mais recente → mais antigo)`;
      return `${label}: ${corpo} [${contexto}]`;
    };

    // Buscar AFP
    const marcadoresComAfp = marcadoresRaw.filter(
      (m) => m.afp !== undefined && m.afp !== null && m.afp !== ""
    );
    if (marcadoresComAfp.length > 0) {
      // Ordenar por data (mais recente primeiro)
      // Inclui validação para datas inválidas ou futuras absurdas
      const agora = new Date().getTime();
      const limiteMaximo = agora + 365 * 24 * 60 * 60 * 1000; // Máximo 1 ano no futuro

      const ordenados = marcadoresComAfp.sort((a, b) => {
        let dataA = new Date(a.dataColeta || a.data || "").getTime();
        let dataB = new Date(b.dataColeta || b.data || "").getTime();

        // Validar datas - se inválida ou absurdamente futura, usar 0 (muito antiga)
        if (isNaN(dataA) || dataA > limiteMaximo) dataA = 0;
        if (isNaN(dataB) || dataB > limiteMaximo) dataB = 0;

        return dataB - dataA;
      });

      const ultimo = ordenados[0];
      // Converter valor: "2,1" -> 2.1
      const afpValor = parseFloat(String(ultimo.afp).replace(",", ".")) || 0;

      let interpretacao = "";
      if (afpValor <= 10)
        interpretacao =
          "(normal - variações dentro desta faixa não são clinicamente significativas)";
      else if (afpValor <= 20)
        interpretacao = "(normal-alto - monitorar tendência)";
      else if (afpValor <= 200)
        interpretacao =
          "(elevação leve - inespecífico, pode ser regeneração hepática)";
      else if (afpValor <= 400)
        interpretacao = "(elevação moderada - suspeito para CHC)";
      else if (afpValor <= 1000)
        interpretacao = "(elevação significativa - alta probabilidade de CHC)";
      else
        interpretacao = "(muito elevado - considerar drop-out se >1000 ng/mL)";

      marcadores.push(`AFP: ${afpValor} ng/mL ${interpretacao}`);
      marcadores.push(`   Data: ${ultimo.dataColeta || ultimo.data || "N/A"}`);

      // Calcular variação se houver mais de um valor
      if (ordenados.length > 1) {
        const anterior = ordenados[1];
        const afpAnterior =
          parseFloat(String(anterior.afp).replace(",", ".")) || 0;
        if (afpAnterior > 0) {
          const variacao = (
            ((afpValor - afpAnterior) / afpAnterior) *
            100
          ).toFixed(1);
          const variacaoNum = parseFloat(variacao);

          // Só destacar variação se for clinicamente relevante
          // Variações dentro da faixa normal (<10 ng/mL) são menos significativas
          let significancia = "";
          if (afpValor <= 10 && afpAnterior <= 10) {
            significancia =
              " (variação dentro da normalidade - pouco significativa)";
          } else if (variacaoNum > 50 && afpValor > 20) {
            significancia = " (⚠️ elevação significativa - monitorar de perto)";
          } else if (variacaoNum > 100 && afpValor > 100) {
            significancia =
              " (🚨 duplicação - alto risco de progressão tumoral)";
          }

          marcadores.push(
            `   Variação: ${variacao}%${significancia} (anterior: ${afpAnterior} em ${
              anterior.dataColeta || anterior.data || "N/A"
            })`
          );
        }
      }
    }

    // Marcadores tumorais — série (até 3 coletas) p/ a IA ler a cinética.
    // B284-FU (AI_123): CA 125 e PSA não chegavam à IA (formatador os omitia);
    // B284-FU2: além do último valor, manda a série (antes só AFP tinha tendência).
    const ca199Linha = serieMarcadorTumoral(
      (m) => m.ca199 || m["ca19-9"],
      "CA 19-9",
      "U/mL"
    );
    if (ca199Linha) marcadores.push(ca199Linha);

    const ceaLinha = serieMarcadorTumoral((m) => m.cea, "CEA", "ng/mL");
    if (ceaLinha) marcadores.push(ceaLinha);

    const ca125Linha = serieMarcadorTumoral((m) => m.ca125, "CA 125", "U/mL");
    if (ca125Linha) marcadores.push(ca125Linha);

    const psaLinha = serieMarcadorTumoral((m) => m.psa, "PSA total", "ng/mL");
    if (psaLinha) marcadores.push(psaLinha);

    // ═══════════════════════════════════════════
    // 🧬 METABÓLICOS / IMUNOLÓGICOS (etiologia: Wilson, A1AT, HAI/IgG4) — B195
    // ═══════════════════════════════════════════
    const metabolicosImuno: string[] = [];
    const camposMetabImuno: {
      campo: string;
      label: string;
      unidade: string;
      contexto: string;
    }[] = [
      // Metabolismo do ferro (B241 fase 2 — AI_109)
      {
        campo: "ferro",
        label: "Ferro sérico",
        unidade: "µg/dL",
        contexto: "metabolismo do ferro",
      },
      {
        campo: "ferritina",
        label: "Ferritina",
        unidade: "ng/mL",
        contexto: "sobrecarga (fase aguda: eleva em inflamação)",
      },
      {
        campo: "saturacaoTransferrina",
        label: "Saturação de transferrina",
        unidade: "%",
        contexto: "hemocromatose — >45% sugere sobrecarga",
      },
      {
        campo: "cobreSerico",
        label: "Cobre sérico",
        unidade: "µg/dL",
        contexto: "Wilson",
      },
      {
        campo: "cobreUrinario",
        label: "Cobre urinário (24h)",
        unidade: "µg/24h",
        contexto: "Wilson",
      },
      {
        campo: "ceruloplasmina",
        label: "Ceruloplasmina",
        unidade: "mg/dL",
        contexto: "Wilson — baixa é sugestiva",
      },
      {
        campo: "alfa1Antitripsina",
        label: "Alfa-1 antitripsina",
        unidade: "mg/dL",
        contexto: "deficiência de A1AT",
      },
      // Função renal / urina 24h (B279 — AI_120)
      {
        campo: "clearanceCreatinina",
        label: "Clearance de creatinina (total)",
        unidade: "mL/min",
        contexto: "função renal — depuração absoluta",
      },
      {
        campo: "clearanceCreatininaCorrigido",
        label: "Clearance de creatinina (corrigido 1,73m²)",
        unidade: "mL/min/1,73m²",
        contexto: "função renal — <60 sugere DRC",
      },
      {
        campo: "proteinuria24h",
        label: "Proteinúria de 24h",
        unidade: "mg/24h",
        contexto: "lesão renal — normal <150",
      },
      {
        campo: "sodioUrinarioAmostra",
        label: "Sódio urinário (amostra isolada)",
        unidade: "mEq/L",
        contexto: "natriurese — <20 sugere retenção ávida de sódio",
      },
      {
        campo: "sodioUrinario24h",
        label: "Sódio urinário (24h)",
        unidade: "mEq/24h",
        contexto: "resposta a diurético — >78 indica natriurese adequada",
      },
      {
        campo: "iggSerico",
        label: "IgG sérico",
        unidade: "mg/dL",
        contexto: "HAI — elevada apoia",
      },
      {
        campo: "igg4",
        label: "IgG4",
        unidade: "mg/dL",
        contexto: "doença IgG4-relacionada",
      },
      {
        campo: "efpAlbumina",
        label: "EFP — Albumina",
        unidade: "g/dL",
        contexto: "proteinograma",
      },
      {
        campo: "efpAlfa1",
        label: "EFP — Globulina α1",
        unidade: "g/dL",
        contexto: "proteinograma",
      },
      {
        campo: "efpAlfa2",
        label: "EFP — Globulina α2",
        unidade: "g/dL",
        contexto: "proteinograma",
      },
      {
        campo: "efpBeta",
        label: "EFP — Globulina β",
        unidade: "g/dL",
        contexto: "proteinograma (fusão β-γ sugere cirrose)",
      },
      {
        campo: "efpGama",
        label: "EFP — Gamaglobulina",
        unidade: "g/dL",
        contexto: "hipergama = autoimune/cirrose",
      },
    ];

    for (const { campo, label, unidade, contexto } of camposMetabImuno) {
      // B315 (AI_142): série (até 3) em vez de só o último valor → cinética.
      const linha = serieMarcadorRaw(campo, label, contexto, unidade);
      if (linha) metabolicosImuno.push(linha);
    }

    if (metabolicosImuno.length > 0) {
      marcadores.push(""); // linha em branco separadora
      marcadores.push("METABÓLICOS / IMUNOLÓGICOS:");
      marcadores.push(...metabolicosImuno);
    }

    // ═══════════════════════════════════════════
    // 🔬 AUTOANTICORPOS (HAI, CBP, CEP, overlap)
    // ═══════════════════════════════════════════
    const autoanticorpos: string[] = [];
    const camposAuto: { campo: string; label: string; contexto: string }[] = [
      { campo: "fan", label: "FAN/ANA", contexto: "HAI tipo 1, lúpus" },
      {
        campo: "antiMusculoLiso",
        label: "Anti-Músculo Liso (ASMA)",
        contexto: "HAI tipo 1",
      },
      { campo: "antiLKM", label: "Anti-LKM1", contexto: "HAI tipo 2" },
      {
        campo: "antiMitocondria",
        label: "Anti-Mitocôndria (AMA)",
        contexto: "CBP — patognomônico",
      },
      { campo: "anca", label: "pANCA", contexto: "CEP, colite ulcerativa" },
      { campo: "antiDNA", label: "Anti-DNA", contexto: "Lúpus, overlap" },
      {
        campo: "antiTransglutaminase",
        label: "Anti-Transglutaminase",
        contexto: "Doença celíaca",
      },
    ];

    for (const { campo, label, contexto } of camposAuto) {
      // B315 (AI_142): série (até 3) preservando o TÍTULO (ex.: 1/40→1/160→1/320)
      // → a IA lê a dinâmica de atividade (HAI/CBP/CEP), não só o snapshot final.
      const linha = serieMarcadorRaw(campo, label, contexto);
      if (linha) autoanticorpos.push(linha);
    }

    if (autoanticorpos.length > 0) {
      marcadores.push(""); // linha em branco separadora
      marcadores.push("AUTOANTICORPOS:");
      marcadores.push(...autoanticorpos);
    }
  }

  return marcadores.length > 0
    ? `MARCADORES SÉRICOS (TUMORAIS, METABÓLICOS/IMUNOLÓGICOS E AUTOANTICORPOS):\n${marcadores.join(
        "\n"
      )}`
    : "MARCADORES SÉRICOS: Não disponíveis";
}
// =============================================================================
// FUNÇÃO GERAR DIRETRIZES CONTEXTUAIS - ATUALIZADA v4.2.0
// =============================================================================

/**
 * 🦠 FORMATAR SOROLOGIAS (B241 fase 2 — AI_109)
 * Renderiza o status sorológico/viral por coleta. Antes da AI_109 as sorologias
 * NÃO chegavam à IA (anonimização legada quebrada). São cruciais p/ elegibilidade,
 * profilaxia (ex.: HBsAg+/anti-HBc → reativação VHB) e risco infeccioso pós-Tx.
 */
export function formatarSorologias(dados: DadosAnonimizados): string {
  const soros = (dados.sorologias || []) as any[];
  if (!soros.length) return "SOROLOGIAS: Nenhuma registrada.";

  const LABELS: Record<string, string> = {
    hbsag: "HBsAg",
    antiHBs: "Anti-HBs",
    antiHBcIgG: "Anti-HBc IgG/total",
    antiHBcIgM: "Anti-HBc IgM",
    hbeag: "HBeAg",
    antiHBe: "Anti-HBe",
    genotipagemVHB: "Genótipo VHB",
    pcrVHB: "PCR/DNA VHB",
    antiHCV: "Anti-HCV",
    hcvRNA: "HCV-RNA",
    antiHAVIgG: "Anti-HAV IgG/total",
    antiHAVIgM: "Anti-HAV IgM",
    antiHDV: "Anti-HDV",
    pcrVHD: "PCR VHD",
    antiHEV: "Anti-HEV",
    cmvIgG: "CMV IgG",
    cmvIgM: "CMV IgM",
    cmvPCR: "CMV-PCR",
    ebvIgG: "EBV IgG",
    ebvIgM: "EBV IgM",
    ebvPCR: "EBV-PCR",
    rubeolaIgG: "Rubéola IgG",
    rubeolaIgM: "Rubéola IgM",
    antiHIV: "Anti-HIV",
    vdrl: "VDRL/RPR",
    ftaAbs: "FTA-ABS",
    toxoIgG: "Toxo IgG",
    toxoIgM: "Toxo IgM",
    chagas: "Chagas (geral)",
    chagasIgG: "Chagas IgG",
    chagasIgM: "Chagas IgM",
    htlv: "HTLV I/II",
    estrongiloidesIgG: "Estrongiloides IgG",
    igraTB: "IGRA/Quantiferon-TB",
    ppd: "PPD (mm)",
  };

  const ordenadas = [...soros].sort(
    (a, b) =>
      (new Date(b.dataColeta || b.data || "").getTime() || 0) -
      (new Date(a.dataColeta || a.data || "").getTime() || 0)
  );

  const linhas: string[] = [];
  for (const s of ordenadas) {
    const data = s.dataColeta || s.data || "N/A";
    const parts: string[] = [];
    for (const campo of Object.keys(LABELS)) {
      const v = s[campo];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        parts.push(`${LABELS[campo]}: ${v}`);
      }
    }
    if (parts.length) linhas.push(`- ${data}: ${parts.join("; ")}`);
  }

  if (!linhas.length) return "SOROLOGIAS: Nenhuma registrada.";
  return `SOROLOGIAS (status viral/infeccioso — elegibilidade, profilaxia e risco pós-Tx):\n${linhas.join(
    "\n"
  )}`;
}

/**
 * 📋 FORMATAR AVALIAÇÃO CLÍNICA
 */
export function formatarAvaliacaoClinica(dados: DadosAnonimizados): string {
  if (!dados.avaliacaoClinica) return "";
  const av = dados.avaliacaoClinica;
  const itens: string[] = [];

  if (av.causaCirrose) itens.push(`Causa da cirrose: ${av.causaCirrose}`);
  if (av.ultimaIngestaAlcool) {
    const abst = calcularAbstinencia(av.ultimaIngestaAlcool);
    if (abst)
      itens.push(
        `Abstinência alcoólica: ${abst.texto} (${abst.meses} ${
          abst.meses === 1 ? "mês" : "meses"
        } completos; última ingesta em ${av.ultimaIngestaAlcool})`
      );
  }
  if (av.ascite)
    itens.push(
      `Ascite: SIM${
        av.paracentese
          ? ` (paracentese prévia: SIM${
              av.paracenteseNumero
                ? `, ${av.paracenteseNumero} procedimentos`
                : ""
            }, maior volume retirado: ${
              av.paracenteseVolume
                ? av.paracenteseVolume + " litros"
                : "não informado"
            })`
          : ""
      }`
    );
  if (av.hidrotorax)
    itens.push(
      `Hidrotórax hepático: SIM (${
        av.hidrotoraxSessoes || "??"
      } toracocenteses${
        av.hidrotoraxVolume
          ? `, maior volume retirado: ${av.hidrotoraxVolume} litros`
          : ""
      })`
    );
  if (av.varizesEsofago) itens.push("Varizes esofágicas: SIM");
  if (av.varizesGastrica) itens.push("Varizes gástricas: SIM");
  if (av.hdaPrevia)
    itens.push(`HDA prévia: SIM (${av.hdaEpisodios || "??"} episódios)`);
  if (av.ligaduraElastica)
    itens.push(
      `Ligadura elástica: SIM (${av.ligaduraSessoes || "??"} sessões)`
    );
  if (av.encefalopatia)
    itens.push(
      `Encefalopatia hepática: SIM (grau ${av.encefalopatiaGrau || "??"}, ${
        av.encefalopatiaEpisodios || "??"
      } episódios)`
    );
  if (av.pbe) itens.push("PBE: SIM");
  if (av.empiemaBacterianoEspontaneo)
    itens.push("Empiema pleural bacteriano espontâneo: SIM");
  if (av.colangiteRepeticao)
    itens.push(
      `Colangite bacteriana de repetição: SIM (${
        av.colangiteEpisodios || "??"
      } episódios)`
    );
  if (av.colangioTransparietoHepatica)
    itens.push(
      `Colangiografia/drenagem trans-parieto-hepática (CTPH): SIM${
        av.colangioTransparietoHepaticaData
          ? ` (${av.colangioTransparietoHepaticaData})`
          : ""
      }`
    );
  if (av.shr) itens.push(`SHR: SIM (tipo ${av.shrTipo || "??"})`);
  if (av.dialise)
    itens.push(
      `Terapia de substituição renal (diálise): SIM${
        av.dialiseModalidade || av.dialiseDesde
          ? ` (${[
              av.dialiseModalidade,
              av.dialiseDesde ? `desde ${av.dialiseDesde}` : "",
            ]
              .filter(Boolean)
              .join(", ")})`
          : ""
      }`
    );
  if (av.hipertensaoPortopulmonar)
    itens.push(
      `Hipertensão portopulmonar: SIM${
        av.hipertensaoPortopulmonarGrau
          ? ` (grau ${av.hipertensaoPortopulmonarGrau})`
          : ""
      }`
    );
  if (av.shdHepatopulmonar) itens.push("Síndrome hepatopulmonar: SIM");
  if (av.pruridoIntratavel) itens.push("Prurido intratável: SIM");
  if (av.insuficienciaHepaticaAguda)
    itens.push("Insuficiência hepática aguda (fulminante): SIM");
  if (av.aclf)
    itens.push(
      `ACLF (agudização sobre cirrose): SIM${
        av.aclfGrau ? ` (grau ${av.aclfGrau})` : ""
      }`
    );
  if (av.tips)
    itens.push(`TIPS: SIM${av.tipsData ? ` (${av.tipsData})` : ""}`);
  // B315 (AI_142) — diagnóstico ESTRUTURADO do CHC (B248/B249): antes só a
  // terapia prévia aparecia. Agora o próprio CHC (Milão/BCLC/data) chega à IA.
  if (av.hepatocarcinoma) {
    const chc: string[] = [];
    if (av.hccDataDiagnostico) chc.push(`diagnóstico em ${av.hccDataDiagnostico}`);
    if (av.hccMilao) chc.push(`Milão: ${av.hccMilao}`);
    if (av.hccBCLC) chc.push(`BCLC: ${av.hccBCLC}`);
    itens.push(
      `Hepatocarcinoma (CHC): SIM${chc.length ? ` (${chc.join("; ")})` : ""}`
    );
  }
  if (av.hepatocarcinoma && av.hccTerapiaLocorregional)
    itens.push(
      `Terapia prévia do CHC: SIM${
        av.hccTerapiaLocorregionalTipos &&
        av.hccTerapiaLocorregionalTipos.length
          ? ` (${av.hccTerapiaLocorregionalTipos.join(", ")})`
          : ""
      }${av.hccTerapiaIntencao ? ` — intenção: ${av.hccTerapiaIntencao}` : ""}`
    );
  if (av.sarcopenia)
    itens.push(
      `Sarcopenia: SIM${
        av.lfiScore
          ? ` (LFI: ${av.lfiScore} - ${av.lfiClassificacao || ""})`
          : ""
      }`
    );

  if (itens.length === 0) return "";

  return `
AVALIAÇÃO CLÍNICA PRÉ-TRANSPLANTE:
${itens.join("\n")}
`;
}

/**
 * 🏥 FORMATAR DADOS DO TRANSPLANTE
 */
export function formatarDadosTransplante(dados: DadosAnonimizados): string {
  if (!dados.dadosTransplante || dados.dadosTransplante.length === 0) return "";

  const partes: string[] = [];
  dados.dadosTransplante.forEach((tx, i) => {
    const titulo =
      dados.dadosTransplante!.length > 1
        ? `${i + 1}º Transplante:`
        : "Transplante:";
    const itens: string[] = [];
    if (tx.dataTransplante) itens.push(`Data: ${tx.dataTransplante}`);
    if (tx.motivoRetx) itens.push(`Motivo ReTx: ${tx.motivoRetx}`);
    if (tx.equipeCirurgica) {
      const eq = tx.equipeCirurgica;
      if (eq.tipoDoador) itens.push(`Tipo doador: ${eq.tipoDoador}`);
      if (eq.tempoIsquemiaFria)
        itens.push(`Tempo isquemia fria: ${eq.tempoIsquemiaFria}`);
      if (eq.tecnicaOperatoria) itens.push(`Técnica: ${eq.tecnicaOperatoria}`);
    }
    if (tx.posOperatorio) {
      const po = tx.posOperatorio;
      if (po.complicacoes)
        itens.push(`Complicações pós-op: ${po.complicacoes}`);
      if (po.reintervencao) itens.push(`Reintervenção: ${po.reintervencao}`);
    }
    partes.push(`${titulo}\n${itens.join("\n")}`);
  });

  return `
DADOS DO TRANSPLANTE:
${partes.join("\n\n")}
`;
}

/**
 * 📝 FORMATAR INSCRIÇÃO NO TRANSPLANTE
 */
export function formatarInscricaoTx(dados: DadosAnonimizados): string {
  if (!dados.inscricaoTx) return "";
  const insc = dados.inscricaoTx;
  const itens: string[] = [];

  if (insc.statusInscricao) itens.push(`Status: ${insc.statusInscricao}`);
  if (insc.dataInscricao) itens.push(`Data inscrição: ${insc.dataInscricao}`);
  // B360 (AI_156): numeroRGCT é um MARCADOR protegido (o sistema insere o número).
  if (insc.numeroRGCT)
    itens.push(
      `RGCT: ${insc.numeroRGCT} (identificador protegido — ao citar o RGCT, copie este código exatamente; o sistema insere o número)`
    );
  if (insc.situacaoEspecial)
    itens.push(`Situação especial: SIM (${insc.tipoSE || ""})`);
  if (insc.diagnosticoBase)
    itens.push(`Diagnóstico base: ${insc.diagnosticoBase}`);

  if (itens.length === 0) return "";

  return `
INSCRIÇÃO EM LISTA DE TRANSPLANTE:
${itens.join("\n")}
`;
}

/**
 * 💊 FORMATAR PRESCRIÇÕES RECENTES
 */
export function formatarPrescricoes(dados: DadosAnonimizados): string {
  if (!dados.prescricoes || dados.prescricoes.length === 0) return "";

  // B315-Fase3 (AI_142): considerar TODOS os tipos de receituário (comum +
  // controle + antimicrobiano — todos guardam meds em conteudo.medicamentos) e
  // SOMAR os receituários recentes. Antes só o ÚLTIMO era lido → meds em receitas
  // separadas (ex.: imunossupressor numa, antibiótico noutra) ficavam invisíveis
  // e a IA podia recomendar algo já prescrito (a regra "verificar se já está em
  // uso" operava sobre visão parcial).
  const TIPOS_RECEITUARIO = [
    "receituario",
    "receituario-controle",
    "receituario-antimicrobiano",
    // B356 (AI_154) — RCE eletrônica (1 controlado por documento) e GLP-1 (B350):
    // sem eles, a IA deixaria de ver esses medicamentos já prescritos.
    "receituario-controle-eletronica",
    "receituario-glp1",
  ];
  const receituarios = dados.prescricoes
    .filter((p) => TIPOS_RECEITUARIO.includes(p.tipo))
    // Ordena explicitamente do mais recente ao mais antigo (não confia só na
    // ordem de chegada) — o dedup abaixo depende disso p/ "a mais recente vence".
    .sort((a, b) =>
      String(b.data || "").localeCompare(String(a.data || ""))
    );
  const exames = dados.prescricoes.filter((p) => p.tipo === "exames");

  const partes: string[] = [];

  // Normaliza o nome p/ dedup (acento-insensível + case-insensível): "Ácido
  // fólico" e "Acido folico" são o mesmo medicamento. Mesmo padrão NFD do resto
  // do projeto (detectorContraditorios, gerarPromptMarcadores).
  const chaveMed = (nome: string) =>
    String(nome || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  // Achata quebras de linha (nome/posologia de campo livre) p/ não estourar o
  // formato de uma linha por medicamento no prompt.
  const umaLinha = (s: any) => String(s).replace(/\s+/g, " ").trim();

  if (receituarios.length > 0) {
    const vistos = new Set<string>();
    const meds: string[] = [];
    for (const r of receituarios) {
      const lista: any[] = r.conteudo?.medicamentos || [];
      for (const m of lista) {
        const chave = chaveMed(m?.nome);
        if (!chave || vistos.has(chave)) continue;
        vistos.add(chave);
        const nome = umaLinha(m.nome);
        const posologia = m?.posologia
          ? umaLinha(m.posologia)
          : "(posologia não informada)";
        const data = r.data ? ` (receita de ${String(r.data).slice(0, 10)})` : "";
        meds.push(`  - ${nome}: ${posologia}${data}`);
      }
    }
    if (meds.length > 0) {
      partes.push(
        "Medicamentos prescritos recentemente (somados de todos os receituários):"
      );
      partes.push(...meds);
    }
  }

  if (exames.length > 0) {
    partes.push(`Pedidos de exames recentes: ${exames.length}`);
  }

  if (partes.length === 0) return "";

  return `
PRESCRIÇÕES RECENTES:
${partes.join("\n")}
`;
}
/**
 * 🎯 Gera diretrizes clínicas contextuais baseadas no diagnóstico do paciente
 * Inclui todos os novos guidelines: Wilson, CCA, Transplante, PFIC, HBV 2025, Doenças Císticas
 */
export function gerarDiretrizesContextuais(dados: DadosAnonimizados): string {
  const diretrizes: string[] = [];
  const diagnostico = (dados.diagnosticoBase || "").toLowerCase();
  // Construir contexto a partir dos campos disponíveis
  // Construir contexto a partir dos campos disponíveis
  const contextoAdicional = [
    dados.transplante?.tipoTransplante || "",
    dados.transplante?.indicacao || "",
  ]
    .join(" ")
    .toLowerCase();

  const contexto = `${diagnostico} ${contextoAdicional}`;

  // =========================================================================
  // CIRROSE E COMPLICAÇÕES
  // =========================================================================

  if (contexto.includes("cirrose") || contexto.includes("cirrótico")) {
    diretrizes.push(`
DIRETRIZES CIRROSE (AASLD/EASL):
- Classificação: Child-Pugh (A/B/C) e MELD para prognóstico
- Vigilância CHC: US + AFP cada 6 meses
- Prevenção varizes: EDA se plaquetas <150.000 ou elastografia >20 kPa
- Profilaxia PBE: Considerar se proteína ascite <1.5 g/dL
- Nutrição: 35 kcal/kg/dia, 1.2-1.5g proteína/kg/dia
- Vacinação: Hepatite A, B, pneumococo, influenza
`);
  }

  // =========================================================================
  // HEPATOCARCINOMA (CHC)
  // =========================================================================

  if (
    contexto.includes("hepatocarcinoma") ||
    contexto.includes("chc") ||
    contexto.includes("carcinoma hepatocelular") ||
    contexto.includes("hcc")
  ) {
    diretrizes.push(`
DIRETRIZES CHC (AASLD 2023 / BCLC):
- Estadiamento: BCLC (0, A, B, C, D)
- Critérios de Milão: Lesão única ≤5cm OU até 3 lesões ≤3cm, sem invasão vascular/metástases
- AFP-L3 e DCP: Marcadores complementares para diagnóstico e prognóstico
- Tratamento por estádio:
  * BCLC 0/A: Ressecção, ablação ou transplante
  * BCLC B: TACE, TARE
  * BCLC C: Sistêmico (Atezolizumab/Bevacizumab 1ª linha)
  * BCLC D: Suporte/paliativo
- Vigilância pós-tratamento: TC/RM cada 3-4 meses no 1º ano
`);
  }

  // =========================================================================
  // DOENÇA DE WILSON (NOVO - AASLD 2022)
  // =========================================================================

  if (
    contexto.includes("wilson") ||
    contexto.includes("ceruloplasmina") ||
    contexto.includes("kayser-fleischer") ||
    contexto.includes("atp7b")
  ) {
    diretrizes.push(`
DIRETRIZES DOENÇA DE WILSON (AASLD 2022):
- Diagnóstico: Score de Leipzig ≥4 pontos estabelece diagnóstico
  * Ceruloplasmina <10 mg/dL: altamente sugestivo
  * Cobre urinário 24h >100 mcg: sugestivo
  * Anel de Kayser-Fleischer: presente em 95% forma neurológica
  * Teste genético ATP7B: 2 mutações patogênicas = diagnóstico
- Tratamento inicial:
  * Trientina 750-1500mg/dia OU D-penicilamina 750-1500mg/dia
  * Trientina preferida se sintomas neurológicos (menor risco de piora)
- Tratamento manutenção:
  * Zinco 150mg elementar/dia dividido em 3 doses OU quelante dose reduzida
- Alvos de monitoramento:
  * Cobre urinário 24h: 200-500 mcg (quelante) ou <75 mcg (zinco)
  * Cobre não-ceruloplasmina: 5-15 mcg/dL
- Score NWI ≥11: Alta mortalidade sem transplante - considerar listagem urgente
- Screening familiar: Obrigatório para parentes de 1º grau
- Gravidez: Zinco preferido; se quelante, reduzir dose 25-50% no 3º trimestre
`);
  }

  // =========================================================================
  // COLANGIOCARCINOMA (NOVO - EASL 2023/2025)
  // =========================================================================

  if (
    contexto.includes("colangiocarcinoma") ||
    contexto.includes("cca") ||
    contexto.includes("klatskin") ||
    contexto.includes("tumor biliar") ||
    contexto.includes("icca") ||
    contexto.includes("pcca") ||
    contexto.includes("dcca")
  ) {
    diretrizes.push(`
DIRETRIZES COLANGIOCARCINOMA (EASL 2023-2025):
- Classificação anatômica (tripartida recomendada):
  * iCCA: Intra-hepático (proximal aos ductos de 2ª ordem)
  * pCCA: Peri-hilar/Klatskin (entre 2ª ordem e cístico)
  * dCCA: Distal (abaixo do cístico até ampola)
- Marcadores: CA 19-9 (corte >129 U/mL em PSC; limitado em Lewis-negativos)
- Perfil molecular - SOLICITAR NGS EM DOENÇA AVANÇADA:
  * FGFR2 fusion (10-20% iCCA): Pemigatinib, Futibatinib
  * IDH1 mutation (10-20% iCCA): Ivosidenib
  * MSI-H (1-4%): Pembrolizumab
  * HER2 amplification: T-DXd em estudo
  * BRAF V600E: Dabrafenib + Trametinib
- Tratamento:
  * Ressecável: Cirurgia + Capecitabina adjuvante (BILCAP)
  * Irressecável 1ª linha: Gemcitabina + Cisplatina + Durvalumab (TOPAZ-1)
  * pCCA selecionado: Protocolo Mayo (neoadjuvância + Tx) em centros especializados
- Vigilância em PSC: ColangioRM + CA 19-9 anual
`);
  }

  // =========================================================================
  // COLANGITE ESCLEROSANTE PRIMÁRIA (PSC)
  // =========================================================================

  if (contexto.includes("psc") || contexto.includes("colangite esclerosante")) {
    diretrizes.push(`
DIRETRIZES PSC (AASLD 2023):
- Diagnóstico: ColangioRM (múltiplas estenoses e dilatações)
- Associações: DII (especialmente colite ulcerativa) em 70-80%
- Sem tratamento medicamentoso aprovado (UDCA controverso)
- Vigilância:
  * Colonoscopia anual se DII
  * ColangioRM + CA 19-9 anual para vigilância de CCA
  * US + AFP cada 6 meses se cirrose
- Estenose dominante: CPRE + escovado com citologia ± FISH
- Transplante: Indicado para cirrose descompensada ou CCA selecionado (protocolo Mayo)
- Risco CCA: 5-10% ao longo da vida
`);
  }

  // =========================================================================
  // COLANGITE BILIAR PRIMÁRIA (PBC)
  // =========================================================================

  if (
    contexto.includes("pbc") ||
    contexto.includes("colangite biliar primária") ||
    contexto.includes("cirrose biliar primária")
  ) {
    diretrizes.push(`
DIRETRIZES PBC (AASLD 2021):
- Diagnóstico: AMA+ (ou AMA-M2) + FA elevada + histologia compatível
- Tratamento 1ª linha: UDCA 13-15 mg/kg/dia
- Resposta UDCA (critérios Paris II): FA <1.5x e BT <1 mg/dL após 1 ano
- Não-respondedores:
  * Ácido obeticólico 5-10mg/dia (evitar se cirrose descompensada)
  * Bezafibrato 400mg/dia (off-label, evidência crescente)
- Prurido: Colestiramina → Rifampicina → Naltrexona → Sertralina
- Vigilância osteoporose: DEXA a cada 2-3 anos
- Transplante: Se cirrose descompensada ou prurido intratável
`);
  }

  // =========================================================================
  // HEPATITE AUTOIMUNE (AIH)
  // =========================================================================

  if (
    contexto.includes("hepatite autoimune") ||
    contexto.includes("aih") ||
    contexto.includes("hepatite auto-imune")
  ) {
    diretrizes.push(`
DIRETRIZES HEPATITE AUTOIMUNE (AASLD 2019):
- Diagnóstico: Score simplificado AIH ≥7 = definitivo
- Indução: Prednisona 0.5-1 mg/kg/dia (max 60mg) + Azatioprina 1-2 mg/kg/dia
- Manutenção: Azatioprina 1-2 mg/kg ± prednisona baixa dose
- Alvo: Normalização ALT e IgG
- Monitorar TPMT antes de azatioprina
- Duração mínima: 2-3 anos; tentativa de retirada apenas se remissão completa sustentada
- Recaída: 50-90% após suspensão; considerar tratamento indefinido
- Refratário: MMF, tacrolimus, ciclosporina
`);
  }

  // =========================================================================
  // HEPATITE B CRÔNICA (ATUALIZADO - AASLD 2025)
  // =========================================================================

  if (
    contexto.includes("hepatite b") ||
    contexto.includes("hbv") ||
    contexto.includes("hbsag")
  ) {
    diretrizes.push(`
DIRETRIZES HEPATITE B (AASLD 2025 UPDATE):
- Tratamento indicado se:
  * Cirrose (qualquer DNA ou ALT)
  * HBeAg+ com DNA >20.000 + ALT >2x LSN
  * HBeAg- com DNA >2.000 + ALT >2x LSN
  * Manifestações extra-hepáticas
  * Coinfecção HIV, HCV ou HDV
  * Imunossupressão planejada
- Primeira linha: TAF 25mg/dia (preferido) ou TDF 300mg ou Entecavir 0.5mg
  * TAF preferido se: >60 anos, DRC, osteoporose, uso de outros nefrotóxicos
  * TDF preferido em: Gestação (mais dados de segurança)
- Duração: Indefinida na maioria; considerar suspensão apenas se perda HBsAg
- Vigilância CHC: US ± AFP cada 6 meses se cirrose ou alto risco
- Gravidez: TDF se DNA >200.000 no 3º trimestre + HBIG/vacina ao RN
- Coinfecção HDV: PegIFN ± Bulevirtide (aprovado EMA)
`);
  }

  // =========================================================================
  // HEPATITE C
  // =========================================================================

  if (contexto.includes("hepatite c") || contexto.includes("hcv")) {
    diretrizes.push(`
DIRETRIZES HEPATITE C (AASLD/IDSA 2023):
- Tratamento: Indicado para TODOS com HCV RNA detectável
- Regimes pan-genotípicos preferidos:
  * SOF/VEL 12 semanas (sem cirrose ou cirrose compensada)
  * GLE/PIB 8 semanas (sem cirrose) ou 12 semanas (cirrose compensada)
- Cirrose descompensada: SOF/VEL ± RBV (Child B/C); NÃO usar inibidores de protease
- SVR12: HCV RNA indetectável 12 semanas pós-tratamento = cura
- Vigilância pós-SVR:
  * Sem cirrose: Alta com orientações
  * Com cirrose: US + AFP cada 6 meses INDEFINIDAMENTE
- Retratamento: SOF/VEL/VOX 12 semanas
`);
  }

  // =========================================================================
  // DOENÇA HEPÁTICA GORDUROSA (MASLD/MASH)
  // =========================================================================

  if (
    contexto.includes("esteatose") ||
    contexto.includes("nash") ||
    contexto.includes("nafld") ||
    contexto.includes("mash") ||
    contexto.includes("masld") ||
    contexto.includes("dhgna")
  ) {
    diretrizes.push(`
DIRETRIZES MASLD/MASH (AASLD 2023):
- Nova nomenclatura: MASLD (metabolic dysfunction-associated steatotic liver disease)
- Avaliação fibrose: FIB-4 → se indeterminado → elastografia ou ELF
- Tratamento:
  * Perda de peso: ≥5% melhora esteatose; ≥10% pode reverter fibrose
  * Resmetirom (aprovado FDA 2024): Para MASH com fibrose F2-F3
  * Semaglutide: Benefício em esteatose e fibrose (estudos em andamento para aprovação)
  * Pioglitazona: Opção se diabetes (melhora histologia MASH)
  * Vitamina E 800 UI/dia: Considerar em MASH sem diabetes
- Comorbidades: Tratar agressivamente DM2, dislipidemia, HAS
- Vigilância CHC: Se cirrose - US + AFP cada 6 meses
`);
  }

  // =========================================================================
  // DOENÇA HEPÁTICA ALCOÓLICA (ALD)
  // =========================================================================

  if (
    contexto.includes("alcoólica") ||
    contexto.includes("alcoolica") ||
    contexto.includes("etílica") ||
    contexto.includes("etilica") ||
    contexto.includes("álcool") ||
    contexto.includes("alcool")
  ) {
    diretrizes.push(`
DIRETRIZES DOENÇA HEPÁTICA ALCOÓLICA (AASLD 2024):
- Abstinência: FUNDAMENTAL - associar com programa de tratamento
- Hepatite alcoólica grave (Maddrey ≥32 ou MELD ≥21):
  * Prednisolona 40mg/dia por 28 dias
  * Contraindicação: Infecção ativa, sangramento GI, IRA
  * Score Lille dia 7: Se ≥0.45 → suspender (não respondedor)
- Nutrição: Essencial - 35-40 kcal/kg/dia, 1.2-1.5g proteína/kg/dia
- Encefalopatia: Lactulose + Rifaximina se refratário
- Transplante: Seleção cuidadosa; regra de 6 meses em revisão
  * Hepatite alcoólica grave não-respondedora: "Early transplant" em centros selecionados
`);
  }

  // =========================================================================
  // ASCITE E SÍNDROME HEPATORRENAL
  // =========================================================================

  if (
    contexto.includes("ascite") ||
    contexto.includes("hepatorrenal") ||
    contexto.includes("shr") ||
    contexto.includes("hrs")
  ) {
    diretrizes.push(`
DIRETRIZES ASCITE E SHR (AASLD 2021):
- Ascite 1ª linha: Restrição sódio (<2g/dia) + Espironolactona 100mg (até 400mg)
- Ascite refratária: Adicionar furosemida (proporção 100:40 espiro:furo)
- Paracentese de grande volume: Repor albumina 8g/L removido se >5L
- Profilaxia PBE:
  * Proteína ascite <1.5 g/dL + Child ≥9 ou creatinina ≥1.2: Norfloxacino 400mg/dia
  * Pós-PBE: Norfloxacino 400mg/dia indefinidamente
- SHR-AKI (critérios ICA-AKI):
  * Suspender diuréticos e nefrotóxicos
  * Albumina 1g/kg (máx 100g) no D1
  * Terlipressina 1mg IV 4-6h (titular até 2mg se não resposta) + Albumina
  * Se sem terlipressina: Noradrenalina + Albumina
- TIPS: Considerar para ascite refratária recorrente
`);
  }

  // =========================================================================
  // ENCEFALOPATIA HEPÁTICA
  // =========================================================================

  if (
    contexto.includes("encefalopatia") ||
    contexto.includes("hepática") ||
    contexto.includes("confusão mental") ||
    contexto.includes("asterixis")
  ) {
    diretrizes.push(`
DIRETRIZES ENCEFALOPATIA HEPÁTICA (AASLD 2014):
- Classificação: West Haven (graus 0-4) ou ISHEN (covert/overt)
- Identificar precipitantes: Infecção, sangramento GI, constipação, hipoK, alcalose
- Tratamento agudo:
  * Lactulose 25mL 2-4x/dia (alvo: 2-3 evacuações pastosas/dia)
  * Rifaximina 550mg 2x/dia se recorrente ou refratário
- Profilaxia secundária: Lactulose + Rifaximina
- Nutrição: NÃO restringir proteína; 1.2-1.5g/kg/dia
- EH pós-TIPS: Rifaximina profilática pode reduzir incidência
- EH recorrente grave: Avaliar redução de TIPS ou transplante
`);
  }

  // =========================================================================
  // HEMORRAGIA VARICOSA
  // =========================================================================

  if (
    contexto.includes("varizes") ||
    contexto.includes("hemorragia") ||
    contexto.includes("sangramento") ||
    contexto.includes("hda")
  ) {
    diretrizes.push(`
DIRETRIZES VARIZES E HEMORRAGIA (BAVENO VII 2022):
- Profilaxia primária:
  * Sem varizes ou pequenas sem red signs: Betabloqueador OU vigilância EDA
  * Varizes médias/grandes: Carvedilol 6.25-12.5mg/dia OU ligadura elástica
- Hemorragia aguda:
  * Ressuscitação volêmica (Hb alvo 7-8 g/dL)
  * Terlipressina 2mg bolus + 1mg 4/4h OU Octreotida 50mcg bolus + 50mcg/h
  * ATB profilático: Ceftriaxona 1g/dia por 5-7 dias
  * EDA em 12h: Ligadura elástica
  * TIPS preemptivo: Child B com sangramento ativo ou Child C (≤13) em 72h
- Profilaxia secundária: Carvedilol + Ligadura seriada
`);
  }

  // =========================================================================
  // TIPS (ATUALIZADO - AASLD 2024)
  // =========================================================================

  if (contexto.includes("tips") || contexto.includes("shunt portossistêmico")) {
    diretrizes.push(`
DIRETRIZES TIPS (AASLD 2024):
- Indicações principais:
  * Ascite refratária recorrente (≥3 paracenteses/mês)
  * Sangramento varicoso refratário
  * TIPS preemptivo: Child B/C com sangramento ativo na EDA (em 72h)
  * Síndrome de Budd-Chiari
- Contraindicações:
  * ICC grave, hipertensão pulmonar grave
  * Encefalopatia recorrente/refratária
  * Child >13, MELD >18 (relativa)
  * Trombose portal extensa (avaliar caso a caso)
- Profilaxia encefalopatia pós-TIPS: Rifaximina 550mg 2x/dia
- Alvos: Gradiente porto-hepático <12 mmHg
`);
  }

  // =========================================================================
  // TROMBOSE DE VEIA PORTA
  // =========================================================================

  if (
    contexto.includes("trombose") &&
    (contexto.includes("porta") || contexto.includes("portal"))
  ) {
    diretrizes.push(`
DIRETRIZES TROMBOSE PORTAL (AASLD 2020):
- Classificação Yerdel:
  * Grau 1: <50% lúmen, sem extensão mesentérica
  * Grau 2: >50% lúmen, incluindo confluência
  * Grau 3: Trombose completa porta + veia mesentérica proximal
  * Grau 4: Trombose completa porta + mesentérica extensa
- Anticoagulação:
  * Aguda (<6 meses): Anticoagulação plena (HBPM ou Warfarin; DOACs em estudo)
  * Crônica/cavernomatosa: Individualizar; menos benefício de anticoagulação
  * Candidatos a Tx: Anticoagular para manter perviedade
- Duração: Mínimo 6 meses; indefinida se fator de risco persistente ou candidato Tx
- Candidatos a transplante: Essencial tentar recanalizar antes
- Cirrose: Anticoagulação NÃO aumenta sangramento significativamente
`);
  }

  // =========================================================================
  // TROMBOSE PORTAL EM CANDIDATO A TRANSPLANTE (Bhangui 2019, Fernandes 2020)
  // =========================================================================

  if (
    dados.achadosImagem?.some(
      (a) => a.trombosePortal && a.trombosePortal !== "NÃO"
    )
  ) {
    // Extrair classificação Yerdel dos achados
    const achadoTrombose = dados.achadosImagem.find(
      (a) => a.trombosePortal && a.trombosePortal !== "NÃO"
    );
    const yerdel = achadoTrombose?.trombosePortal || "";

    diretrizes.push(`
DIRETRIZES TROMBOSE PORTAL EM TRANSPLANTE (Bhangui 2019, Fernandes 2020):

📌 CLASSIFICAÇÃO ATUAL: ${yerdel || "Não especificada"}

📌 RECONSTRUÇÃO VASCULAR POR GRAU:
- Yerdel 1-2: Trombectomia/tromboendovenectomia + anastomose porto-portal padrão
- Yerdel 3: Jump graft VMS → VP do enxerto (veia ilíaca ou criopreservada)
- Yerdel 4 com shunts: Anastomose reno-portal (preferencial) ou cavo-portal
- Yerdel 4 sem shunts: Arterialização portal ou transplante multivisceral

📌 ANTICOAGULAÇÃO PRÉ-TRANSPLANTE:
- Objetivo: Prevenir progressão e manter patência
- Contraindicações relativas: Plaquetas < 50k, INR > 2.5, varizes não tratadas
- Se plaquetas < 30k: Geralmente contraindicada (risco hemorrágico > benefício)
- Alternativa se contraindicada: TIPS para manter patência

📌 AVALIAÇÃO PRÉ-OPERATÓRIA:
- AngioTC ou AngioRM com reconstrução 3D
- Mapear shunts espontâneos (esplenorrenal, mesocaval, varizes)
- Identificar veias utilizáveis para reconstrução

📌 IMPORTANTE: A reconstrução vascular é realizada pela equipe de transplante.
`);
  }

  // =========================================================================
  // HEMOCROMATOSE
  // =========================================================================

  if (
    contexto.includes("hemocromatose") ||
    contexto.includes("sobrecarga de ferro") ||
    contexto.includes("ferritina")
  ) {
    diretrizes.push(`
DIRETRIZES HEMOCROMATOSE (AASLD 2011):
- Diagnóstico: Saturação transferrina >45% + Ferritina elevada + Genótipo HFE
- Tratamento: Flebotomia 500mL semanal até ferritina 50-100 ng/mL
- Manutenção: Flebotomia a cada 2-4 meses para manter ferritina <100
- Vigilância CHC: Se cirrose - US + AFP cada 6 meses
- Screening familiar: Genotipagem HFE em parentes de 1º grau
- Dieta: Evitar suplementos de ferro e vitamina C em excesso
`);
  }

  // =========================================================================
  // DOENÇAS COLESTÁTICAS GENÉTICAS (NOVO - EASL 2024)
  // =========================================================================

  if (
    contexto.includes("alagille") ||
    contexto.includes("pfic") ||
    contexto.includes("mdr3") ||
    contexto.includes("bsep") ||
    contexto.includes("alfa-1") ||
    contexto.includes("alpha-1") ||
    contexto.includes("deficiência de alfa") ||
    contexto.includes("aatd")
  ) {
    diretrizes.push(`
DIRETRIZES DOENÇAS COLESTÁTICAS GENÉTICAS (EASL 2024):

DEFICIÊNCIA ALFA-1 ANTITRIPSINA:
- Screening: AAT sérico <110 mg/dL → genotipar
- Pi*ZZ: Risco cirrose 20-35% em adultos; vigilância CHC se cirrose
- Tratamento: Não há terapia hepática específica aprovada (reposição é para pulmão)
- Evitar álcool e tabaco (fator de risco para progressão)
- Fazirsiran (siRNA): Em estudo - resultados promissores

SÍNDROME DE ALAGILLE (ALGS):
- Diagnóstico: ≥3 critérios (colestase, cardiopatia, vértebras borboleta, embriotoxon, face típica) OU mutação JAG1/NOTCH2
- Tratamento prurido: Maralixibat (inibidor IBAT) - APROVADO
- Estatinas: NÃO recomendadas (lipoproteína-X não é aterogênica)
- Vigilância vascular: Risco de aneurismas cerebrais

PFIC (Colestase Intra-hepática Familiar Progressiva):
- PFIC1/2: GGT baixo; PFIC3: GGT alto
- PFIC2 (BSEP): Alto risco CHC - vigilância desde diagnóstico
- Tratamento: UDCA (melhor em PFIC3), Odevixibat (inibidor IBAT)
- Derivação biliar parcial: Opção em casos selecionados
- Pós-Tx PFIC2: Risco de aloimunização anti-BSEP
`);
  }

  // =========================================================================
  // DOENÇAS CÍSTICAS DO FÍGADO (NOVO - EASL 2022)
  // =========================================================================

  if (
    contexto.includes("cisto") ||
    contexto.includes("policístic") ||
    contexto.includes("pld") ||
    contexto.includes("caroli")
  ) {
    diretrizes.push(`
DIRETRIZES DOENÇAS CÍSTICAS (EASL 2022):

CISTOS SIMPLES:
- Assintomáticos: Observação, SEM seguimento rotineiro
- Sintomáticos: Aspiração + esclerose OU fenestração laparoscópica
- Características atípicas (septos, componente sólido): Investigar! (cistoadenoma, metástase)

DOENÇA HEPÁTICA POLICÍSTICA (PLD):
- Associada a ADPKD (PKD1/2) ou isolada (PRKCSH, SEC63)
- Evitar estrogênios (aceleram crescimento)
- Tratamento sintomático:
  * Análogos somatostatina: Lanreotida 120mg SC/mês ou Octreotida LAR 40mg IM/mês
  * Redução volume: 3-6%/ano (estabilização)
- Cirurgia: Fenestração para Gigot I; hepatectomia com fenestração para Gigot II/III
- Transplante: Gigot III com sintomas incapacitantes (função hepática geralmente preservada)

DOENÇA DE CAROLI:
- Tipo I: Dilatação ductal segmentar (sacular)
- Tipo II (Síndrome de Caroli): Com fibrose hepática congênita
- Complicações: Colangite recorrente, colelitíase, CCA (7-15%)
- Tratamento: UDCA, ATB para colangite; ressecção se localizado; Tx se difuso
`);
  }

  // =========================================================================
  // PRÉ E PÓS-TRANSPLANTE (EXPANDIDO - EASL 2024)
  // =========================================================================

  if (
    contexto.includes("transplante") ||
    contexto.includes("tx") ||
    contexto.includes("pré-tx") ||
    contexto.includes("pós-tx") ||
    contexto.includes("lista de espera")
  ) {
    diretrizes.push(`
DIRETRIZES TRANSPLANTE HEPÁTICO (EASL 2024 / AASLD 2025):

AVALIAÇÃO PRÉ-TRANSPLANTE:
⚠️ OBRIGATÓRIO (candidato pré-Tx — B253/AI_115): as RECOMENDAÇÕES da análise DEVEM incluir um eixo de "completar avaliação de candidatura pré-Tx" cobrindo os itens abaixo — (1) fragilidade/sarcopenia (atenção à obesidade sarcopênica: IMC alto MASCARA sarcopenia; redução ponderal NÃO substitui a avaliação de massa/função muscular), (2) rastreio infeccioso/sorológico (CMV/EBV, estrongiloidíase, TB latente IGRA/PPD etc.), (3) vacinação pré-Tx (janela do vírus vivo), (4) avaliação psicossocial e de ADESÃO (suporte social, transtornos psiquiátricos/uso de substâncias; adesão é determinante para a imunossupressão pós-Tx — atenção redobrada se houver polifarmácia psiquiátrica). Recomende APENAS o que NÃO constar dos dados ("verificar/completar"; anti-redundância). FORMATO OBRIGATÓRIO (B253-FU/AI_115; B253-FU2/AI_116): cada eixo cujo dado NÃO CONSTAR aparece com a CONDUTA específica nomeada, como recomendação VISÍVEL — é PROIBIDO citar VACINAÇÃO ou PSICOSSOCIAL/ADESÃO como substantivo solto no fim de outro item ou diluídos numa lista. Se usar um cabeçalho de candidatura, cada eixo faltante deve aparecer como SUA PRÓPRIA recomendação/linha visível (uma conduta por linha) — NUNCA vários eixos espremidos numa única frase corrida. NÃO omitir esse eixo só porque o caso tem achados mais salientes.
- Fragilidade: Liver Frailty Index (hand grip, chair stands, balance)
  * >4.4 = frágil → pior prognóstico pós-Tx
- Sarcopenia: SMI em TC L3 (<50 cm²/m² homens, <39 mulheres)
- Cardiopulmonar: Eco em todos; teste funcional se ≥3 fatores risco
- Porto-pulmonar: Se PSAP eco >50 → cateterismo direito
- Hepato-pulmonar: PaO2 <80 → eco com bolhas/cintilografia
- RASTREIO INFECCIOSO PRÉ-Tx (AST IDCOP; B253/AI_115 — SÓ em candidato pré-Tx; VERIFICAR se já feito e recomendar APENAS o que NÃO constar dos dados; frasear como "confirmar se coletado", NUNCA assumir que não foi feito; anti-redundância):
  * Sorologias: HBV (HBsAg/anti-HBc/anti-HBs), HCV (anti-HCV → RNA se reagente), anti-HIV, CMV IgG, EBV IgG, VZV/varicela IgG, Toxoplasmose IgG, VDRL/sífilis
  * Endêmicos no Brasil: HTLV I/II, Chagas (T. cruzi), Estrongiloidíase IgG (risco de hiperinfecção sob imunossupressão)
  * TB latente: IGRA ou PPD + radiografia de tórax
  * O status CMV (e EBV) do RECEPTOR define profilaxia/monitorização pós-Tx — destacar quando ausente
- VACINAÇÃO PRÉ-Tx (AST; B253/AI_115 — SÓ candidato pré-Tx; VERIFICAR carteira e recomendar APENAS o que faltar; "atualizar o que faltar", não assumir; anti-redundância):
  * VÍRUS VIVO — só no pré-Tx (CONTRAINDICADAS após imunossupressão), completar ≥4 semanas antes do Tx: MMR/tríplice viral (sem evidência de imunidade), varicela (anti-VZV negativo), febre amarela (Brasil, área endêmica)
  * NÃO-VIVAS (completar no pré-Tx; podem ser dadas mesmo peri-Tx): hepatite A; hepatite B (checar anti-HBs; esquema reforçado/dose dobrada se não-respondedor); influenza anual; pneumocócica (PPSV23; sequência PCV13/15→PPSV23 conforme idade); dTpa (reforço a cada 10 anos); COVID-19; HPV (até 26 anos); ZOSTER recombinante/Shingrix (≥50 anos — NÃO é vírus vivo)
  * SÓ com fator de risco adicional (asplenia, TCTH etc.): Hib, meningocócica ACWY/B

MACHINE PERFUSION:
- HOPE (hipotérmica): Reduz lesão isquemia-reperfusão em DCD
- NMP (normotérmica): Permite avaliação funcional do enxerto (bile, lactato)
- Indicação: Doadores marginais, DCD, isquemia prolongada

ACLF E TRANSPLANTE:
- ACLF-3: Sobrevida pós-Tx aceitável se seleção cuidadosa
- Contraindicações relativas: Lactato >9, vasopressores crescentes, VM com FiO2 alta
- Janela terapêutica: Estabilização/melhora de falências é pré-requisito

IMUNOSSUPRESSÃO PÓS-TX:
- Indução: Basiliximab 20mg D0 e D4 (padrão)
- Manutenção: Tacrolimus (níveis 8-12 → 5-8 ao longo do tempo) ± MMF
- mTORi (everolimus/sirolimus): Se CHC, nefrotoxicidade, neoplasia de novo

RECORRÊNCIA PÓS-TX:
- HCV: DAA pré ou pós-Tx (SVR >95%)
- HBV: HBIG + análogo indefinidamente
- AIH: 20-40% recorrência; manter imunossupressão adequada
- MASLD: Alta recorrência esteatose; controle metabólico agressivo
`);
  }

  // =========================================================================
  // MARCADORES TUMORAIS
  // =========================================================================

  if (dados.marcadores && dados.marcadores.length > 0) {
    const afpValores = dados.marcadores.filter((m) =>
      m.tipo?.toLowerCase().includes("afp")
    );

    if (afpValores.length > 0) {
      diretrizes.push(`
INTERPRETAÇÃO AFP (AASLD):
- Normal: <10 ng/mL
- Elevação leve (10-200): Pode ocorrer em hepatite ativa, cirrose sem CHC
- Elevação significativa (>200): Alta especificidade para CHC
- Elevação progressiva: Mais preocupante que valor isolado
- AFP-L3 >10%: Aumenta especificidade para CHC
- Limitação: 30-40% dos CHC têm AFP normal
`);
    }
  }

  // =========================================================================
  // SÍNDROME METABÓLICA / COMORBIDADES
  // =========================================================================

  if (
    contexto.includes("diabetes") ||
    contexto.includes("obesidade") ||
    contexto.includes("dislipidemia") ||
    contexto.includes("síndrome metabólica")
  ) {
    diretrizes.push(`
MANEJO COMORBIDADES METABÓLICAS:
- DM2: Metformina (se TFG >30); Semaglutide/GLP-1 RA (benefício em MASLD)
- HAS: IECA/BRA preferidos; evitar betabloqueadores não-seletivos se varizes
- Dislipidemia: Estatinas SEGURAS na cirrose compensada
- Obesidade: Perda ≥10% pode reverter fibrose; considerar cirurgia bariátrica pré-Tx
`);
  }

  // =========================================================================
  // MENSAGEM PADRÃO SE NENHUMA CONDIÇÃO ESPECÍFICA
  // =========================================================================

  if (diretrizes.length === 0) {
    diretrizes.push(`
DIRETRIZES GERAIS DE HEPATOLOGIA:
- Avaliar função hepática: Albumina, bilirrubinas, INR, plaquetas
- Investigar etiologia: Sorologias virais, autoanticorpos, perfil metabólico
- Estadiar fibrose: FIB-4, elastografia hepática
- Vigilância apropriada conforme diagnóstico estabelecido
- Vacinação: Hepatite A e B se suscetível
`);
  }

  // =========================================================================
  // RETORNAR DIRETRIZES COMPILADAS
  // =========================================================================

  return diretrizes.join("\n---\n");
}

// =============================================================================
// FUNÇÕES DE GERAÇÃO DE PROMPTS
// =============================================================================

/**
 * Prompt para análise de função hepática
 */
export function gerarPromptFuncaoHepatica(dados: DadosAnonimizados): string {
  // B244 (AI_110) — pós-Tx? Define se MELD/Child entram (só pré-Tx) e a nota de
  // não comentar sua inaplicabilidade no pós-Tx.
  const isPosTx =
    dados.tipoAtendimento === "pos-transplante" ||
    dados.tipoAtendimento === "pós-transplante" ||
    !!dados.transplante;
  return `
ANÁLISE DE FUNÇÃO HEPÁTICA - HEPATOLOGIA/TRANSPLANTE
=====================================================

${formatarContextoClinico(dados)}

${formatarScores(dados)}

EXAMES LABORATORIAIS:
${
  dados.examesLab && dados.examesLab.length > 0
    ? dados.examesLab
        .map((e) => {
          const indicador =
            (e as any).coletaNumero > 1 || (e as any).horario
              ? ` (coleta ${(e as any).coletaNumero}${
                  (e as any).horario ? ", " + (e as any).horario : ""
                })`
              : "";
          return `${e.data}${indicador}: ${valoresLabParaIA(e.valores)}`;
        })
        .join("\n")
    : "Não disponíveis"
}

${formatarTendencias(dados)}

${gerarDiretrizesContextuais(dados)}

INTERPRETAÇÃO PÓS-TRANSPLANTE HEPÁTICO POR FASE:

=== FASE 1: PÓS-OPERATÓRIO IMEDIATO (Dias 0-3) ===
Nesta fase, alterações laboratoriais significativas são ESPERADAS e NÃO devem ser interpretadas como disfunção.

TRANSAMINASES (AST/ALT):
- < 2000 U/L: NORMAL para o período - lesão de isquemia-reperfusão esperada
- 2000-5000 U/L: ATENÇÃO - monitorar tendência nas próximas 12-24h
- > 5000 U/L: ALERTA CRÍTICO - risco de disfunção primária do enxerto (EAD)

COAGULAÇÃO:
- INR elevado (até 2.5-3.0): ESPERADO nos primeiros 48-72h
- Fibrinogênio baixo (100-200): ESPERADO - consumo cirúrgico
- IMPORTANTE: Avaliar TENDÊNCIA - valores devem MELHORAR progressivamente

LACTATO:
- Elevado no POI e em QUEDA nas primeiras 24h: NORMAL - clareamento esperado
- Elevado e PERSISTENTE ou em ASCENSÃO após 24h: ALERTA - suspeita de hipoperfusão/disfunção
- Lactato > 4 mmol/L persistente após 48h: CRÍTICO

HEMOGLOBINA/HEMATÓCRITO:
- Queda progressiva: ALERTA - investigar sangramento ativo
- Hb < 7-8 g/dL com instabilidade: considerar reabordagem cirúrgica

BILIRRUBINAS:
- Podem permanecer elevadas por 5-7 dias: ESPERADO
- BT > 10 persistente após 7 dias: ALERTA

=== FASE 2: PÓS-OPERATÓRIO PRECOCE (Dias 4-30) ===
Exames devem estar em NORMALIZAÇÃO progressiva.

TRANSAMINASES:
- Em queda progressiva: ESPERADO
- Platô ou nova elevação: ALERTA - investigar rejeição, complicação vascular ou biliar
- AST/ALT 2-5x LSN: Disfunção LEVE
- AST/ALT 5-10x LSN: Disfunção MODERADA  
- AST/ALT > 10x LSN: Disfunção GRAVE

DIAGNÓSTICOS DIFERENCIAIS PRIORITÁRIOS (ordem de investigação):

⚠️ EM DISFUNÇÃO DO ENXERTO NOS PRIMEIROS 6 MESES, A PRINCIPAL HIPÓTESE É REJEIÇÃO AGUDA ATÉ PROVA EM CONTRÁRIO.

1. REJEIÇÃO AGUDA CELULAR (mais comum): 
   - Suspeitar sempre que houver elevação de transaminases sem causa aparente
   - Biópsia hepática é OBRIGATÓRIA para confirmação e graduação (Banff)
   - Tratamento: pulsoterapia com metilprednisolona 500-1000mg/dia por 3 dias

2. Complicações VASCULARES: 
   - Trombose de artéria hepática (USG Doppler urgente)
   - Estenose/trombose portal
   - Solicitar Doppler em TODA disfunção do enxerto

3. Complicações BILIARES: 
   - Estenose anastomótica, fístula, biloma, colangite
   - Solicitar ColangioRM ou CPRE se colestase predominante

4. Infecções: CMV, EBV, reativação viral, bacteriana
   - PCR quantitativo para CMV/EBV obrigatório

5. Toxicidade medicamentosa:
   - Reduzir tacrolimus para alvo 5-8 ng/mL
⚠️ REGRAS DE CONDUTA PARA IMUNOSSUPRESSÃO:
${REGRA_IMUNOSSUPRESSAO_CONVERSAO}
- Conversão de imunossupressor é decisão de equipe transplantadora, não emergencial
- Se suspeita de rejeição com tacrolimus baixo: AUMENTAR dose + pulsoterapia

⚠️ EM DISFUNÇÃO DO ENXERTO pós-Tx (alteração laboratorial sugestiva):
SINALIZE o achado com destaque. A investigação por IMAGEM (Doppler de artéria/
porta, vias biliares) e a BIÓPSIA NÃO são prescritas por esta análise laboratorial
— indique que serão definidas na análise geral / pela equipe transplantadora.
No laboratório, pode sugerir os exames LABORATORIAIS pertinentes (ex.: PCR CMV/EBV).

=== FASE 3: INTERPRETAÇÃO LABORATORIAL APÓS O 1º MÊS (> 30 dias) ===
(ATENÇÃO: estes são limiares de INTERPRETAÇÃO DE EXAMES após o 30º dia.
NÃO confundir com o PERÍODO clínico do paciente — a régua de período é
precoce ≤1 mês, intermediário 1–6 meses, TARDIO só >6 meses. NÃO chamar
de "tardio" um paciente com menos de 6 meses de transplante.)
TRANSAMINASES:
- < 2x LSN: Aceitável, monitorar
- 2-5x LSN: Disfunção LEVE - investigar causa
- 5-10x LSN: Disfunção MODERADA - biópsia indicada
- > 10x LSN: Disfunção GRAVE - investigação urgente

DIAGNÓSTICOS DIFERENCIAIS:
1. Rejeição aguda ou crônica
2. Estenose biliar tardia (especialmente anastomótica)
3. Recidiva de doença de base (HCV, HAI, CBP, CEP)
4. Infecções oportunistas
5. PTLD (doença linfoproliferativa pós-transplante) - especialmente se EBV+

=== REGRAS DE TENDÊNCIA ===
SEMPRE avaliar a DIREÇÃO dos valores, não apenas o valor absoluto:
- Valores alterados em MELHORA: tranquilizador
- Valores alterados ESTÁVEIS: atenção, investigar causa
- Valores em PIORA: alerta, ação imediata

CONTEXTO PÓS-TRANSPLANTE - REGRAS GERAIS:
- NÃO classificar como "disfunção grave" baseado apenas em transaminases elevadas nos primeiros 3 dias
- SEMPRE considerar complicações vasculares (trombose arterial, estenose portal) no diagnóstico diferencial
- SEMPRE considerar complicações biliares (estenose, fístula) no diagnóstico diferencial
- Nível de tacrolimus/ciclosporina ZERADO ou muito baixo: ALERTA CRÍTICO para rejeição

DIAGNÓSTICO DE CIRROSE — CRITÉRIOS CLÍNICOS (AASLD 2024 / EASL 2023 / Baveno VII):
- Cirrose NÃO requer confirmação por elastografia ou biópsia quando há EVIDÊNCIA CLÍNICA INEQUÍVOCA
- CRITÉRIOS CLÍNICOS SUFICIENTES PARA DIAGNÓSTICO: Se o paciente apresenta QUALQUER combinação de:
  * Descompensação hepática documentada (ascite, encefalopatia, HDA varicosa)
  * Plaquetopenia <100.000 + esplenomegalia
  * Coagulopatia (INR >1.5) + hipoalbuminemia (<3.5) sem outra causa
  * Sinais de hepatopatia crônica/hipertensão portal em exame de imagem (US, TC ou RM)
  → NÃO solicitar elastografia ou biópsia "para confirmar cirrose" — o diagnóstico JÁ ESTÁ CONFIRMADO clinicamente
  → Elastografia é útil para ESTADIAR doença compensada (F3 vs F4), NÃO para confirmar cirrose já descompensada
- QUANDO SOLICITAR CONFIRMAÇÃO: apenas se hepatopatia compensada SEM descompensação prévia, SEM plaquetopenia, SEM coagulopatia
- NUNCA condicionar listagem para transplante a "confirmação de cirrose" quando há descompensação clínica evidente

VIGILÂNCIA DE CHC — CONSIDERAR EXAMES JÁ REALIZADOS:
- Se paciente tem USG abdominal recente (<6 meses) SEM lesões focais → rastreio INICIAL já foi feito
  → NÃO afirmar que "CHC não foi investigado" ou "screening desatualizado"
  → Recomendar TC trifásica ou RM com contraste como COMPLEMENTO (maior sensibilidade), não como se nenhum rastreio existisse
- Se paciente tem TC ou RM recente sem lesões → vigilância está em dia
- Recomendar AFP APENAS como complemento ao exame de imagem, NUNCA como screening isolado (sensibilidade <60%)
- Periodicidade de rastreio: USG ± AFP a cada 6 meses (AASLD 2024)

SOLICITAÇÃO:
Esta é a análise da ABA DE EXAMES LABORATORIAIS. O objetivo é INTERPRETAR OS
EXAMES E IDENTIFICAR SUAS TENDÊNCIAS — NÃO é a análise geral do caso.

ESCOPO (importante — ficar na própria pista):
- NÃO recomende exames de IMAGEM (USG/Doppler/TC/RM/ColangioRM/PET), vigilância
  oncológica/rastreio de CHC, biópsia nem profilaxias. Se um achado laboratorial
  sugerir algo assim, apenas SINALIZE o achado e diga que a conduta será definida
  na análise geral / pela equipe.
- TRABALHE SÓ COM OS DADOS PRESENTES. NÃO faça meta-comentários sobre dados
  ausentes nem peça para "verificar" exames que não estão aqui (laudo de imagem,
  lista de medicações, albumina não coletada). Se um dado não está, simplesmente
  não o mencione. Quando uma conduta depender de um dado que você não tem, diga-a
  de forma condicional, sem reclamar da ausência.
- Transfusão: o gatilho restritivo é Hb < 7,0 g/dL (ou paciente sintomático); NÃO
  trate 8,0 g/dL como "limiar crítico".${
    isPosTx
      ? `
- PÓS-TRANSPLANTE: NÃO use nem comente MELD/Child-Pugh (a cirrose nativa não
  existe mais — é pressuposto, não precisa ser dito).`
      : ""
  }

Analise considerando:

1. PADRÃO DE LESÃO HEPÁTICA (a partir dos exames)
   - Hepatocelular vs colestático vs misto; magnitude; tendência

2. FUNÇÃO DE SÍNTESE
   - Albumina, INR, bilirrubinas — implicações

${
  isPosTx
    ? ""
    : `3. SCORES DE GRAVIDADE (pré-transplante)
   - Interpretação do MELD/Child-Pugh; tendência e prognóstico

`
}${isPosTx ? "3" : "4"}. TENDÊNCIAS
   - Direção e trajetória dos analitos RECENTES (use as datas exibidas; não trate
     valores antigos como estado atual)

${isPosTx ? "4" : "5"}. CONDUTA LABORATORIAL
   - Próximos EXAMES LABORATORIAIS pertinentes
   - Ajustes medicamentosos LIGADOS AOS LABS (ex.: nível de imunossupressor;
     antiviral conforme função renal)

Formate de forma clara e objetiva. Racional clínico sem nomear sociedade é
aceitável; só nomeie diretriz/ano se houver fonte recuperada.
`;
}

/**
 * Prompt para análise de exames de imagem
 */
export function gerarPromptImagem(dados: DadosAnonimizados): string {
  return `
ANÁLISE DE EXAMES DE IMAGEM - HEPATOLOGIA/TRANSPLANTE
======================================================

${formatarContextoClinico(dados)}

${formatarScores(dados)}

${formatarAchadosImagem(dados.achadosImagem || [])}

LAUDOS DE IMAGEM DISPONÍVEIS:
${
  dados.imagens && dados.imagens.length > 0
    ? dados.imagens
        .map(
          (i) =>
            `${i.data} - ${i.tipo}: ${i.laudo || i.descricao || "Sem laudo"}`
        )
        .join("\n")
    : "Não disponíveis"
}

${gerarDiretrizesContextuais(dados)}

SOLICITAÇÃO:
Analise os achados de imagem considerando:

1. AVALIAÇÃO HEPÁTICA
   - Parênquima (esteatose, cirrose, heterogeneidade)
   - Lesões focais e classificação LI-RADS se aplicável
   - Critérios de Milão se CHC presente

2. SISTEMA PORTAL
   - Patência da veia porta
   - Trombose: Classificação de Yerdel se presente
   - Diferenciação bland vs tumoral

3. HIPERTENSÃO PORTAL
   - Esplenomegalia
   - Ascite
   - Varizes/colaterais

4. CORRELAÇÃO CLÍNICO-RADIOLÓGICA
   - Consistência com quadro clínico
   - Progressão comparada a exames anteriores

5. RECOMENDAÇÕES
   - Exames complementares necessários
   - Intervalos de seguimento
   - Condutas específicas

Formate a resposta citando classificações e guidelines (LI-RADS, Yerdel, Milão, BCLC).
`;
}

/**
 * Prompt para análise de imunossupressão (pós-transplante)
 */
export function gerarPromptImunossupressao(dados: DadosAnonimizados): string {
  return `
ANÁLISE DE IMUNOSSUPRESSÃO - PÓS-TRANSPLANTE HEPÁTICO
=====================================================

${formatarContextoClinico(dados)}

TEMPO PÓS-TRANSPLANTE: ${
    dados.transplante?.tempoTransplante || "Não especificado"
  }
INDICAÇÃO: ${
    dados.transplante?.indicacao ||
    dados.transplante?.diagnosticoBase ||
    "Não especificada"
  }

  EXAMES LABORATORIAIS:
  ${
    dados.examesLab && dados.examesLab.length > 0
      ? dados.examesLab
          .map((e) => {
            const v = e.valores;
            const indicadorColeta =
              (e as any).coletaNumero > 1 || (e as any).horario
                ? ` (coleta ${(e as any).coletaNumero}${
                    (e as any).horario ? ", " + (e as any).horario : ""
                  })`
                : "";
            let texto = `📅 ${e.data}${indicadorColeta}:\n`;

            // Enzimas hepáticas
            if (v.AST || v.ALT || v.GGT || v.FA) {
              texto += `  Enzimas Hepáticas: AST ${v.AST || "-"}, ALT ${
                v.ALT || "-"
              }, GGT ${v.GGT || "-"}, FA ${v.FA || "-"}\n`;
            }

            // Bilirrubinas
            if (v.BT || v.BD || v.BI) {
              texto += `  Bilirrubinas: BT ${v.BT || "-"}, BD ${
                v.BD || "-"
              }, BI ${v.BI || "-"}\n`;
            }

            // Função sintética
            if (v.Albumina || v.INR || v.TAP || v.FatorV) {
              texto += `  Função Sintética: Albumina ${
                v.Albumina || "-"
              } g/dL, INR ${v.INR || "-"}, TAP ${v.TAP || "-"}%`;
              if (v.FatorV) texto += `, Fator V ${v.FatorV}%`;
              texto += `\n`;
            }

            // Função renal
            if (v.Creatinina || v.Ureia) {
              texto += `  Função Renal: Creatinina ${
                v.Creatinina || "-"
              } mg/dL, Ureia ${v.Ureia || "-"} mg/dL\n`;
            }

            // Eletrólitos
            if (v.Sodio) {
              texto += `  Eletrólitos: Sódio ${v.Sodio || "-"} mEq/L\n`;
            }

            // Hemograma
            if (v.Hemoglobina || v.Hematocrito || v.Plaquetas || v.Leucocitos) {
              texto += `  Hemograma: Hb ${v.Hemoglobina || "-"} g/dL, Ht ${
                v.Hematocrito || "-"
              }%, Plaquetas ${v.Plaquetas || "-"}, Leucócitos ${
                v.Leucocitos || "-"
              }\n`;
            }

            // Imunossupressores (apenas se tiver valor > 0)
            const imunossupressores: string[] = [];
            if (v.Tacrolimus && Number(v.Tacrolimus) > 0)
              imunossupressores.push(`Tacrolimus ${v.Tacrolimus} ng/mL`);
            if (v.Ciclosporina && Number(v.Ciclosporina) > 0)
              imunossupressores.push(`Ciclosporina ${v.Ciclosporina} ng/mL`);
            if (v.Everolimus && Number(v.Everolimus) > 0)
              imunossupressores.push(`Everolimus ${v.Everolimus} ng/mL`);
            if (v.Sirolimus && Number(v.Sirolimus) > 0)
              imunossupressores.push(`Sirolimus ${v.Sirolimus} ng/mL`);
            if (v.Rapamicina && Number(v.Rapamicina) > 0)
              imunossupressores.push(`Rapamicina ${v.Rapamicina} ng/mL`);

            // Gasometria
            if (v.GasometriaPh || v.GasometriaPco2 || v.GasometriaPo2) {
              texto += `  GASOMETRIA (${
                v.GasometriaTipo || "tipo não informado"
              }): `;
              texto += `pH ${v.GasometriaPh || "-"}, `;
              texto += `pCO2 ${v.GasometriaPco2 || "-"} mmHg, `;
              texto += `pO2 ${v.GasometriaPo2 || "-"} mmHg, `;
              texto += `HCO3 ${v.GasometriaHco3 || "-"} mEq/L, `;
              texto += `BE ${v.GasometriaBe || "-"}, `;
              texto += `SatO2 ${v.GasometriaSaturacao || "-"}%\n`;
            }

            // Lactato
            if (v.Lactato) {
              texto += `  Lactato: ${v.Lactato} mmol/L\n`;
            }

            // Perfil Metabólico / Lipídico
            if (
              v.ColesterolTotal ||
              v.LDL ||
              v.HDL ||
              v.VLDL ||
              v.Triglicerideos
            ) {
              texto += `  Perfil Lipídico: Colesterol Total ${
                v.ColesterolTotal || "-"
              }, LDL ${v.LDL || "-"}, HDL ${v.HDL || "-"}, VLDL ${
                v.VLDL || "-"
              }, Triglicerídeos ${v.Triglicerideos || "-"} mg/dL\n`;
            }

            // Glicemia / Diabetes
            if (v.Glicemia || v.HbA1c || v.Frutosamina) {
              texto += `  Metabolismo Glicídico: Glicemia ${
                v.Glicemia || "-"
              } mg/dL`;
              if (v.HbA1c) texto += `, HbA1c ${v.HbA1c}%`;
              if (v.Frutosamina) texto += `, Frutosamina ${v.Frutosamina}`;
              texto += `\n`;
            }

            // Scores calculados
            if (v.MELD || v.ChildPugh) {
              texto += `  Scores: `;
              if (v.MELD) texto += `MELD ${v.MELD}`;
              if (v.MELD && v.ChildPugh) texto += `, `;
              if (v.ChildPugh) texto += `Child-Pugh ${v.ChildPugh}`;
              texto += `\n`;
            }

            // Complicações (Ascite, Encefalopatia)
            if (v.Ascite || v.Encefalopatia) {
              texto += `  Complicações: `;
              if (v.Ascite) texto += `Ascite: ${v.Ascite}`;
              if (v.Ascite && v.Encefalopatia) texto += `, `;
              if (v.Encefalopatia) texto += `Encefalopatia: ${v.Encefalopatia}`;
              texto += `\n`;
            }

            return texto;
          })
          .join("\n")
      : "Não disponíveis"
  }

CONSULTAS RECENTES:
${
  dados.consultas && dados.consultas.length > 0
    ? dados.consultas
        .slice(0, 7)
        .map((c) => {
          let texto = `📅 ${c.data}:`;
          if (c.queixaPrincipal) texto += `\n  Queixa: ${c.queixaPrincipal}`;
          if (c.hma) texto += `\n  História: ${c.hma}`;
          if (c.hipoteses) texto += `\n  Hipóteses: ${c.hipoteses}`;
          if (c.conduta) texto += `\n  Conduta: ${c.conduta}`;
          return texto;
        })
        .join("\n\n")
    : "Não disponíveis"
}

NÍVEIS ALVO DE TACROLIMUS POR TEMPO PÓS-TX:
- 0-3 meses: 8-12 ng/mL
- 3-6 meses: 6-10 ng/mL
- 6-12 meses: 5-8 ng/mL
- > 12 meses: 4-7 ng/mL (individualizar)

SOLICITAÇÃO:
Analise o manejo imunossupressor considerando:

1. ADEQUAÇÃO DOS NÍVEIS
   - Tacrolimus dentro do alvo para o tempo pós-Tx?
   - Variabilidade dos níveis
   - Necessidade de ajuste de dose

2. FUNÇÃO DO ENXERTO
   - Provas hepáticas
   - Sinais de rejeição (elevação enzimas, colestase)
   - Necessidade de biópsia?

3. EFEITOS ADVERSOS
   - Nefrotoxicidade (função renal)
   - Neurotoxicidade
   - Metabólicos (diabetes, HAS, dislipidemia)

4. INFECÇÕES
   - Profilaxias adequadas
   - Sinais de infecção oportunista

5. RECOMENDAÇÕES
   - Ajustes de dose
   - Exames de monitoramento
   - Intervalo de retorno
`;
}

/**
 * Prompt para análise de marcadores tumorais
 */
export function gerarPromptMarcadores(dados: DadosAnonimizados): string {
  // Formatar histórico completo de marcadores na estrutura real do Firebase
  const historicoFormatado =
    dados.marcadores && dados.marcadores.length > 0
      ? dados.marcadores
          .sort((a, b) => {
            const dataA = a.dataColeta || a.data || "";
            const dataB = b.dataColeta || b.data || "";
            return dataA.localeCompare(dataB);
          })
          .map((m) => {
            const data = m.dataColeta || m.data || "Data não informada";
            const partes: string[] = [];
            if (m.afp !== undefined && m.afp !== null && m.afp !== "")
              partes.push(`AFP: ${m.afp}`);
            if (m.ca199 !== undefined && m.ca199 !== null && m.ca199 !== "")
              partes.push(`CA 19-9: ${m.ca199}`);
            if (m.cea !== undefined && m.cea !== null && m.cea !== "")
              partes.push(`CEA: ${m.cea}`);
            if (m.ca125 !== undefined && m.ca125 !== null && m.ca125 !== "")
              partes.push(`CA 125: ${m.ca125}`);
            if (m.psa !== undefined && m.psa !== null && m.psa !== "")
              partes.push(`PSA: ${m.psa}`);
            // Metabólicos / Etiológicos (B195)
            if (
              m.cobreSerico !== undefined &&
              m.cobreSerico !== null &&
              m.cobreSerico !== ""
            )
              partes.push(`Cobre sérico: ${m.cobreSerico} µg/dL`);
            if (
              m.cobreUrinario !== undefined &&
              m.cobreUrinario !== null &&
              m.cobreUrinario !== ""
            )
              partes.push(`Cobre urinário (24h): ${m.cobreUrinario} µg/24h`);
            if (
              m.ceruloplasmina !== undefined &&
              m.ceruloplasmina !== null &&
              m.ceruloplasmina !== ""
            )
              partes.push(`Ceruloplasmina: ${m.ceruloplasmina} mg/dL`);
            if (
              m.alfa1Antitripsina !== undefined &&
              m.alfa1Antitripsina !== null &&
              m.alfa1Antitripsina !== ""
            )
              partes.push(`Alfa-1 antitripsina: ${m.alfa1Antitripsina} mg/dL`);
            // Função renal / urina 24h (B279 — AI_120)
            if (
              m.clearanceCreatinina !== undefined &&
              m.clearanceCreatinina !== null &&
              m.clearanceCreatinina !== ""
            )
              partes.push(
                `Clearance de creatinina (total): ${m.clearanceCreatinina} mL/min`
              );
            if (
              m.clearanceCreatininaCorrigido !== undefined &&
              m.clearanceCreatininaCorrigido !== null &&
              m.clearanceCreatininaCorrigido !== ""
            )
              partes.push(
                `Clearance de creatinina (corrigido 1,73m²): ${m.clearanceCreatininaCorrigido} mL/min/1,73m²`
              );
            if (
              m.proteinuria24h !== undefined &&
              m.proteinuria24h !== null &&
              m.proteinuria24h !== ""
            )
              partes.push(`Proteinúria de 24h: ${m.proteinuria24h} mg/24h`);
            if (
              m.sodioUrinarioAmostra !== undefined &&
              m.sodioUrinarioAmostra !== null &&
              m.sodioUrinarioAmostra !== ""
            )
              partes.push(
                `Sódio urinário (amostra isolada): ${m.sodioUrinarioAmostra} mEq/L`
              );
            if (
              m.sodioUrinario24h !== undefined &&
              m.sodioUrinario24h !== null &&
              m.sodioUrinario24h !== ""
            )
              partes.push(
                `Sódio urinário (24h): ${m.sodioUrinario24h} mEq/24h`
              );
            // Imunoglobulinas / Autoimune-IgG4 (B195)
            if (
              m.iggSerico !== undefined &&
              m.iggSerico !== null &&
              m.iggSerico !== ""
            )
              partes.push(`IgG sérico: ${m.iggSerico} mg/dL`);
            if (m.igg4 !== undefined && m.igg4 !== null && m.igg4 !== "")
              partes.push(`IgG4: ${m.igg4} mg/dL`);
            // Eletroforese de proteínas (frações em g/dL) — B195
            const efpPartes: string[] = [];
            if (
              m.efpAlbumina !== undefined &&
              m.efpAlbumina !== null &&
              m.efpAlbumina !== ""
            )
              efpPartes.push(`Alb ${m.efpAlbumina}`);
            if (m.efpAlfa1 !== undefined && m.efpAlfa1 !== null && m.efpAlfa1 !== "")
              efpPartes.push(`α1 ${m.efpAlfa1}`);
            if (m.efpAlfa2 !== undefined && m.efpAlfa2 !== null && m.efpAlfa2 !== "")
              efpPartes.push(`α2 ${m.efpAlfa2}`);
            if (m.efpBeta !== undefined && m.efpBeta !== null && m.efpBeta !== "")
              efpPartes.push(`β ${m.efpBeta}`);
            if (m.efpGama !== undefined && m.efpGama !== null && m.efpGama !== "")
              efpPartes.push(`γ ${m.efpGama}`);
            if (efpPartes.length > 0)
              partes.push(`Eletroforese (g/dL): ${efpPartes.join(", ")}`);
            return `${data}: ${
              partes.length > 0 ? partes.join(" | ") : "Sem valores"
            }`;
          })
          .join("\n")
      : "Nenhum marcador registrado";

  return `
ANÁLISE DE MARCADORES SÉRICOS - CURVA E TENDÊNCIAS POR CATEGORIA
================================================================

CONTEXTO DO PACIENTE:
- Idade: ${dados.idade} anos | Sexo: ${dados.sexo}
- Tipo atendimento: ${dados.tipoAtendimento}
${
  dados.transplante
    ? `- Transplante: ${dados.transplante.dataTransplante} (${dados.transplante.tempoTransplante})`
    : "- Sem transplante registrado"
}
${
  dados.diagnosticoBase ? `- Diagnóstico de base: ${dados.diagnosticoBase}` : ""
}

HISTÓRICO COMPLETO DE MARCADORES (ordem cronológica):
${historicoFormatado}

Total de coletas: ${dados.marcadores?.length || 0}

FOCO DESTA ANÁLISE:
Analise EXCLUSIVAMENTE os marcadores acima e suas tendências, organizando a
resposta por CATEGORIA. Comente APENAS as categorias que têm pelo menos um
valor registrado (ignore as vazias). NÃO faça análise global do paciente.

── CATEGORIA A — MARCADORES TUMORAIS (AFP, CA 19-9, CEA, CA 125, PSA) ──

1. CURVA DE AFP (análise de TODOS os valores do histórico):
   - Descrever a trajetória completa: valores iniciais, picos, quedas, valor atual
   - Classificar cada fase: ascensão, platô, queda, estabilidade
   - Se houve queda após pico: correlacionar com possível tratamento (TACE, ablação)
   - Calcular velocidade de variação entre coletas consecutivas
   - Identificar se há padrão de recidiva (queda seguida de nova elevação)

2. SIGNIFICADO CLÍNICO DA CURVA DE AFP:
   - AFP <20: sem valor diagnóstico, flutuação normal
   - AFP 20-200: monitorização, correlacionar com imagem
   - AFP 200-400: fortemente sugestivo de CHC ativo
   - AFP >400: praticamente diagnóstico de CHC
   - AFP >1000: alto risco de invasão vascular e metástase
   - Tendência é MAIS importante que valor absoluto isolado

3. CA 19-9, CEA, CA 125, PSA (se disponíveis):
   - Tendência e significado
   - Se CA 19-9 elevado: considerar colangiocarcinoma no diferencial

── CATEGORIA B — METABÓLICOS / ETIOLÓGICOS (cobre, ceruloplasmina, A1AT) ──

4. COBRE E CERULOPLASMINA (doença de Wilson):
   - Cobre sérico normal: 70-140 µg/dL | Cobre urinário (24h): < 60 µg/24h
   - Ceruloplasmina normal: 20-35 mg/dL
   - PADRÃO DE WILSON: ceruloplasmina BAIXA (< 20) + cobre urinário 24h ELEVADO
     (> 100). Correlacione os três; isoladamente têm baixa especificidade.
   - Em paciente já tratado para Wilson, o cobre urinário pode subir por
     mobilização (quelante) — interprete conforme o contexto.

5. ALFA-1 ANTITRIPSINA (deficiência de A1AT):
   - Normal: 90-200 mg/dL. BAIXA: sugere deficiência de A1AT como etiologia
     (correlacionar com fenótipo/genótipo; valor isolado não fecha diagnóstico).

── CATEGORIA C — IMUNOGLOBULINAS / AUTOIMUNE-IgG4 (IgG, IgG4, eletroforese) ──

6. IgG E ELETROFORESE (hepatite autoimune):
   - IgG normal: 700-1600 mg/dL. ELEVADA apoia hepatite autoimune (junto com
     autoanticorpos, se houver).
   - Eletroforese de proteínas (frações em g/dL — albumina, α1, α2, β, gama):
     gamaglobulina ELEVADA (policlonal) apoia autoimunidade/cirrose; banda/pico
     monoclonal exige outra investigação; albumina baixa = déficit hepatossin-
     tético; fusão beta-gama sugere cirrose. Interprete o conjunto das frações.

7. IgG4 (doença relacionada a IgG4):
   - Normal: < 135 mg/dL. ELEVADA: levantar colangite/doença esclerosante
     IgG4-relacionada no diferencial. Elevação leve isolada é pouco específica.

8. INTERPRETAÇÃO INTEGRADA (apenas categorias com dados):
   - O que as curvas/valores sugerem em cada categoria com dados?
   - Tendência e próxima dosagem/intervalo sugeridos.

NÃO incluir: análise de função hepática, recomendações de imagem, condutas terapêuticas globais. Foque APENAS nos marcadores.
`;
}

/**
 * Prompt para análise de evolução clínica
 */
export function gerarPromptEvolucao(dados: DadosAnonimizados): string {
  // 🎯 B314/AI_141 — a análise de CONSULTA foca em JULGAR as CONDUTAS da consulta
  // em avaliação (a mais recente), NÃO em refazer a análise geral. A consulta-foco
  // é destacada; as anteriores entram só como contexto.
  const consultasOrdenadas = [...(dados.consultas || [])].sort(
    (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
  );
  const foco = consultasOrdenadas[0];
  const anteriores = consultasOrdenadas.slice(1, 5);

  const fmtConsulta = (c: any): string => {
    const sinaisVitais = [
      c.peso ? `Peso: ${c.peso}kg` : "",
      c.altura ? `Altura: ${c.altura}cm` : "",
      c.pa ? `PA: ${c.pa}` : "",
      c.fc ? `FC: ${c.fc}bpm` : "",
    ]
      .filter(Boolean)
      .join(", ");
    return `📅 ${c.data}
   Queixa: ${c.queixaPrincipal || "Não registrada"}
   História: ${c.hma || "Não registrada"}${
      sinaisVitais ? `\n   Sinais vitais: ${sinaisVitais}` : ""
    }${
      c.revisaoSistemas
        ? `\n   Revisão de sistemas/Medicações: ${c.revisaoSistemas}`
        : ""
    }
   Exame físico: ${c.exameFisico || "Não registrado"}
   Hipóteses: ${c.hipoteses || "Não registradas"}
   Conduta: ${c.conduta || "Não registrada"}${
      c.observacoes ? `\n   Observações: ${c.observacoes}` : ""
    }`;
  };

  return `
ANÁLISE DA CONSULTA (EVOLUÇÃO) — JUÍZO DAS CONDUTAS DO DIA
=========================================================

${formatarContextoClinico(dados)}

${formatarScores(dados)}

CONSULTA EM AVALIAÇÃO (é ESTA consulta que você deve julgar):
${foco ? fmtConsulta(foco) : "Não disponível"}

CONSULTAS ANTERIORES (apenas contexto — NÃO reanalisar):
${anteriores.length > 0 ? anteriores.map(fmtConsulta).join("\n\n") : "—"}

EXAMES LABORATORIAIS RECENTES (contexto para julgar as condutas):
${
  dados.examesLab && dados.examesLab.length > 0
    ? dados.examesLab
        .slice(-5)
        .map((e) => {
          const indicador =
            (e as any).coletaNumero > 1 || (e as any).horario
              ? ` (coleta ${(e as any).coletaNumero}${
                  (e as any).horario ? ", " + (e as any).horario : ""
                })`
              : "";
          return `${e.data}${indicador}: ${valoresLabParaIA(e.valores)}`;
        })
        .join("\n")
    : "Não disponíveis"
}

${(() => {
  // ═══════════════════════════════════════════════════════════
  // AI_53 Patch 2a — Inclusão temporal de imagens e marcadores
  // ═══════════════════════════════════════════════════════════
  // Em laudo de Evolução, o prompt anterior NÃO incluía exames de
  // imagem nem marcadores tumorais — falha grave detectada AI_53
  // (caso Sebastião: TC/RM 06/04/2026 com linfonodomegalias hilares
  // 38x27mm omitidas do laudo final).
  //
  // Estratégia de janela (Opção B AI_53): pós-Tx integral + N últimos
  // pré-Tx. Sustenta escalabilidade para pacientes de longo seguimento
  // sem inflar prompt indefinidamente. Refinamentos futuros: B36-B39.
  // ═══════════════════════════════════════════════════════════
  const dataTx = (dados as any)?.transplante?.dataTransplante;
  const txTime = dataTx ? new Date(dataTx).getTime() : null;
  const limitePreTxImagens = 5;

  const imagensTodas = Array.isArray((dados as any).achadosImagem)
    ? (dados as any).achadosImagem
    : [];

  let imagensFiltradas: any[] = imagensTodas;
  if (txTime && !isNaN(txTime)) {
    const ordenadasDesc = [...imagensTodas].sort((a, b) => {
      const dA = new Date(a.dataExame || "").getTime() || 0;
      const dB = new Date(b.dataExame || "").getTime() || 0;
      return dB - dA;
    });
    const posTx = ordenadasDesc.filter((img) => {
      const t = new Date(img.dataExame || "").getTime();
      return !isNaN(t) && t >= txTime;
    });
    const preTx = ordenadasDesc
      .filter((img) => {
        const t = new Date(img.dataExame || "").getTime();
        return !isNaN(t) && t < txTime;
      })
      .slice(0, limitePreTxImagens);
    imagensFiltradas = [...posTx, ...preTx];
  }

  const totalImg = imagensTodas.length;
  const usadasImg = imagensFiltradas.length;
  console.log(
    `📊 [Evolução] Janela temporal de imagens: ${usadasImg}/${totalImg} (pós-Tx + ${limitePreTxImagens} últimas pré-Tx)`
  );

  return `ACHADOS DE IMAGEM RECENTES (contexto — use APENAS para checar se as condutas da consulta os endereçaram; a análise de imagem em si é da aba Imagem):
${formatarAchadosImagem(imagensFiltradas)}

⚠️ SEGURANÇA (AI_53): se houver um achado de imagem recente RELEVANTE (linfonodomegalia, lesão suspeita, derrame, dilatação biliar, recidiva, espessamento) — sobretudo pós-transplante — que as condutas da consulta NÃO endereçaram, SINALIZE em 1 linha. Não produza uma análise de imagem completa.`;
})()}

${formatarTendencias(dados)}

SOLICITAÇÃO — SEJA SUCINTO E FOCADO:
Você está avaliando UMA consulta específica (a "CONSULTA EM AVALIAÇÃO"). Seu papel é
JULGAR as CONDUTAS registradas nessa consulta — NÃO refazer a análise global do caso
(isso é a "Análise IA" geral). As demais consultas, labs, scores e imagem são só
CONTEXTO para esse juízo.

NÃO inclua: prognóstico de transplante, eixos de candidatura, rastreio pré-Tx,
vacinação, priorização em lista, nem um plano de cuidados amplo. Se algum achado
sugerir algo assim, apenas SINALIZE em 1 linha que "é tema da análise geral".

Responda de forma CURTA (alvo ~200 palavras), ancorada nos dados (não invente),
nesta estrutura:

**Contexto** (1 linha): o que foi essa consulta.

**Condutas do dia** (bullets curtos): para CADA conduta registrada — está adequada?
precisa ajuste? faltou algo? Aponte risco/interação/dose/gatilho de monitorização
quando houver. Se estiverem adequadas, diga isso e seja breve.

**Pontos a não perder** (bullets curtos, só se acionável AGORA). Inclua aqui um
CHECK DE SEGURANÇA das MEDICAÇÕES EM USO relevantes ao quadro (não revise a lista
inteira — só sinalize RISCO ATIVO acionável), por exemplo:
- Betabloqueador/NSBB (carvedilol/propranolol/nadolol) na janela de segurança da
  ascite refratária/hiponatremia/lesão renal — reduzir/suspender se PAS < 90,
  Na < 130 ou IRA.
- Tiazídico (hidroclorotiazida) em cirrótico com ascite — risco de distúrbio
  hidroeletrolítico; questionar manutenção.
- Interação/contraindicação medicamentosa; nível de imunossupressor fora do alvo.
- Achado recente relevante (imagem/lab) não endereçado pelas condutas.
NÃO repita a revisão farmacológica completa nem o plano global (isso é da análise geral).
`;
}

/**
 * Prompt para avaliação pré-transplante
 */
export function gerarPromptPreTransplante(dados: DadosAnonimizados): string {
  return `
AVALIAÇÃO PRÉ-TRANSPLANTE HEPÁTICO
==================================

${formatarContextoClinico(dados)}

${formatarScores(dados)}

${formatarMarcadores(dados)}

${formatarSorologias(dados)}

${formatarAchadosImagem(dados.achadosImagem || [])}

CRITÉRIOS DE ALOCAÇÃO — SITUAÇÃO ESPECIAL × PRIORIZAÇÃO (NÃO CONFUNDIR, B316):
- MELD 3.0: reflete a gravidade e define o benefício/ordem na fila (≥ 15 = benefício de transplante).
- SITUAÇÃO ESPECIAL (exceção de MELD — pontuação ADICIONAL, NÃO pula a fila): CHC (critérios de Milão), ascite/hidrotórax refratário, síndrome hepatopulmonar, colangites de repetição, etc. Apontar ao médico quando o paciente PREENCHE um critério.
- PRIORIZAÇÃO / URGÊNCIA (TOPO da fila — pula a fila): hepatite fulminante, disfunção/não-função primária do enxerto. NÃO se aplica à descompensação crônica.

CONTRAINDICAÇÕES A AVALIAR:
- Neoplasia extra-hepática ativa
- Doença cardiopulmonar avançada não corrigível
- Infecção ativa não controlada
- Má aderência documentada
- Uso ativo de substâncias

EXAMES LABORATORIAIS:
${
  dados.examesLab && dados.examesLab.length > 0
    ? dados.examesLab
        .slice(-5)
        .map((e) => {
          const indicador =
            (e as any).coletaNumero > 1 || (e as any).horario
              ? ` (coleta ${(e as any).coletaNumero}${
                  (e as any).horario ? ", " + (e as any).horario : ""
                })`
              : "";
          return `${e.data}${indicador}: ${valoresLabParaIA(e.valores)}`;
        })
        .join("\n")
    : "Não disponíveis"
}

${gerarDiretrizesContextuais(dados)}

SOLICITAÇÃO:
Analise a elegibilidade para transplante considerando:

1. INDICAÇÃO
   - Doença de base e gravidade
   - MELD atual e tendência
   - Qualidade de vida

2. CHC (SE PRESENTE)
   - Critérios de Milão: DENTRO ou FORA?
   - Necessidade de tratamento ponte
   - Risco de drop-out

3. CONTRAINDICAÇÕES
   - Absolutas identificadas?
   - Relativas que precisam otimização?

4. COMORBIDADES
   - Cardiovascular
   - Renal
   - Nutricional/sarcopenia
   - Psicossocial

5. OTIMIZAÇÃO PRÉ-TX
   - O que precisa ser abordado antes da listagem?
   - Exames complementares necessários
   - Vacinações em dia?
   - Suporte nutricional

6. RECOMENDAÇÕES
   - Elegível para listagem?
   - Prioridade estimada
   - Próximos passos
`;
}

/**
 * Prompt principal para análise completa
 */
export function gerarPromptAnaliseCompleta(
  dados: DadosAnonimizados,
  contexto?: ContextoClinico
): string {
  // Se há contexto específico, selecionar o prompt apropriado
  if (contexto?.tipoAnalise) {
    switch (contexto.tipoAnalise) {
      case "funcao_hepatica":
        return gerarPromptFuncaoHepatica(dados);
      case "imagem":
        return gerarPromptImagem(dados);
      case "imunossupressao":
        return gerarPromptImunossupressao(dados);
      case "marcadores":
        return gerarPromptMarcadores(dados);
      case "evolucao":
        return gerarPromptEvolucao(dados);
      case "pre_transplante":
        return gerarPromptPreTransplante(dados);
    }
  }

  // Análise completa padrão
  return `
ANÁLISE CLÍNICA COMPLETA - HEPATOLOGIA/TRANSPLANTE
==================================================

Você é um assistente médico especializado em hepatologia e transplante de órgãos.
Analise os dados abaixo e forneça uma avaliação clínica completa.

${formatarContextoClinico(dados)}

${formatarScores(dados)}

${formatarMarcadores(dados)}

${formatarSorologias(dados)}

${formatarAchadosImagem(dados.achadosImagem || [])}
${formatarImagensNaoAnalisadas(dados)}
${formatarTendencias(dados)}
${formatarAvaliacaoClinica(dados)}
${formatarDadosTransplante(dados)}
${formatarInscricaoTx(dados)}
${formatarPrescricoes(dados)}
EXAMES LABORATORIAIS RECENTES:
${
  dados.examesLab && dados.examesLab.length > 0
    ? dados.examesLab
        .slice(-5)
        .map((e) => {
          const indicador =
            (e as any).coletaNumero > 1 || (e as any).horario
              ? ` (coleta ${(e as any).coletaNumero}${
                  (e as any).horario ? ", " + (e as any).horario : ""
                })`
              : "";
          return `${e.data}${indicador}: ${valoresLabParaIA(e.valores)}`;
        })
        .join("\n")
    : "Não disponíveis"
}

CONSULTAS RECENTES:
${
  dados.consultas && dados.consultas.length > 0
    ? dados.consultas
        .slice(0, 7)
        .map((c) => {
          const sinaisVitais = [
            (c as any).peso ? `Peso: ${(c as any).peso}kg` : "",
            (c as any).pa ? `PA: ${(c as any).pa}` : "",
            (c as any).fc ? `FC: ${(c as any).fc}bpm` : "",
          ]
            .filter(Boolean)
            .join(", ");
          const partes = [
            // B315 (AI_142): tipo da consulta (retorno/intercorrência/pré-Tx…)
            `${c.data}${
              (c as any).tipoConsulta ? ` [${(c as any).tipoConsulta}]` : ""
            }:`,
            c.queixaPrincipal
              ? `Queixa: ${c.queixaPrincipal}`
              : "Sem queixa registrada",
            c.hma ? `História: ${c.hma}` : "",
            sinaisVitais ? `Sinais vitais: ${sinaisVitais}` : "",
            (c as any).revisaoSistemas
              ? `Revisão de sistemas/Medicações: ${(c as any).revisaoSistemas}`
              : "",
            // B315 (AI_142): exame físico e observações — coletados e mostrados no
            // chat, mas antes omitidos aqui na análise geral.
            (c as any).exameFisico ? `Exame físico: ${(c as any).exameFisico}` : "",
            c.hipoteses ? `Hipóteses: ${c.hipoteses}` : "",
            c.conduta ? `Conduta: ${c.conduta}` : "",
            (c as any).observacoes
              ? `Observações: ${(c as any).observacoes}`
              : "",
          ].filter(Boolean);
          return partes.join(" | ");
        })
        .join("\n")
    : "Não disponíveis"
}

${gerarDiretrizesContextuais(dados)}

REGRAS CLÍNICAS OBRIGATÓRIAS (NÃO VIOLAR):

MELD 3.0 — REGRA DE SCORE:
${REGRA_MELD_30}
TERMINOLOGIA ADMINISTRATIVA:
${REGRA_TERMINOLOGIA}

REGRAS CONTEXTUAIS — PRÉ-TRANSPLANTE E ONCOLOGIA:

CINÉTICA DE MARCADORES TUMORAIS:
- Quando AFP aparece em 2+ coletas, CALCULAR: variação absoluta/percentual, intervalo entre coletas, tempo de duplicação.
- AFP com duplicação <30 dias = biologia extremamente agressiva — mencionar como fator prognóstico adverso.
- AFP em queda pós-tratamento: reportar velocidade e meta (<20 ideal; <400 para elegibilidade a transplante).

DISCORDÂNCIA BIÓPSIA-IMAGEM — REGRA ABSOLUTA:
Quando biópsia mostra fibrose menor (ex: F3→F0) mas imagem atual mostra
sinais de hepatopatia crônica (esplenomegalia, contornos hepáticos
irregulares, circulação colateral, vias biliares alteradas): o resultado
histológico menor É ERRO AMOSTRAL — nunca interpretar como regressão de
fibrose. A biópsia com maior grau de fibrose + achados de imagem atuais
têm precedência absoluta sobre biópsia com menor fibrose discordante.
NUNCA usar linguagem de "regressão de fibrose sob tratamento" quando
imagem atual contradiz o achado histológico.

DISCORDÂNCIA ENTRE EXAMES SEQUENCIAIS — REGRA ABSOLUTA:
Quando exame de controle mostra melhora dramática incompatível com a
biologia da doença e o tratamento em uso, a PRIMEIRA hipótese NÃO é
resposta terapêutica — é discordância técnica entre exames:
- Protocolo diferente, janela de aquisição inadequada, equipamento
  diferente, erro de interpretação do laudo, artefato técnico.
- Somente após excluir causas técnicas considerar resposta real.
Exemplos de melhora biologicamente implausível:
- Resposta completa de CHC avançado (>5cm viável + invasão vascular)
  com sorafenibe/lenvatinibe em <3 meses. TKIs são citostáticos
  (estabilização), não citotóxicos — taxa de resposta completa <1%.
- Resolução de trombose tumoral macrovascular (hepáticas, cava, porta)
  em semanas com terapia sistêmica.
- Redução volumétrica >50% de lesão sólida em <8 semanas sem terapia
  locorregional direta (TACE, RFA, radioembolização).
Quando detectar melhora implausível:
- NÃO usar "resposta excepcional", "downstaging bem-sucedido" ou
  linguagem celebratória.
- USAR "discordância entre exames sequenciais que requer
  esclarecimento antes de qualquer decisão clínica".
- RECOMENDAR: revisão comparativa das imagens DICOM pelo mesmo
  radiologista, repetição do exame com mesmo protocolo/equipamento,
  ou segundo método de imagem para confirmação.
- Listar explicitamente as hipóteses: (1) artefato técnico/protocolo,
  (2) erro de interpretação, (3) resposta real atípica.
- NÃO fundamentar mudanças de conduta (nova SE, listagem, suspensão
  de tratamento) sobre exame discordante não confirmado.
ESPESSAMENTO DE PAREDE VESICULAR EM CIRRÓTICO:
- Espessamento de parede vesicular em paciente cirrótico NÃO é sinônimo de colecistite. Diagnóstico diferencial amplo: hipoalbuminemia/edema por hipertensão portal (causa mais frequente), ascite adjacente, hepatite viral ativa, congestão por trombose de hepáticas/VCI, inflamação reativa pós-TACE, e só então colecistite. NÃO recomendar conduta específica para colecistite sem correlação clínica (febre, Murphy, leucocitose) e sem excluir as causas mais prevalentes no cirrótico.
ACHADOS DE IMAGEM SUSPEITOS vs CONFIRMADOS:
- Focos hipervasculares sem medida, sem washout ou sem LI-RADS NÃO são lesões confirmadas.
- NÃO usar achados indeterminados para declarar "doença bilobar" ou alterar decisão cirúrgica.
- Apenas lesões com critérios completos (APHE + washout OU biópsia) = CHC confirmado.
LESÃO LI-RADS M — REGRA DE PRUDÊNCIA:
LR-M = provavelmente maligno, não necessariamente CHC (inclui colangiocarcinoma, HCC-CCA combinado, linfoma). Regras:
- Regressão parcial sob antibiótico NÃO exclui malignidade — tumores com necrose/componente cístico ou infecção sobreposta podem reduzir de tamanho sem significar natureza benigna.
- Biópsia é INDICAÇÃO FORMAL em LR-M (AASLD 2024) — NÃO omitir esta recomendação por regressão parcial. Só dispensar biópsia se resolução COMPLETA documentada em controle de imagem.
- Controle de imagem em LR-M deve ser curto (4-6 semanas) até resolução completa ou estabilização — NÃO usar intervalo padrão de 3 meses enquanto houver lesão residual classificada como LR-M.
- NÃO usar linguagem definitiva ("virtualmente incompatível com neoplasia", "exclui malignidade") para lesão LR-M em regressão parcial. USAR "regressão favorece etiologia inflamatória mas não exclui malignidade coexistente".
IDADE E AGRESSIVIDADE TERAPÊUTICA:
- Pacientes jovens (<40 anos): cálculo risco-benefício FAVORECE condutas agressivas. NÃO descartar ressecção, PVE ou ALPPS com base em achados suspeitos.
- Idade avançada (>70) + comorbidades = ponderar risco. Jovem + reserva preservada = máxima agressividade curativa.

VOLUMETRIA HEPÁTICA (FLR):
- Em tumores >5 cm: FLR deve ser discutido sobre volume FUNCIONAL (total MENOS tumor), não volume bruto.
- Limiar: ≥25% fígado saudável; ≥30-40% com hepatopatia crônica/fibrose/cirrose.

DIAGNÓSTICO DE CHC POR IMAGEM (AASLD 2024 / EASL 2018):
- Lesão ≥1cm com padrão típico em RM ou TC dinâmica (hipervascularização arterial + washout portal/tardio) = diagnóstico NÃO-INVASIVO de CHC
- NÃO recomendar biópsia de lesão hepática que já tem critérios de imagem para CHC (LI-RADS 5)
- Biópsia hepática só está indicada quando: (a) lesão indeterminada (LI-RADS 3 ou LI-RADS M), (b) discordância entre métodos de imagem, (c) necessidade de diagnóstico diferencial que mude conduta
- Riscos da biópsia em cirrótico: sangramento, disseminação tumoral (seeding), falso-negativo
- Para lesão nova suspeita: PRIMEIRO solicitar segundo método de imagem (se RM, pedir TC trifásica e vice-versa), NÃO biópsia imediata

DISPUTA DE CLASSIFICAÇÃO LI-RADS (quando há divergência entre radiologista/centro e Central de Transplantes):
Quando houver indeferimento de Situação Especial por reclassificação de LI-RADS (ex: laudo local LR-5, Central reclassifica para LR-4), a análise DEVE:

1. LISTAR CADA CRITÉRIO MAIOR INDIVIDUALMENTE com base nos laudos disponíveis:
   - APHE (Arterial Phase Hyperenhancement): presente/ausente/discreto/borderline
   - Washout não-periférico (portal ou tardia): presente/ausente
   - Cápsula de realce (enhancing capsule): presente/ausente/tendência capsular
   - Threshold growth: crescimento ≥50% em ≤6 meses (CRITÉRIO MAIOR INDEPENDENTE)
   - Tamanho: ≥10mm (pré-requisito para LR-3 a LR-5)

   2. APLICAR A TABELA DE CLASSIFICAÇÃO LI-RADS v2018:
   - LR-3: nenhum critério maior OU 1 critério em lesão <20mm
   - LR-4: 1 critério maior (sem APHE) OU APHE isolado em lesão <10mm
   - LR-5: APHE + ≥1 outro critério maior (washout OU cápsula OU threshold growth) EM lesão ≥10mm
   - IMPORTANTE: Threshold growth SOZINHO + qualquer outro critério (APHE OU washout OU cápsula) = LR-5
   - IMPORTANTE: Em contexto pós-tratamento, usar LR-TR (tratado) — crescimento pós-TACE com novo realce sugere recorrência viável

   ⚠️ REGRA DE ANÁLISE POR EXAME:
   - NÃO assumir qual exame foi contestado pela Central — analisar CADA exame de imagem separadamente
   - Para CADA exame: listar os critérios maiores presentes/ausentes conforme o laudo disponível
   - Se a Central reclassificou um exame específico (ex: LR-5 → LR-4), o argumento principal NÃO é re-discutir o mesmo exame, mas sim:
     a) LR-4 = "provavelmente CHC" — NÃO exclui CHC, apenas reduz certeza diagnóstica
     b) Comportamento biológico subsequente CONFIRMA a natureza da lesão: se uma lesão LR-4 foi tratada com TACE (equipe já assumiu CHC) e PROGREDIU após tratamento, o comportamento tumoral é confirmatório
     c) Threshold growth deve ser calculado entre TODOS os pares de exames disponíveis (não apenas o contestado):
        - Exame pré-TACE → último exame (crescimento total)
        - Exame pós-TACE (nadir) → último exame (recrescimento — mais relevante pois demonstra falha terapêutica)
     d) IHQ favorável em biópsia (mesmo inconclusiva) é evidência complementar, não isolada
     e) A soma de evidências (LR-4 + comportamento tumoral + resposta/progressão pós-TACE + IHQ sugestiva) constitui diagnóstico operacional de CHC mesmo sem LR-5 isolado

3. ARTICULAR ARGUMENTAÇÃO TÉCNICA PARA RECURSO:
   - Se threshold growth está presente (crescimento ≥50% em ≤6 meses): este é critério maior LI-RADS independente do APHE e pode ser verificado objetivamente por medições em exames seriados — é o argumento mais difícil de contestar
   - Se APHE é borderline/discreto: reconhecer a subjetividade ("APHE discreto é o ponto mais frequente de discordância entre radiologistas") mas articular que threshold growth + washout OU cápsula = LR-5 mesmo sem APHE robusto
   - Se biópsia foi realizada e tem IHQ sugestiva (Glypican-3+, GS+, CD34+): argumentar que perfil imunohistoquímico favorece CHC mesmo em amostra inadequada, reforçando o diagnóstico
   - SEMPRE calcular velocidade de crescimento: (tamanho atual - tamanho anterior) ÷ intervalo em meses = cm/mês
   - SEMPRE projetar janela de Milão: a que velocidade a lesão atingirá 5cm e quanto tempo resta

   ⚠️ REGRA DE CÁLCULO DE THRESHOLD GROWTH:
   - Calcular TODOS os intervalos disponíveis e apresentar o mais favorável:
     * Entre primeiro e último exame (crescimento global)
     * Entre nadir pós-tratamento e último exame (recrescimento pós-falha — frequentemente o mais expressivo)
     * Entre qualquer par consecutivo com crescimento significativo
   - Fórmula: ((tamanho final - tamanho inicial) / tamanho inicial) × 100 = variação%
   - Projetar para 6 meses: se variação% em X meses, projeção 6m = (variação% / X) × 6
   - Se projeção ≥50% em 6 meses: satisfaz critério de threshold growth
   - Apresentar o cálculo explicitamente na análise (ex: "3,0→4,2cm em 3 meses = +40%, projeção 6m = +80%, satisfaz threshold growth")

4. CONTEXTUALIZAR PARA O RECURSO ADMINISTRATIVO:
   - Citar explicitamente: "Conforme ACR LI-RADS v2018 e AASLD Practice Guidance 2024"
   - Sugerir envio de CD físico com imagens DICOM (não apenas laudos ou acesso web)
   - Sugerir solicitar segunda opinião radiológica formal por radiologista certificado em LI-RADS
   - Se houve tratamento locorregional prévio: documentar que classificação LR-TR se aplica e que crescimento pós-TACE com novo componente viável equivale a recorrência tumoral ativa

   FIDELIDADE FACTUAL — REGRAS ABSOLUTAS:
   - NUNCA inventar, fabricar ou extrapolar o número de eventos (indeferimentos, consultas, internações, procedimentos). Contar APENAS os que estão documentados nos dados fornecidos. Se há 1 indeferimento documentado, dizer "1 indeferimento", NUNCA "quatro indeferimentos" ou "múltiplos indeferimentos" sem evidência
   - NUNCA inventar consultas, datas, diagnósticos ou procedimentos não documentados
   - Se a informação não está nos dados fornecidos, dizer "não documentado" — NUNCA preencher lacunas com suposições
   - Ao mencionar documentos administrativos (ofícios, pareceres, cartas), citar APENAS os que foram fornecidos nos dados do paciente, com número/data exatos quando disponíveis

   ACHADOS DE IMAGEM — REGRAS ADICIONAIS (complemento ao system prompt):
   - PRECEDÊNCIA DE DADOS: Se uma consulta menciona que um laudo/exame "não foi localizado" ou "está pendente", MAS o exame aparece na seção ACHADOS DE IMAGEM ou LAUDOS DE IMAGEM DISPONÍVEIS com análise IA, considerar que o documento FOI obtido posteriormente. A informação do exame/laudo real TEM PRECEDÊNCIA sobre a narrativa da consulta. Nunca repetir que um documento está pendente se ele já está anexado ao prontuário.
   - NÃO mencionar achado anatômico em mais de UMA seção da análise
   - Se relevante para planejamento cirúrgico, mencionar BREVEMENTE (1 frase) apenas em "Considerações Especiais"
   - Ectasia/dilatação da veia porta NÃO é trombose portal — é variante anatômica

HIPERTENSÃO PORTAL:
- Esplenomegalia isolada + plaquetopenia NÃO é "complicação" em cirrótico — é manifestação esperada da doença de base
- Só listar como complicação se houver descompensação: ascite, varizes com sangramento, encefalopatia
- Plaquetopenia/leucopenia são MARCADORES de hipertensão portal/hiperesplenismo, NÃO doença hematológica independente nem diagnóstico co-principal. Hemostasia do cirrótico é REBALANCEADA: a contagem de plaquetas correlaciona MAL com sangramento espontâneo — o risco é sobretudo periprocedimento invasivo. NÃO converter plaquetopenia em "risco hemorrágico" ou limitação funcional sem sangramento/complicação documentada.

AFP EM CHC:
- AFP <20 ng/mL = NÃO tem valor diagnóstico para CHC (sensibilidade muito baixa)
- AFP elevada só é significativa se >200 ng/mL (diagnóstico) ou se tendência ascendente sustentada (>3 medidas consecutivas)
- Variações de AFP <20 ng/mL entre coletas são flutuações normais, NÃO enfatizar variação percentual

CRITÉRIOS DE MILÃO:
- 1 lesão ≤5cm OU até 3 lesões ≤3cm cada, SEM invasão vascular, SEM metástase extra-hepática
- Se lesão nova é SUSPEITA (não confirmada), NÃO afirmar que "mantém Milão" — dizer que "depende de confirmação diagnóstica"
CINÉTICA TUMORAL — REGRA OBRIGATÓRIA QUANDO HÁ MÚLTIPLOS EXAMES DE IMAGEM:
- Se há ≥2 exames de imagem no histórico, AVALIAR a cinética de CADA lesão focal (dimensão em cada exame, variação percentual, tempo total de seguimento)
- CHC típico tem tempo de duplicação de 80-120 dias. Se uma lesão LI-RADS 5 DIMINUIU ou ficou ESTÁVEL por >6 meses SEM tratamento documentado (TACE, ablação, sistêmico), isso é SINAL DE ALARME DIAGNÓSTICO — questionar ATIVAMENTE se é realmente CHC
- LI-RADS classifica PADRÃO DE IMAGEM, não histologia. Lesões não-CHC podem mimetizar LI-RADS 5: nódulo displásico de alto grau, adenoma hepatocelular (especialmente em NASH/MASLD), hemangioma esclerosante, HNF atípica
- Regressão sem tratamento é biologicamente incompatível com CHC típico e deve ser destacada como ACHADO CRÍTICO, com recomendação de revisão multidisciplinar e considerar biópsia
- ANTI-RUÍDO (AI_115, OBRIGATÓRIO — regra CONCEITUAL, não apenas de redação): quando a regressão/estabilidade da lesão É EXPLICADA por tratamento documentado (TACE, ablação, terapia sistêmica), descreva SOMENTE a resposta ao tratamento (downstaging) e NADA sobre certeza/dúvida diagnóstica. É PROIBIDO comentar — em QUALQUER redação ou paráfrase, e mesmo para AFIRMAR, NEGAR ou TRANQUILIZAR — se o diagnóstico de CHC/LI-RADS está ou não em questão. Proibido introduzir, ainda que para descartar, qualquer um destes temas: "questionar/duvidar do diagnóstico", "gatilho para questionar", "sem necessidade de questionar", "não há dúvida/razão para questionar", "cinética compatível/incompatível com CHC". Com tratamento documentado, esse tema simplesmente NÃO se aplica — não o suscite. O alarme diagnóstico da regra acima vale APENAS para regressão SEM tratamento. Não responda a uma pergunta que ninguém fez
- Colangiocarcinoma intrahepático tem sobrevida mediana ~8 meses sem tratamento. Lesão estável por >12 meses SEM tratamento é biologicamente incompatível — NÃO sugerir este diagnóstico em lesões comprovadamente estáveis por longo período
- NÃO atribuir regressão a "discordância técnica entre protocolos" como hipótese principal sem evidência concreta de mudança de equipamento ou protocolo. Regressão documentada em múltiplos exames consecutivos é dado real, não artefato

PRESCRIÇÃO E CONDUTA:
- NÃO sugerir procedimentos invasivos (biópsia, TACE) como primeira linha sem antes esgotar diagnóstico por imagem
- Ordem de investigação para lesão hepática nova: (1) segundo método de imagem, (2) PET-CT se necessário, (3) biópsia apenas se indeterminado após imagem
- Para bridge therapy (TACE, ablação): só indicar após confirmação diagnóstica E se tempo em lista estimado >6 meses

ASCITE — CLASSIFICAÇÃO E ESCALONAMENTO (AASLD 2021 / EASL 2018):
- Ascite refratária tem DEFINIÇÃO ESTRITA: falha com espironolactona ≥400mg/dia + furosemida ≥160mg/dia OU intolerância aos diuréticos (hiponatremia <125, hipercalemia >6, creatinina >2, encefalopatia)
- ANTES de classificar como "ascite refratária" ou sugerir TIPS/paracenteses seriadas:
  * Verificar nas prescrições e consultas as doses ATUAIS de diuréticos
  * Se espironolactona <400mg ou furosemida <160mg: recomendar OTIMIZAÇÃO DE DOSE antes de considerar refratariedade
  * Proporção obrigatória: 100:40 (espiro:furo) — ex: espironolactona 200mg = furosemida 80mg
  * Se paciente tolera doses atuais sem hipercalemia/hiponatremia: aumentar escalonadamente
- TIPS como bridge ao transplante: avaliar APENAS se (a) ascite refratária documentada OU (b) paracenteses >3×/mês com diuréticos em dose máxima tolerada
- NÃO sugerir TIPS se diuréticos ainda não foram otimizados — isso é erro de conduta

PRESCRIÇÕES ATUAIS — VERIFICAR ANTES DE RECOMENDAR (REGRA OBRIGATÓRIA):
- ANTES de recomendar qualquer medicação, verificar se ela já está prescrita nos dados disponíveis (seção PRESCRIÇÕES RECENTES e campo revisaoSistemas das consultas)
- Se a medicação JÁ está em uso: dizer "manter [medicação] em uso" ou "ajustar dose de [medicação] já prescrita"
- Se a medicação NÃO está prescrita e NÃO aparece nas consultas: dizer "iniciar [medicação] — não consta nas prescrições atuais"
- Se não há dados de prescrições: dizer "verificar se já em uso; se não, iniciar [medicação]"
- Esta regra se aplica especialmente a: lactulose, rifaximina, NSBB, diuréticos, imunossupressores, antivirais, UDCA
- NUNCA recomendar medicação sem contextualizar se é nova ou manutenção — isso muda completamente a conduta do médico

HEPATITE AUTOIMUNE — MANEJO DA IMUNOSSUPRESSÃO EM ATIVIDADE (AASLD 2020 / EASL 2025):
- Se transaminases estão elevadas (>1,5× LSN) E tendência de alta → HAI com atividade não controlada
- Verificar esquema imunossupressor atual: dose de prednisona E dose de azatioprina
- Se azatioprina está LIMITADA por leucopenia (<3.000) ou outro efeito adverso:
  * NÃO deixar HAI subimunossuprimida — compensar com AUMENTO da prednisona
  * Prednisona 5mg é dose mínima de manutenção; se HAI ativa, aumentar para 10-20mg
  * Documentar: "Azatioprina limitada por leucopenia; aumentar prednisona para compensar atividade da HAI"
- Se leucopenia persistir com azatioprina na dose atual:
  * Dosar TPMT e NUDT15 (se disponível)
- Se TPMT/NUDT15 NUNCA foram dosados e paciente usa azatioprina: solicitar — farmacogenômica preventiva (EASL 2025)
  * Se leucócitos <2.000 ou neutrófilos <1.000: suspender azatioprina
  * Alternativa: micofenolato mofetil 1-1,5g 12/12h (AASLD 2020; dados de eficácia limitados mas amplamente utilizado em intolerantes)
  * NÃO deixar paciente apenas com prednisona baixa dose + azatioprina subterapêutica — isso é pior que trocar o esquema
- Em paciente cirrótico com HAI ativa pré-transplante: manter imunossupressão adequada é ESSENCIAL para:
  * Evitar flares que precipitem ACLF
  * Documentar atividade da doença para planejamento pós-Tx (recidiva de HAI ocorre em 25-40%)
- IgG sérica: SOLICITAR se não disponível — é o melhor marcador de atividade da HAI e deve guiar ajustes de dose

OVERLAP HAI-CBP / HAI-CEP — CONFIRMAÇÃO DIAGNÓSTICA (EASL 2025 / Paris Criteria):
- Se hipótese de overlap está nas consultas mas NÃO há autoanticorpos nos dados:
  * ALERTAR que o diagnóstico está incompleto
  * SOLICITAR: AMA (ou AMA-M2), ANA, anti-SMA (anti-músculo liso), IgG total, IgM
  * Critérios de Paris para overlap HAI-CBP: ≥2 de 3 critérios de HAI (ALT >5× LSN, IgG >2× LSN, hepatite de interface na biópsia) + ≥2 de 3 critérios de CBP (FA >2× LSN ou GGT >5× LSN, AMA+, ductopenia na biópsia)
- Implicações terapêuticas do overlap confirmado:
  * HAI-CBP: UDCA 13-15mg/kg/dia + imunossupressão (prednisona + AZA) — ambos necessários
  * HAI-CEP: UDCA controverso + imunossupressão — colonoscopia obrigatória (DII em 70-80%)
- Implicações para pós-transplante:
  * HAI confirmada: risco de recidiva 25-40% — manter prednisona baixa dose prolongada pós-Tx
  * CBP confirmada: risco de recidiva 20-30% — UDCA pós-Tx pode reduzir recidiva
  * Documentação sorológica PRÉ-Tx é obrigatória para planejamento de IS pós-Tx

INSTRUÇÕES DE ESTILO:
- Evite repetição excessiva de termos técnicos (ex: não mencionar "Yerdel" mais que 2-3 vezes)
- Ao referenciar classificações já mencionadas, use termos como "conforme classificado" ou "como descrito acima"
- Para AFP dentro da normalidade (<10 ng/mL), não enfatizar variações percentuais - apenas mencionar tendência
- Seja conciso: não repita informações já apresentadas em seções anteriores

CONTEXTO PÓS-TRANSPLANTE (se aplicável):
- Nos primeiros 6 meses pós-Tx, priorizar diagnósticos diferenciais de:
  * Rejeição aguda celular ou humoral
  * Complicações vasculares: trombose de artéria hepática, estenose/trombose de veia porta
  * Complicações biliares: estenose de via biliar, fístula, colangite
  * Infecções oportunistas (CMV, EBV)
  * Toxicidade por imunossupressores
- NÃO mencionar CHC ou trombose portal como "ausente" em paciente transplantado - são irrelevantes no contexto
- Classificação de disfunção do enxerto:
  * LEVE: AST/ALT 2-5x LSN, BT <3, sem coagulopatia
  * MODERADA: AST/ALT 5-10x LSN, BT 3-10, INR <2
  * GRAVE: AST/ALT >10x LSN ou BT >10 ou INR >2 ou encefalopatia

${
  contexto?.objetivoEspecifico
    ? `\nOBJETIVO ESPECÍFICO DA ANÁLISE: ${contexto.objetivoEspecifico}`
    : ""
}
${contexto?.urgencia ? `\nURGÊNCIA: ${contexto.urgencia}` : ""}

${TEMPLATE_INVESTIGACAO_E_FORMATO}
`;
}

// =============================================================================
// =============================================================================
//  NOVAS FUNÇÕES DE IA v4.2.0
//  Colar ACIMA do "export default {"
// =============================================================================
// =============================================================================

// =============================================================================
// FUNÇÃO 1: CALCULAR PERÍODO PÓS-TRANSPLANTE
// =============================================================================
// Lê a data do transplante e calcula quantos dias se passaram.
// Categoriza em: precoce (0-30d), intermediario (1-6m), tardio (6-12m), muito_tardio (>1a)

export function calcularPeriodoPosTx(dados: DadosAnonimizados): {
  dias: number;
  categoria: "precoce" | "intermediario" | "tardio" | "muito_tardio";
  descricao: string;
} {
  const transplantes = dados.dadosTransplante || [];
  let dataTxStr: string | undefined;

  if (transplantes.length > 0) {
    const txComData = [...transplantes]
      .reverse()
      .find((tx) => tx.dataTransplante);
    dataTxStr = txComData?.dataTransplante;
  }

  if (!dataTxStr && dados.transplante?.dataTransplante) {
    dataTxStr = dados.transplante.dataTransplante;
  }

  if (!dataTxStr) {
    return {
      dias: 0,
      categoria: "precoce",
      descricao: "Data do transplante não registrada",
    };
  }

  const partes = dataTxStr.split("-");
  let dataTx: Date;
  if (partes.length === 3) {
    dataTx = new Date(
      parseInt(partes[0]),
      parseInt(partes[1]) - 1,
      parseInt(partes[2])
    );
  } else {
    dataTx = new Date(dataTxStr);
  }

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  dataTx.setHours(0, 0, 0, 0);

  const dias = Math.floor(
    (hoje.getTime() - dataTx.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (dias <= 30) {
    return {
      dias,
      categoria: "precoce",
      descricao: `${dias} dias pós-Tx (período precoce: 0-30d)`,
    };
  } else if (dias <= 180) {
    const meses = Math.floor(dias / 30);
    return {
      dias,
      categoria: "intermediario",
      descricao: `${meses} meses pós-Tx (~${dias}d) — período intermediário: 1-6m`,
    };
  } else if (dias <= 365) {
    const meses = Math.floor(dias / 30);
    return {
      dias,
      categoria: "tardio",
      descricao: `${meses} meses pós-Tx (~${dias}d) — período tardio: 6-12m`,
    };
  } else {
    const anos = Math.floor(dias / 365);
    const mesesRestantes = Math.floor((dias % 365) / 30);
    return {
      dias,
      categoria: "muito_tardio",
      descricao: `${anos}a${
        mesesRestantes > 0 ? ` ${mesesRestantes}m` : ""
      } pós-Tx (~${dias}d) — período tardio: >1 ano`,
    };
  }
}

// =============================================================================
// FUNÇÃO 2: DIAGNÓSTICOS DIFERENCIAIS TEMPORAIS PÓS-TX
// =============================================================================
// Gera o bloco de texto que será injetado no prompt, priorizando os
// diferenciais conforme o período pós-transplante.

export function gerarDiferenciaisTemporaisPosTx(
  dados: DadosAnonimizados
): string {
  const periodo = calcularPeriodoPosTx(dados);

  const header = `
⏱️ PERÍODO PÓS-TRANSPLANTE: ${periodo.descricao}

DIAGNÓSTICOS DIFERENCIAIS PRIORIZADOS PARA ESTE PERÍODO:`;

  switch (periodo.categoria) {
    case "precoce":
      return `${header}
(Nos primeiros 30 dias, SEMPRE pensar primeiro em complicações cirúrgicas e rejeição aguda)

1. REJEIÇÃO AGUDA CELULAR — mais comum entre 5-30 dias
   → Biópsia hepática é padrão-ouro (critérios de Banff)
   → Pulsoterapia: Metilprednisolona 500-1000mg IV por 3 dias

2. TROMBOSE DE ARTÉRIA HEPÁTICA PRECOCE
   → Doppler URGENTE se elevação súbita de transaminases
   → Emergência cirúrgica: revascularização ou retransplante

3. DISFUNÇÃO PRIMÁRIA DO ENXERTO (EAD) — primeiras 72h
   → AST/ALT >2000, INR >1.6, Bilirrubina >10 no D7
   → Critérios de Olthoff para classificar

4. COMPLICAÇÕES BILIARES PRECOCES
   → Fístula biliar (bile no dreno), estenose anastomótica
   → ColangioRM ou CPRE se colestase

5. INFECÇÃO BACTERIANA — ITU, pneumonia, sítio cirúrgico
   → Hemoculturas, urocultura, RX tórax

6. COMPLICAÇÕES VASCULARES PORTAIS
   → Trombose ou estenose de veia porta — Doppler

IMUNOSSUPRESSÃO NESTE PERÍODO:
→ Tacrolimus alvo: 8-12 ng/mL (manter nível mais alto no início)
→ Corticóide em desmame conforme protocolo do centro`;

    case "intermediario":
      return `${header}
(Entre 1-6 meses, rejeição ainda é frequente E infecções oportunistas começam a aparecer)

1. REJEIÇÃO AGUDA CELULAR OU HUMORAL
   → Ainda frequente, especialmente com subimunossupressão
   → Biópsia obrigatória para diagnóstico e graduação

2. CMV PRIMÁRIO — especialmente se doador CMV+ / receptor CMV- (D+/R-)
   → Monitorar PCR quantitativo para CMV regularmente
   → Profilaxia com Valganciclovir se alto risco

3. TROMBOSE DE ARTÉRIA HEPÁTICA TARDIA
   → Doppler + AngioTC se suspeita

4. COLANGIOPATIA ISQUÊMICA
   → Estenoses intra-hepáticas (não-anastomóticas)
   → ColangioRM para avaliação

5. ESTENOSE BILIAR ANASTOMÓTICA
   → CPRE ou ColangioRM se padrão colestático

6. TOXICIDADE POR INIBIDOR DE CALCINEURINA
   → Nefrotoxicidade (aumento de creatinina)
   → Neurotoxicidade (tremor, cefaleia, confusão)

7. INFECÇÕES OPORTUNISTAS
   → CMV, EBV, Pneumocystis jirovecii
   → Manter profilaxias (SMX-TMP, Valganciclovir)

IMUNOSSUPRESSÃO NESTE PERÍODO:
→ Tacrolimus alvo: 6-10 ng/mL
→ Iniciar desmame de corticóide conforme protocolo
→ Considerar associar micofenolato para poupar CNI se nefrotoxicidade`;

    case "tardio":
      return `${header}
(Entre 6-12 meses, pensar em rejeição crônica E recidiva da doença de base)

1. REJEIÇÃO CRÔNICA DUCTOPÊNICA
   → GGT e FA em elevação progressiva
   → Perda de ductos biliares na biópsia (<50% dos espaços porta)

2. RECIDIVA DA DOENÇA DE BASE
   → HCV: raro com DAAs, mas verificar SVR
   → Hepatite autoimune: recidiva em 20-40%
   → CBP/CEP: recidiva possível
   → Álcool: avaliar aderência à abstinência
   → MASLD/NASH: recidiva frequente

3. EBV E RISCO DE PTLD
   → Monitorar carga viral EBV periodicamente
   → Se EBV em ascensão: considerar redução de imunossupressão

4. TOXICIDADE CRÔNICA DE CNI
   → Nefrotoxicidade crônica (DRC progressiva)
   → Considerar conversão para Everolimus se indicado
   → Indicação de mTORi: CHC prévio, nefrotoxicidade, neoplasias de novo

5. SÍNDROME METABÓLICA PÓS-TX
   → Diabetes, HAS, dislipidemia (efeito de CNI + corticóide)

IMUNOSSUPRESSÃO NESTE PERÍODO:
→ Tacrolimus alvo: 5-8 ng/mL
→ Considerar monoterapia com tacrolimus se estável
→ mTORi: avaliar se CHC foi a indicação do transplante`;

    case "muito_tardio":
      return `${header}
(Após 1 ano, as complicações de LONGO PRAZO dominam o cenário)

1. RECIDIVA DE CHC — se transplante foi por hepatocarcinoma
   → AFP + imagem a cada 6 meses nos primeiros 5 anos
   → Protocolo de vigilância oncológica obrigatório

2. PTLD (Doença Linfoproliferativa Pós-Transplante)
   → Associada a EBV. Monitorar carga viral
   → Sinais: linfonodomegalia, febre, perda de peso, massas

3. NEOPLASIAS DE NOVO
   → Pele (carcinoma espinocelular, basocelular): proteção solar rigorosa
   → Colorretal: colonoscopia de rastreamento
   → Risco aumentado por imunossupressão crônica

4. DOENÇA CARDIOVASCULAR
   → Principal causa de mortalidade tardia pós-Tx
   → Controle agressivo: HAS, DM, dislipidemia, tabagismo

5. DOENÇA RENAL CRÔNICA POR CNI
   → TFG em queda progressiva ao longo dos anos
   → Considerar conversão para Everolimus/Sirolimus se TFG <40-50

6. RECIDIVA DA DOENÇA DE BASE (mesmo após > 1 ano)
   → AIH, MASLD/NASH do enxerto, CBP, CEP

7. OSTEOPOROSE / FRATURAS
   → Densitometria óssea anual
   → Suplementação de cálcio e vitamina D

8. REJEIÇÃO CRÔNICA TARDIA
   → Possível mesmo após anos de estabilidade
   → Investigar se elevação inexplicada de enzimas canaliculares

IMUNOSSUPRESSÃO NESTE PERÍODO:
→ Tacrolimus alvo: 4-6 ng/mL (minimização ativa se estável >2 anos)
→ Monoterapia frequente
→ Revisão anual do esquema imunossupressor
→ Se CHC prévio: manter Everolimus (efeito antitumoral)`;
  }
}

// =============================================================================
// FUNÇÃO 3: CLASSIFICAÇÃO CSPH — BAVENO VII
// =============================================================================
// Busca elastografia (kPa) e plaquetas nos dados do paciente.
// Classifica a hipertensão portal e diz se EDA é necessária ou dispensável.
// Só é usada em pacientes PRÉ-transplante com doença hepática.

export function gerarSecaoCSPH(dados: DadosAnonimizados): string {
  // ---- Buscar elastografia nos exames de imagem ----
  let lsm: number | null = null;

  if (dados.imagens && dados.imagens.length > 0) {
    for (const img of dados.imagens) {
      const textoExame = [
        img.tipo || "",
        img.descricao || "",
        img.resumoAchados || "",
      ]
        .join(" ")
        .toLowerCase();

      if (
        textoExame.includes("elastografia") ||
        textoExame.includes("fibroscan") ||
        textoExame.includes("arfi") ||
        textoExame.includes("shear wave")
      ) {
        const textoCompleto = [
          img.descricao || "",
          img.resumoAchados || "",
        ].join(" ");
        const matchKpa = textoCompleto.match(/(\d+[.,]\d+)\s*kPa/i);
        if (matchKpa) {
          lsm = parseFloat(matchKpa[1].replace(",", "."));
          break;
        }
      }
    }
  }

  // ---- Buscar plaquetas no último exame laboratorial ----
  // B361 (AI_156): a lista chega em ordem ASCENDENTE (anonimizarDados ordena do
  // mais antigo ao mais recente) — percorrer do começo pegava a plaqueta MAIS
  // ANTIGA. Caso real: 167.000 (18/03) em vez de 129.000 (18/09), e o limiar de
  // 150.000 decide entre "CSPH improvável — EDA dispensável" e "zona cinzenta".
  let plaquetas: number | null = null;
  let plaquetasData = "";

  if (dados.examesLab && dados.examesLab.length > 0) {
    for (let i = dados.examesLab.length - 1; i >= 0; i--) {
      const exame = dados.examesLab[i];
      const plaq = exame.valores?.Plaquetas;
      if (plaq !== undefined && plaq !== null && plaq !== "") {
        plaquetas = parseFloat(
          String(plaq).replace(",", ".").replace(/\./g, "")
        );
        if (plaquetas < 1000) {
          plaquetas = plaquetas * 1000;
        }
        plaquetasData = (exame as any).data || "";
        break;
      }
    }
  }

  // ---- Buscar achados de imagem (ascite, shunts, varizes) ----
  const achados = dados.achadosImagem || [];

  const temAscite = achados.some(
    (a) =>
      (typeof a.ascite === "string" &&
        (a.ascite.trim().toUpperCase() === "SIM" ||
          a.ascite.toLowerCase().includes("presente"))) ||
      false
  );

  // B361 (AI_156): a lista fixa ("não"/"nao"/"ausentes") lia "Sem shunts" e
  // "Não identificados" como shunt PRESENTE — fonte única agora.
  const temShunts = achados.some((a) => ehRespostaPositiva(a.shunts));

  const temVarizes = (dados.imagens || []).some((img) => {
    const texto = [img.descricao || "", img.resumoAchados || ""]
      .join(" ")
      .toLowerCase();
    return texto.includes("variz") || texto.includes("varize");
  });

  // ---- Montar seção do prompt ----
  return `
AVALIE HIPERTENSÃO PORTAL CLINICAMENTE SIGNIFICATIVA (CSPH) — BAVENO VII:

DADOS DISPONÍVEIS PARA CLASSIFICAÇÃO:
- Elastografia hepática (LSM): ${
    lsm !== null ? `${lsm} kPa` : "Não disponível nos dados"
  }
- Plaquetas: ${
    plaquetas !== null
      ? `${plaquetas.toLocaleString("pt-BR")} /mm³${
          plaquetasData ? ` (exame de ${plaquetasData})` : ""
        }`
      : "Não disponível"
  }
- Ascite (achados de imagem): ${
    temAscite ? "SIM — documentada" : "Não documentada nos exames"
  }
- Shunts portossistêmicos: ${
    temShunts ? "SIM — documentados" : "Não documentados"
  }
- Varizes (exames): ${
    temVarizes ? "SIM — documentadas" : "Não documentadas nos exames"
  }

REGRAS DE CLASSIFICAÇÃO BAVENO VII — APLICAR NA ANÁLISE:

a) CSPH CONFIRMADA se QUALQUER um destes:
   → Varizes documentadas em EDA
   → Descompensação hepática (ascite, hemorragia varicosa, encefalopatia)

b) CSPH PRESUMIDA (Rule of Five) se:
   → LSM ≥ 25 kPa (independente das plaquetas)

c) CSPH IMPROVÁVEL se AMBOS:
   → LSM < 15 kPa  E  plaquetas > 150.000/mm³
   → Neste caso: EDA pode ser DISPENSADA (monitorar com elastografia anual)

d) ZONA CINZENTA se:
   → LSM 15-25 kPa  OU  LSM <15 mas plaquetas <150.000
   → Calcular LSPS = (LSM × tamanho do baço) / plaquetas
   → EDA recomendada para definir

e) REGRA DE DISPENSA DE EDA (Baveno VII expandido):
   → LSM < 20 kPa  E  plaquetas > 150.000  → Baixo risco de varizes de alto risco → EDA pode ser evitada

CLASSIFICAR O PACIENTE EM UMA DAS CATEGORIAS:
→ "CSPH confirmada"
→ "CSPH presumida"
→ "CSPH improvável — EDA dispensável"
→ "Zona cinzenta — investigar com EDA"
→ "Dados insuficientes para classificar"

⚠️ ALERTA ESPECIAL:
Se CSPH confirmada ou presumida E MELD < 15:
→ Sinalizar: "ALTO RISCO CLÍNICO APESAR DE MELD BAIXO"
→ Estes pacientes são subestimados pelo MELD e podem se beneficiar de avaliação para transplante
`;
}

// =============================================================================
// FUNÇÃO 4: MAPEAMENTO CID-10 AUTOMÁTICO
// =============================================================================
// Lê os diagnósticos do paciente (diagnosticoBase, hipóteses das consultas,
// inscrição em TX) e mapeia para os códigos CID-10 correspondentes.
// Essencial para laudos previdenciários (INSS, IR, BPC, Passe Livre).

export function gerarCID10Paciente(dados: DadosAnonimizados): string {
  const cids: Array<{ cid: string; descricao: string }> = [];

  // Juntar todos os textos diagnósticos do paciente em um só
  const textosParaBusca: string[] = [];

  if (dados.diagnosticoBase) {
    textosParaBusca.push(dados.diagnosticoBase.toLowerCase());
  }

  if (dados.consultas) {
    for (const consulta of dados.consultas) {
      if (consulta.hipoteses) {
        textosParaBusca.push(
          typeof consulta.hipoteses === "string"
            ? consulta.hipoteses.toLowerCase()
            : String(consulta.hipoteses).toLowerCase()
        );
      }
    }
  }

  if (dados.inscricaoTx?.diagnosticoBase) {
    textosParaBusca.push(dados.inscricaoTx.diagnosticoBase.toLowerCase());
  }

  const textoCompleto = textosParaBusca.join(" ");

  // --- Hepatopatias ---
  if (
    textoCompleto.includes("cirrose") &&
    (textoCompleto.includes("álcool") ||
      textoCompleto.includes("alcool") ||
      textoCompleto.includes("etíl") ||
      textoCompleto.includes("etil"))
  ) {
    cids.push({ cid: "K70.3", descricao: "Cirrose hepática alcoólica" });
  } else if (
    textoCompleto.includes("cirrose") &&
    textoCompleto.includes("biliar")
  ) {
    cids.push({ cid: "K74.3", descricao: "Cirrose biliar primária" });
  } else if (textoCompleto.includes("cirrose")) {
    cids.push({
      cid: "K74.6",
      descricao: "Cirrose hepática, outras e as não especificadas",
    });
  }

  if (textoCompleto.includes("hepatite b") || textoCompleto.includes("hbv")) {
    cids.push({
      cid: "B18.1",
      descricao: "Hepatite viral crônica B sem agente delta",
    });
  }
  if (textoCompleto.includes("hepatite c") || textoCompleto.includes("hcv")) {
    cids.push({ cid: "B18.2", descricao: "Hepatite viral crônica C" });
  }
  if (
    textoCompleto.includes("hepatite autoimune") ||
    textoCompleto.includes("hai") ||
    textoCompleto.includes("hepatite auto-imune")
  ) {
    cids.push({ cid: "K75.4", descricao: "Hepatite autoimune" });
  }
  if (
    textoCompleto.includes("cbp") ||
    textoCompleto.includes("colangite biliar primária")
  ) {
    cids.push({ cid: "K74.3", descricao: "Cirrose biliar primária" });
  }
  if (
    textoCompleto.includes("cep") ||
    textoCompleto.includes("colangite esclerosante")
  ) {
    cids.push({ cid: "K83.0", descricao: "Colangite esclerosante" });
  }
  if (
    textoCompleto.includes("nash") ||
    textoCompleto.includes("esteatohepatite") ||
    textoCompleto.includes("masld") ||
    textoCompleto.includes("mash")
  ) {
    cids.push({ cid: "K75.8", descricao: "Esteatohepatite não-alcoólica" });
  }
  if (textoCompleto.includes("wilson")) {
    cids.push({ cid: "E83.0", descricao: "Doença de Wilson" });
  }
  if (textoCompleto.includes("hemocromatose")) {
    cids.push({ cid: "E83.1", descricao: "Hemocromatose" });
  }
  if (textoCompleto.includes("budd") && textoCompleto.includes("chiari")) {
    cids.push({ cid: "I82.0", descricao: "Síndrome de Budd-Chiari" });
  }
  if (
    textoCompleto.includes("alfa-1") ||
    textoCompleto.includes("alpha-1") ||
    textoCompleto.includes("antitripsina")
  ) {
    cids.push({
      cid: "E88.0",
      descricao: "Deficiência de alfa-1 antitripsina",
    });
  }
  if (textoCompleto.includes("alagille")) {
    cids.push({ cid: "Q44.7", descricao: "Síndrome de Alagille" });
  }
  if (textoCompleto.includes("caroli")) {
    cids.push({ cid: "Q44.6", descricao: "Doença cística do fígado (Caroli)" });
  }
  if (
    textoCompleto.includes("policístic") ||
    textoCompleto.includes("policistic")
  ) {
    cids.push({ cid: "Q61.3", descricao: "Doença policística do fígado" });
  }

  // --- Neoplasias ---
  if (
    textoCompleto.includes("hepatocarcinoma") ||
    textoCompleto.includes("chc") ||
    textoCompleto.includes("carcinoma hepatocelular") ||
    textoCompleto.includes("hcc")
  ) {
    cids.push({ cid: "C22.0", descricao: "Carcinoma hepatocelular" });
  }
  if (textoCompleto.includes("colangiocarcinoma")) {
    // AI_145 (revisão): distinguir o subtipo p/ o CID correto — o peri-hilar
    // (Klatskin) e o distal são vias biliares EXTRA-hepáticas (C24.0); só o
    // intra-hepático é C22.1. Este bloco só roda com "colangiocarcinoma" já
    // presente, então "hilar"/"distal" aqui não colidem com outros diagnósticos.
    const colangioExtraHepatico =
      textoCompleto.includes("klatskin") ||
      textoCompleto.includes("hilar") || // cobre "peri-hilar"/"perihilar"
      textoCompleto.includes("distal") ||
      textoCompleto.includes("extra-hep") ||
      textoCompleto.includes("extra hep") ||
      textoCompleto.includes("extrahep");
    if (colangioExtraHepatico) {
      cids.push({
        cid: "C24.0",
        descricao:
          "Colangiocarcinoma de vias biliares extra-hepáticas (peri-hilar/distal)",
      });
    } else {
      cids.push({ cid: "C22.1", descricao: "Colangiocarcinoma intra-hepático" });
    }
  }
  if (
    textoCompleto.includes("metástase") ||
    textoCompleto.includes("metastase")
  ) {
    cids.push({
      cid: "C78.7",
      descricao: "Neoplasia maligna secundária do fígado",
    });
  }

  // --- Transplante ---
  if (
    dados.transplante ||
    (dados.dadosTransplante && dados.dadosTransplante.length > 0)
  ) {
    cids.push({ cid: "Z94.4", descricao: "Fígado transplantado" });
  }

  // --- Complicações da cirrose ---
  if (textoCompleto.includes("ascite")) {
    cids.push({ cid: "R18", descricao: "Ascite" });
  }
  if (textoCompleto.includes("encefalopatia")) {
    cids.push({
      cid: "K72.9",
      descricao: "Insuficiência hepática não classificada em outra parte",
    });
  }
  if (
    textoCompleto.includes("variz") ||
    textoCompleto.includes("hemorragia digestiva") ||
    textoCompleto.includes("hda")
  ) {
    cids.push({
      cid: "I85.0",
      descricao: "Varizes esofágicas com sangramento",
    });
  }
  if (textoCompleto.includes("hipertensão portal")) {
    cids.push({ cid: "K76.6", descricao: "Hipertensão portal" });
  }
  if (
    textoCompleto.includes("síndrome hepatorrenal") ||
    textoCompleto.includes("sindrome hepatorrenal") ||
    textoCompleto.includes("shr")
  ) {
    cids.push({ cid: "K76.7", descricao: "Síndrome hepatorrenal" });
  }
  if (
    textoCompleto.includes("peritonite bacteriana espontânea") ||
    textoCompleto.includes("pbe")
  ) {
    cids.push({ cid: "K65.0", descricao: "Peritonite bacteriana espontânea" });
  }
  if (
    textoCompleto.includes("trombose") &&
    (textoCompleto.includes("porta") || textoCompleto.includes("portal"))
  ) {
    cids.push({ cid: "I81", descricao: "Trombose da veia porta" });
  }
  if (textoCompleto.includes("hepatopulmonar")) {
    cids.push({ cid: "K76.8", descricao: "Síndrome hepatopulmonar" });
  }

  // --- Comorbidades comuns ---
  if (
    textoCompleto.includes("diabetes") ||
    textoCompleto.includes("dm2") ||
    textoCompleto.includes("dm 2")
  ) {
    cids.push({ cid: "E11.9", descricao: "Diabetes mellitus tipo 2" });
  }
  if (
    textoCompleto.includes("hipertensão arterial") ||
    textoCompleto.includes(" has ") ||
    textoCompleto.includes("has,")
  ) {
    cids.push({ cid: "I10", descricao: "Hipertensão essencial (primária)" });
  }
  if (
    textoCompleto.includes("insuficiência renal") ||
    textoCompleto.includes("doença renal") ||
    textoCompleto.includes("drc")
  ) {
    cids.push({
      cid: "N18.9",
      descricao: "Doença renal crônica não especificada",
    });
  }
  if (textoCompleto.includes("obesidade")) {
    cids.push({ cid: "E66.9", descricao: "Obesidade não especificada" });
  }
  if (
    textoCompleto.includes("depressão") ||
    textoCompleto.includes("depressao")
  ) {
    cids.push({
      cid: "F32.9",
      descricao: "Episódio depressivo não especificado",
    });
  }
  if (textoCompleto.includes("hipotireoidismo")) {
    cids.push({ cid: "E03.9", descricao: "Hipotireoidismo não especificado" });
  }

  // Se não encontrou nenhum diagnóstico, colocar genérico
  if (cids.length === 0) {
    cids.push({ cid: "K76.9", descricao: "Doença hepática não especificada" });
  }

  // Remover duplicatas (se o mesmo CID apareceu mais de uma vez)
  const cidsUnicos = cids.filter(
    (cid, index, self) => index === self.findIndex((c) => c.cid === cid.cid)
  );

  // Montar o texto que será injetado no prompt
  const linhas = cidsUnicos.map((c) => `   • ${c.cid} — ${c.descricao}`);

  return `
CID-10 APLICÁVEIS A ESTE PACIENTE (baseado nos diagnósticos registrados):
${linhas.join("\n")}

REGRA: Em laudos previdenciários (INSS, IR, BPC, Passe Livre), incluir o CID-10 principal
entre parênteses logo após o diagnóstico. Exemplo: "Cirrose hepática por vírus C (K74.6 + B18.2)"
O CID é OBRIGATÓRIO nestes documentos.
`;
}

// =============================================================================
// FUNÇÃO 5: PROMPT DE ANÁLISE GERAL REFINADO
// =============================================================================
// Esta é a função que JUNTA tudo: diferenciais temporais + CSPH + CID-10.
// Ela pega o prompt completo que já existe (gerarPromptAnaliseCompleta)
// e ADICIONA as novas seções automaticamente.
//
// Pense assim: é como pegar a análise que já funciona e colar 3 post-its
// com informações extras antes de entregar para a IA.

export function gerarPromptAnaliseGeralRefinado(
  dados: DadosAnonimizados,
  contexto?: ContextoClinico
): string {
  // Se tem contexto específico (funcao_hepatica, imagem, etc.),
  // usar os prompts especializados que já existem — sem mudança
  if (contexto?.tipoAnalise) {
    switch (contexto.tipoAnalise) {
      case "funcao_hepatica":
        return gerarPromptFuncaoHepatica(dados);
      case "imagem":
        return gerarPromptImagem(dados);
      case "imunossupressao":
        return gerarPromptImunossupressao(dados);
      case "marcadores":
        return gerarPromptMarcadores(dados);
      case "evolucao":
        return gerarPromptEvolucao(dados);
      case "pre_transplante":
        return gerarPromptPreTransplante(dados);
    }
  }

  // Verificar se é pós-transplante
  const isPosTx = !!(
    dados.transplante ||
    (dados.dadosTransplante && dados.dadosTransplante.length > 0)
  );

  // Verificar se é pré-Tx com doença hepática (para CSPH)
  const isPreTxFigado =
    !isPosTx &&
    (dados.tipoAtendimento === "Fígado" ||
      dados.tipoAtendimento === "figado" ||
      (dados.diagnosticoBase || "").toLowerCase().includes("cirrose"));

  // =============================================
  // CONSTRUIR AS SEÇÕES NOVAS
  // =============================================

  // Seção 1: Diferenciais temporais (só para pós-Tx)
  let secaoDiferenciais = "";
  if (isPosTx) {
    secaoDiferenciais = gerarDiferenciaisTemporaisPosTx(dados);
  }

  // Seção 2: CSPH Baveno VII (só para pré-Tx com fígado)
  let secaoCSPH = "";
  if (isPreTxFigado) {
    secaoCSPH = gerarSecaoCSPH(dados);
  }

  // Seção 3: CID-10 (sempre disponível)
  const secaoCID = gerarCID10Paciente(dados);

  // =============================================
  // PEGAR O PROMPT QUE JÁ EXISTE E ADICIONAR
  // =============================================

  // Gerar o prompt completo existente (gerarPromptAnaliseCompleta)
  // que já é excelente e tem todas as regras clínicas
  const promptBase = gerarPromptAnaliseCompleta(dados, contexto);

  // Agora vamos INJETAR as novas seções no prompt existente.
  // Procuramos a palavra "SOLICITAÇÃO:" e inserimos ANTES dela.
  // Assim as novas informações ficam disponíveis para a IA
  // antes dela começar a gerar a resposta.

  const marcadorSolicitacao = "SOLICITAÇÃO:";
  const posicaoSolicitacao = promptBase.indexOf(marcadorSolicitacao);

  if (posicaoSolicitacao === -1) {
    // Se não encontrou "SOLICITAÇÃO:", adiciona no final
    return (
      promptBase + "\n" + secaoDiferenciais + "\n" + secaoCSPH + "\n" + secaoCID
    );
  }

  // Cortar o prompt em duas partes e inserir as novas seções no meio
  const parteAntes = promptBase.substring(0, posicaoSolicitacao);
  const parteDepois = promptBase.substring(posicaoSolicitacao);

  return (
    parteAntes +
    "\n" +
    secaoDiferenciais +
    "\n" +
    secaoCSPH +
    "\n" +
    secaoCID +
    "\n\n" +
    parteDepois
  );
}

// =============================================================================
// EXPORTS DEFAULT
// =============================================================================

export default {
  // Versão
  VERSAO_PROMPT,

  // Constantes clínicas
  CRITERIOS_MILAO,
  CRITERIOS_UCSF,
  CRITERIO_UP_TO_7,
  CLASSIFICACAO_LI_RADS,
  CLASSIFICACAO_BCLC,
  TRATAMENTO_SISTEMICO_HCC,
  CLASSIFICACAO_AFP,
  CLASSIFICACAO_YERDEL,
  TROMBOSE_PORTAL_TIPOS,
  ANTICOAGULACAO_PVT,
  MELD_3_0,
  CHILD_PUGH,
  ACLF_CRITERIA,
  NACSELD_ACLF,
  ASCITE_GUIDELINES,
  PBE_GUIDELINES,
  SHR_GUIDELINES,
  ENCEFALOPATIA_GUIDELINES,
  HIPERTENSAO_PORTAL_GUIDELINES,
  TIPS_GUIDELINES,
  VARIZES_GASTRICAS,
  NUTRICAO_CIRROSE,
  DOENCA_HEPATICA_ALCOOLICA,
  HEPATITE_AUTOIMUNE,
  PBC_GUIDELINES,
  PSC_GUIDELINES,
  MASLD_GUIDELINES,
  RESMETIROM_GUIDELINES,
  SEMAGLUTIDE_GUIDELINES,
  HBV_GUIDELINES,
  HCV_GUIDELINES,
  DILI_GUIDELINES,
  HEMOCROMATOSE_GUIDELINES,
  // NOVOS - Doença de Wilson
  WILSON_DISEASE_GUIDELINES,
  SCORE_NWI_WILSON,

  // NOVOS - Colangiocarcinoma
  COLANGIOCARCINOMA_GUIDELINES,

  // NOVOS - Transplante Update
  TRANSPLANTE_GUIDELINES_UPDATE,

  // NOVOS - Doenças Colestáticas Genéticas
  DOENCAS_COLESTATICAS_GENETICAS,

  // NOVOS - HBV Update 2025
  HBV_GUIDELINES_2025,

  // NOVOS - Doenças Císticas
  DOENCAS_CISTICAS_HEPATICAS,

  // Funções de formatação
  formatarContextoClinico,
  formatarScores,
  formatarAchadosImagem,
  formatarMarcadores,
  gerarDiretrizesContextuais,

  // Funções de geração de prompts
  gerarPromptFuncaoHepatica,
  gerarPromptImagem,
  gerarPromptImunossupressao,
  gerarPromptMarcadores,
  gerarPromptEvolucao,
  gerarPromptPreTransplante,
  gerarPromptAnaliseCompleta,
  // ✨ NOVAS FUNÇÕES v4.2.0
  calcularPeriodoPosTx,
  gerarDiferenciaisTemporaisPosTx,
  gerarSecaoCSPH,
  gerarCID10Paciente,
  gerarPromptAnaliseGeralRefinado,
};
